import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import Swal from 'sweetalert2';
import IngredientResolverTable from '../../components/admin/IngredientResolverTable';

const SPECIAL_DAY_TAG_MAP = {
 meal: 'sd_meal',
 sweet: 'sd_sweet',
 savory: 'sd_savory',
 meze: 'sd_meze'
};

const SPECIAL_DAY_LABELS = {
 meal: 'Yemekli',
 sweet: 'Tatlı',
 savory: 'Tuzlu',
 meze: 'Meze'
};

const normalizeSpecialDayTagText = (value) =>
 String(value || '')
 .toLocaleLowerCase('tr-TR')
 .trim();

const parseManualSpecialDayLabelsFromTags = (tags = []) => {
 const normalizedTags = Array.isArray(tags) ? tags.map(normalizeSpecialDayTagText) : [];
 return Object.entries(SPECIAL_DAY_TAG_MAP)
 .filter(([, canonical]) => normalizedTags.includes(canonical))
 .map(([label]) => label);
};

const applyManualSpecialDayLabelsToTags = (tags = [], labels = []) => {
 const normalizedCanonicalValues = Object.values(SPECIAL_DAY_TAG_MAP);
 const baseTags = (Array.isArray(tags) ? tags : [])
 .map((tag) => String(tag || '').trim())
 .filter(Boolean)
 .filter((tag) => !normalizedCanonicalValues.includes(normalizeSpecialDayTagText(tag)));

 const normalizedLabels = (Array.isArray(labels) ? labels : []).map(normalizeSpecialDayTagText);
 const next = [...baseTags];
 Object.entries(SPECIAL_DAY_TAG_MAP).forEach(([label, canonical]) => {
 if (normalizedLabels.includes(label)) next.push(canonical);
 });
 return [...new Set(next)];
};

const deriveSystemSpecialDayLabels = (dish = {}) => {
 const makeSearchBag = (...parts) =>
 parts
 .map((part) => String(part || '').toLocaleLowerCase('tr-TR'))
 .join(' ');
 const includesAny = (searchBag, tokens = []) =>
 tokens.some((token) => searchBag.includes(String(token || '').toLocaleLowerCase('tr-TR')));

 const searchBag = makeSearchBag(
 dish.name,
 dish.category,
 dish.serveStyle,
 Array.isArray(dish.tags) ? dish.tags.join(' ') : ''
 );

 return {
 sweet: includesAny(searchBag, ['tatlı', 'dessert', 'kek', 'pasta', 'kurabiye', 'baklava', 'sütlaç', 'helva', 'muhallebi']),
 meze: includesAny(searchBag, ['meze', 'salata', 'appetizer', 'ikramlık', 'dip', 'zeytinyağlı', 'yan_yemek', 'atıştırmalık']),
 meal: includesAny(searchBag, ['ana_yemek', 'ana yemek', 'main', 'çorba', 'soup', 'kebap', 'fırın', 'tencere', 'et', 'tavuk', 'balık', 'makarna']),
 savory: includesAny(searchBag, ['tuzlu', 'savory', 'börek', 'poğaça', 'kısır', 'meze', 'salata', 'ana_yemek', 'hamur işi', 'atıştırmalık'])
 };
};

const CATEGORY_LABELS = {
 'AUTO': 'Otomatik (AI Karar Versin)',
 'MAIN': 'Genel / Diğer',
 'LIGHT_MEAL': 'Hafif Öğün / Sandviç / Wrap',
 'BASE_PRODUCT': 'Baz Ürün / Hammadde',
 'FILLING': 'İç Harç / Dolgu',
 'SALAD': 'Salata / Meze',
 'SOUP': 'Çorba',
 'BREAKFAST': 'Kahvaltılık',
 'DESSERT': 'Tatlı',
 'PASTRY': 'Hamur İşi',
 'LEGUME': 'Bakliyat',
 'SEAFOOD': 'Deniz Ürünleri',
 'VEGAN_DIET': 'Vegan / Diyet',
 'PASTA': 'Makarna',
 'CHICKEN': 'Tavuk Yemekleri (Genel)',
 'MEAT': 'Et Yemekleri (Genel)',
 'SAUCE': 'Sos / Yan Ürün'
};
const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS);

const resolveImageUrl = (raw) => {
 const value = String(raw || '').trim();
 if (!value) return '';
 if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) return value;
 return `${API_BASE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
};

const UTF8_SUSPECT_PATTERN = /([\u00C3\u00C4\u00C5].|[\u00E2]\u20AC|\uFFFD)/g;

const stripControlChars = (value) =>
 String(value ?? '')
 .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, ' ')
 .replace(/\s+/g, ' ')
 .trim();

const normalizeExportText = (value) =>
 stripControlChars(String(value ?? '').normalize('NFC'));

const escapeCsvCell = (value) => {
 const text = normalizeExportText(value);
 if (/["\n,;]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
 return text;
};

const WIN1254_MAP = new Map([
 ['Ğ', 0xD0], ['ğ', 0xF0],
 ['İ', 0xDD], ['ı', 0xFD],
 ['Ş', 0xDE], ['ş', 0xFE],
 ['Ç', 0xC7], ['ç', 0xE7],
 ['Ö', 0xD6], ['ö', 0xF6],
 ['Ü', 0xDC], ['ü', 0xFC]
]);

const encodeWindows1254 = (input) => {
 const text = String(input ?? '');
 const bytes = [];
 for (const ch of text) {
 const cp = ch.codePointAt(0);
 if (cp <= 0x7F) {
 bytes.push(cp);
 continue;
 }
 if (WIN1254_MAP.has(ch)) {
 bytes.push(WIN1254_MAP.get(ch));
 continue;
 }
 if (cp <= 0xFF) {
 bytes.push(cp);
 continue;
 }
 // Unsupported char fallback
 bytes.push(0x3F);
 }
 return new Uint8Array(bytes);
};

const buildWindows1254CsvBlob = (text) => {
 const normalized = String(text ?? '');
 return new Blob([encodeWindows1254(normalized)], { type: 'text/csv;charset=windows-1254;' });
};

const normalizeAdminDisplayUnit = (unit) => {
 const raw = String(unit || '').trim().toLowerCase();
 if (!raw) return '';
 if (['unit', 'units', 'piece', 'pieces', 'adet', 'tane'].includes(raw)) return 'adet';
 if (raw === 'g') return 'gr';
 if (raw === 'l') return 'lt';
 return String(unit || '').trim();
};

const MEAT_TYPE_LABELS = {
 'AUTO': 'Otomatik (AI Karar Versin)',
 'Tavuk': 'Tavuk',
 'Dana': 'Kırmızı Et (Dana)',
 'Kuzu': 'Kırmızı Et (Kuzu)',
 'Balık': 'Balık',
 'Deniz Ürünü': 'Deniz Ürünü',
 'Sebze': 'Sebze (Proteinsiz)',
 'Bakliyat': 'Bakliyat',
 'Diğer': 'Diğer'
};
const MEAT_TYPE_KEYS = Object.keys(MEAT_TYPE_LABELS);
const PARTY_TYPE_OPTIONS = [
 { value: 'birthday', label: 'Doğum Günü' },
 { value: 'gold_day', label: 'Altın Günü' }
];

const FAMILY_OPTIONS = [
 'Genel Yemek Ailesi',
 'Fasulye Yemegi Ailesi',
 'Nohut Yemegi Ailesi',
 'Mercimek Yemegi Ailesi',
 'Pirinc Pilavi Ailesi',
 'Bulgur Pilavi Ailesi',
 'Makarna Ailesi',
 'Tencere Sebze Yemegi Ailesi',
 'Firin Sebze Yemegi Ailesi',
 'Zeytinyagli Sebze Ailesi',
 'Tavuk Sote Ailesi',
 'Tavuk Firin Ailesi',
 'Kirmizi Et Tencere Yemegi Ailesi',
 'Kirmizi Et Firin Yemegi Ailesi',
 'Kofte Ailesi',
 'Balik Tava Ailesi',
 'Balik Firin Ailesi',
 'Corba Ailesi',
 'Kahvaltilik Yumurta Ailesi',
 'Hamur Isi Ailesi',
 'Salata Ailesi',
 'Meze Ailesi',
 'Kuru Fasulye',
 'Barbunya Yemeği',
 'Nohut Yemeği',
 'Mercimek Yemeği',
 'Pilav',
 'Sebze Yemeği',
 'Kıymalı Yemeği',
 'Tavuk Yemeği',
 'Et Yemeği',
 'Balık Yemeği',
 'Köfte',
 'Dürüm / Sandviç',
 'Mantı / Hamur Yemeği',
 'Yumurta Yemeği',
 'Börek / Poğaça',
 'Fırın Yemeği',
 'Kavurma / Sote'
];

const normalizeFamilyName = (value) => {
 const raw = String(value || '').trim();
 if (!raw) return 'Genel Yemek Ailesi';
 const exact = FAMILY_OPTIONS.find((f) => f.toLocaleLowerCase('tr-TR') === raw.toLocaleLowerCase('tr-TR'));
 return exact || raw;
};

const parseBabyFriendly = (value) => {
 if (typeof value === 'boolean') return value;
 const v = String(value || '').toLocaleLowerCase('tr-TR').trim();
 if (!v) return false;
 const falseTokens = ['hayır', 'hayir', 'uygun değil', 'uygun degil', 'değil', 'degil', 'false', 'no', 'yok'];
 if (falseTokens.some((t) => v.includes(t))) return false;
 const trueTokens = ['evet', 'uygun', 'true', 'yes'];
 if (trueTokens.some((t) => v.includes(t))) return true;
 return false;
};

const ALLERGEN_OPTIONS = [
 'Süt Alerjisi',
 'Gluten Alerjisi',
 'Yumurta Alerjisi',
 'Fındık Alerjisi',
 'Balık Alerjisi',
 'Soya Alerjisi',
 'Susam Alerjisi',
 'Kabuklu Deniz Ürünleri'
];

/**
 * Helper to auto-detect meat type based on ingredient list
 */
const detectMeatType = (ingredients) => {
 if (!ingredients) return 'Diğer';
 const lower = String(ingredients).toLowerCase();

 const rules = [
 { type: 'Tavuk', keywords: ['tavuk', 'piliç', 'göğüs eti', 'baget', 'kanat'] },
 { type: 'Dana', keywords: ['dana', 'kıyma', 'kuşbaşı', 'antrikot', 'bonfile', 'steak'] },
 { type: 'Kuzu', keywords: ['kuzu', 'pirzola', 'gerdan', 'incik'] },
 { type: 'Balık', keywords: ['balık', 'levrek', 'çipura', 'somon', 'mezgit', 'hamsi', 'istavrit', 'lüfer'] },
 { type: 'Deniz Ürünü', keywords: ['karides', 'kalamar', 'ahtapot', 'midye'] },
 { type: 'Bakliyat', keywords: ['nohut', 'fasulye', 'mercimek', 'bakla', 'bezelye'] },
 { type: 'Sebze', keywords: ['mantar', 'patlıcan', 'kabak', 'patates', 'ıspanak'] }
 ];

 for (const rule of rules) {
 if (rule.keywords.some(k => lower.includes(k))) return rule.type;
 }
 return 'Diğer';
};

const normalizeDifficulty = (value) => {
 const v = String(value || '').toLocaleLowerCase('tr-TR');
 if (v.includes('kolay')) return 'Kolay';
 if (v.includes('zor')) return 'Zor';
 return 'Orta';
};

const deriveCategoryFromStrategicRole = (role) => {
 const r = String(role || '').toLocaleLowerCase('tr-TR');
 if (r.includes('çorba') || r.includes('corba')) return 'SOUP';
 if (r.includes('salata')) return 'SALAD';
 if (r.includes('tatlı') || r.includes('tatli') || r.includes('dessert')) return 'DESSERT';
 if (r.includes('kahvalt')) return 'BREAKFAST';
 if (r.includes('hamur')) return 'PASTRY';
 return 'MAIN';
};

const deriveServeStyleFromStrategicRole = (role) => {
 const r = String(role || '').toLocaleLowerCase('tr-TR');
 if (r.includes('meze')) return 'meze';
 if (r.includes('yan')) return 'yan_yemek';
 if (r.includes('atıştır') || r.includes('atistir')) return 'atıştırmalık';
 return 'ana_yemek';
};

const normalizeMode = (mode) => {
 const m = String(mode || '').toLocaleLowerCase('tr-TR');
 if (m === 'pressure_cooker') return 'pressure_cooker';
 if (m.includes('düdük') || m.includes('duduk')) return 'pressure_cooker';
 if (m.includes('fır') || m.includes('firin') || m === 'oven') return 'oven';
 if (m.includes('tava') || m === 'pan') return 'pan';
 if (m.includes('tencere') || m === 'pot') return 'pot';
 return 'common';
};

const normalizeStepType = (type) => {
 const t = String(type || '').toLocaleLowerCase('tr-TR').trim();
 if (!t) return 'preparation';
 if (t === 'preparation' || t.includes('hazırl') || t.includes('hazir')) return 'preparation';
 if (t === 'cooking' || t.includes('piş') || t.includes('pis')) return 'cooking';
 if (t === 'portion_split' || t.includes('böl') || t.includes('bol') || t.includes('ayır') || t.includes('ayir')) return 'portion_split';
 if (t === 'safety_check' || t.includes('güven') || t.includes('guven')) return 'safety_check';
 if (t === 'resting' || t.includes('dinlen')) return 'resting';
 if (t === 'serving' || t.includes('servis')) return 'serving';
 return 'preparation';
};

const normalizeTrigger = (trigger, normalizedType) => {
 const x = String(trigger || '').toLocaleLowerCase('tr-TR').trim();
 if (!x || x === 'null') {
 if (normalizedType === 'portion_split') return 'before_serving';
 if (normalizedType === 'safety_check') return 'texture';
 return null;
 }
 if (x === 'portion_split') return 'before_serving';
 const allowed = ['before_salt', 'before_sugar', 'before_serving', 'before_frying', 'after_cooking', 'texture'];
 return allowed.includes(x) ? x : null;
};

const normalizeStepTarget = (target, normalizedType) => {
 const t = String(target || '').toLocaleLowerCase('tr-TR').trim();
 if (t === '{bebek_ismi}' || t === 'bebek' || t === 'baby') return '{bebek_ismi}';
 if (normalizedType === 'portion_split' || normalizedType === 'safety_check') return '{bebek_ismi}';
 return 'all';
};

const normalizeIngredientType = (value) => {
 const v = String(value || '').toLocaleLowerCase('tr-TR').trim();
 if (v.includes('siv') || v === 'liquid') return 'sivi';
 return 'kati';
};

const splitByOrConnector = (text) => {
 const src = String(text || '').trim();
 if (!src) return [];
 const parts = [];
 let buf = '';
 let i = 0;
 let depth = 0;

 while (i < src.length) {
 const ch = src[i];
 if (ch === '(') {
 depth += 1;
 buf += ch;
 i += 1;
 continue;
 }
 if (ch === ')') {
 depth = Math.max(0, depth - 1);
 buf += ch;
 i += 1;
 continue;
 }

 if (depth === 0) {
 const rest = src.slice(i);
 const match = rest.match(/^(?:\s+)(veya|ya da)(?:\s+)/i);
 if (match) {
 if (buf.trim()) parts.push(buf.trim());
 buf = '';
 i += match[0].length;
 continue;
 }
 }

 buf += ch;
 i += 1;
 }

 if (buf.trim()) parts.push(buf.trim());
 return parts;
};

const uniqTextList = (values = []) => {
 const seen = new Set();
 const out = [];
 (Array.isArray(values) ? values : []).forEach((item) => {
 const text = String(item || '').replace(/\s+/g, ' ').trim();
 if (!text) return;
 const key = text.toLocaleLowerCase('tr-TR');
 if (seen.has(key)) return;
 seen.add(key);
 out.push(text);
 });
 return out;
};

const parseIngredientAlternatives = (rawName) => {
 const source = String(rawName || '').replace(/\s+/g, ' ').trim();
 if (!source) return { primary: '', alternatives: [] };

 const topLevelParts = splitByOrConnector(source);
 if (topLevelParts.length > 1) {
 return {
 primary: topLevelParts[0],
 alternatives: uniqTextList(topLevelParts.slice(1))
 };
 }

 const parenthetical = source.match(/^(.*?)\(([^()]+)\)\s*$/);
 if (parenthetical) {
 const base = String(parenthetical[1] || '').trim();
 const inside = String(parenthetical[2] || '').trim();
 const options = splitByOrConnector(inside);
 if (base && options.length > 1) {
 const expanded = options.map((opt) => {
 const token = String(opt || '').trim();
 if (!token) return '';
 return token.toLocaleLowerCase('tr-TR').includes(base.toLocaleLowerCase('tr-TR'))
 ? token
 : `${token} ${base}`.trim();
 }).filter(Boolean);
 return {
 primary: expanded[0] || source,
 alternatives: uniqTextList(expanded.slice(1))
 };
 }
 }

 return { primary: source, alternatives: [] };
};

const normalizeInstructionStep = (step, index) => ({
 ...(() => {
 const normalizedType = normalizeStepType(step?.type);
 return {
 adim: Number(step?.adim) || index + 1,
 islem: String(step?.islem || '').trim(),
 type: normalizedType,
 detay_acemi: String(step?.detay_acemi || step?.detayAcemi || '').trim(),
 detay_usta: String(step?.detay_usta || step?.detayUsta || '').trim(),
 detay_sef: String(step?.detay_sef || step?.detaySef || '').trim(),
 durationMinutes: Number(step?.durationMinutes) || 1,
 mode: normalizeMode(step?.mode),
 trigger: normalizeTrigger(step?.trigger, normalizedType),
 target: normalizeStepTarget(step?.target, normalizedType),
 neden: String(step?.neden || 'Tarif akış tutarlılığı için').trim()
 };
 })()
});

const normalizeIngredientObject = (item) => {
 if (typeof item === 'string') {
 const parsed = parseIngredientAlternatives(item);
  return {
 name: parsed.primary || item.trim(),
 malzeme: parsed.primary || item.trim(),
 miktar: '',
 birim: '',
 quantity: '',
 unit: '',
 turkmutfagi: '',
 turkmutfagiMiktar: '',
 turkmutfagiBirim: '',
 alternativeIds: [],
 alternativeNames: parsed.alternatives
  };
 }

 const rawName = String(item?.malzeme || item?.name || '').trim();
 const parsed = parseIngredientAlternatives(rawName);
 const name = parsed.primary || rawName;
 const amountRaw = String(item?.miktar ?? item?.amount ?? item?.quantity ?? '').trim();
 const unitRaw = String(item?.birim || item?.unit || item?.unitRaw || '').trim();
 const normalizedQty = normalizeNumericLike(String(item?.quantity ?? amountRaw).trim());
 const normalizedUnit = String(item?.unit ?? unitRaw).trim();
 const trAmount = String(
 item?.turkmutfagiMiktar ??
 item?.turkmutfagi_miktar ??
 item?.turkMutfagiMiktar ??
 ''
 ).trim();
 const trUnit = String(
 item?.turkmutfagiBirim ??
 item?.turkmutfagi_birim ??
 item?.turkMutfagiBirim ??
 ''
 ).trim();
 const mergedTurkMutfagi = String(
 item?.turkmutfagi ??
 item?.originalText ??
 (item?.turkmutfagi_miktar && item?.turkmutfagi_birim ? `${item.turkmutfagi_miktar} ${item.turkmutfagi_birim}` : '') ??
 (trAmount && trUnit ? `${trAmount} ${trUnit}` : '')
 ).trim();
 const inheritedAlternativeNames = Array.isArray(item?.alternativeNames) ? item.alternativeNames : [];

 return {
 ...item,
 name,
 malzeme: name,
 miktar: amountRaw,
 birim: unitRaw,
 birimTipi: normalizeIngredientType(item?.birimTipi ?? item?.birim_tipi),
 birim_tipi: item?.birim_tipi ?? item?.birimTipi ?? '',
 quantity: normalizedQty,
 unit: normalizedUnit,
 turkmutfagi: mergedTurkMutfagi,
 turkmutfagiMiktar: trAmount,
 turkmutfagiBirim: trUnit,
 turkmutfagi_miktar: trAmount,
 turkmutfagi_birim: trUnit,
 alternativeIds: Array.isArray(item?.alternativeIds) ? item.alternativeIds : [],
 alternativeNames: uniqTextList([
 ...parsed.alternatives,
 ...inheritedAlternativeNames
 ].filter((n) => String(n || '').toLocaleLowerCase('tr-TR') !== String(name || '').toLocaleLowerCase('tr-TR')))
 };
};

const buildIngredientResolverLine = (item) => {
 if (!item) return '';
 if (typeof item === 'string') return item.trim();

 const normalized = normalizeIngredientObject(item);
 const name = normalized.name;
 const amountRaw = normalized.miktar;
 const unitRaw = normalized.birim;
 const trAmount = normalized.turkmutfagiMiktar;
 const trUnit = normalized.turkmutfagiBirim;
 const quantity = String(normalized.quantity || amountRaw).trim();
 const unit = String(normalized.unit || unitRaw).trim();

 if (!name) return '';
 if (quantity && unit) return `${quantity} ${unit} ${name}`.trim();
 if (amountRaw && unitRaw) return `${amountRaw} ${unitRaw} ${name}`.trim();
 if (trAmount && trUnit) return `${trAmount} ${trUnit} ${name}`.trim();
 if (amountRaw) return `${amountRaw} ${name}`.trim();
 return name;
};

const normalizeNumericLike = (value) => {
 const text = String(value ?? '').trim();
 if (!text) return '';
 const parsed = parseFloat(text.replace(',', '.').replace(/[^0-9./-]/g, ''));
 return Number.isFinite(parsed) ? String(parsed) : text;
};

const normalizeResolutionKey = (value) => String(value || '')
 .toLocaleLowerCase('tr-TR')
 .normalize('NFD')
 .replace(/[\u0300-\u036f]/g, '')
 .replace(/ı/g, 'i')
 .replace(/ğ/g, 'g')
 .replace(/ü/g, 'u')
 .replace(/ş/g, 's')
 .replace(/ö/g, 'o')
 .replace(/ç/g, 'c')
 .replace(/[^a-z0-9 ]+/g, ' ')
 .replace(/\s+/g, ' ')
 .trim();

const ensureIngredientRowKeys = (rows = []) => {
 const list = Array.isArray(rows) ? rows : [];
 const nonce = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
 return list.map((item, idx) => {
 const normalized = normalizeIngredientObject(item);
 const existingRowKey = String(item?.rowKey || normalized?.rowKey || '').trim();
 if (existingRowKey) return { ...normalized, rowKey: existingRowKey };
 const baseName = normalizeResolutionKey(normalized?.malzeme || normalized?.name || 'malzeme') || 'malzeme';
 return {
 ...normalized,
 rowKey: `rk_${nonce}_${idx + 1}_${baseName.replace(/\s+/g, '_').slice(0, 28)}`
 };
 });
};

const getResolutionForRow = (row = {}, idx = -1, resolutions = []) => {
 const list = Array.isArray(resolutions) ? resolutions : [];
 const rowKey = String(row?.rowKey || '').trim();
 if (rowKey) {
 const byKey = list.find((r) => String(r?.rowKey || '').trim() === rowKey);
 return byKey || null;
 }
 if (idx >= 0 && idx < list.length) return list[idx] || null;
 return null;
};

const toMalzemeProjection = (row = {}, resolution = null) => {
 const rowBaseName = String(
 row?.malzeme ||
 row?.name ||
 row?.canonicalName ||
 row?.dictionaryName ||
 ''
 ).trim();
 const rowBaseKey = normalizeResolutionKey(rowBaseName);
 const resolutionName = String(
 resolution?.canonicalName ||
 resolution?.resolvedName ||
 ''
 ).trim();
 const resolutionKey = normalizeResolutionKey(resolutionName);
 const canApplyResolution = Boolean(
 resolution &&
 (!rowBaseKey || !resolutionKey || rowBaseKey === resolutionKey)
 );
 const safeResolution = canApplyResolution ? resolution : null;

 const resolvedCanonicalName = String(
 safeResolution?.canonicalName ||
 row?.canonicalName ||
 row?.dictionaryName ||
 row?.dictionary?.canonicalName ||
 ''
 ).trim();
 const name = String(
 resolvedCanonicalName ||
 row?.malzeme ||
 row?.name ||
 row?.dictionary?.canonicalName ||
 ''
 ).trim();
 const quantity = normalizeNumericLike(
 row?.quantity ??
 row?.miktar ??
 row?.amount ??
 row?.amountRaw ??
 ''
 );
 const unit = String(
 row?.unit ||
 row?.birim ||
 row?.unitRaw ||
 'adet'
 ).trim() || 'adet';
 const dictionaryId = String(
 safeResolution?.dictionaryId ||
 row?.dictionaryId ||
 row?.dictionary?.id ||
 ''
 ).trim();
 const trAmount = String(
 row?.turkmutfagiMiktar ??
 row?.turkmutfagi_miktar ??
 ''
 ).trim();
 const trUnit = String(
 row?.turkmutfagiBirim ??
 row?.turkmutfagi_birim ??
 ''
 ).trim();
 const turkmutfagi = String(
 row?.turkmutfagi ||
 row?.originalText ||
 (trAmount && trUnit ? `${trAmount} ${trUnit}` : '')
 ).trim();

 return {
 ...((typeof row === 'object' && row) ? row : {}),
 name,
 malzeme: name,
 canonicalName: resolvedCanonicalName || name,
 dictionaryName: resolvedCanonicalName || name,
 familyName: safeResolution?.familyName !== undefined ? safeResolution.familyName : row?.familyName,
 miktar: quantity,
 birim: unit,
 quantity,
 unit,
 amountRaw: quantity,
 unitRaw: unit,
 dictionaryId: dictionaryId || undefined,
 turkmutfagi,
 turkmutfagi_miktar: trAmount,
 turkmutfagiMiktar: trAmount,
 turkmutfagi_birim: trUnit,
 turkmutfagiBirim: trUnit,
 originalText: turkmutfagi || row?.originalText || ''
 };
};

const buildMalzemelerProjectionFromRows = (rows = [], resolutions = []) => {
 const list = Array.isArray(rows) ? rows : [];
 if (!list.length) return [];
 return list.map((row, idx) => toMalzemeProjection(row, getResolutionForRow(row, idx, resolutions)));
};

const buildApiErrorMessage = (data, fallback = 'İşlem başarısız') => {
 const base = data?.message || fallback;
 const errors = Array.isArray(data?.errors) ? data.errors : [];
 if (!errors.length) return base;
 const detail = errors
 .slice(0, 3)
 .map((e) => e?.message || e?.code)
 .filter(Boolean)
 .join(' | ');
 return detail ? `${base}: ${detail}` : base;
};

const mapIncomingChefJsonToDraft = (input) => {
 const src = input || {};
 const meta = src.tarifKimligi || {};
 const nutritional = src?.besinDegerleri?.nutritionalValues || src?.nutritionalValues || {};
 const incomingIngredients = Array.isArray(src.malzemeler) ? src.malzemeler : [];
 const incomingSteps = Array.isArray(src.adimlar) ? src.adimlar : [];

 const malzemeler = incomingIngredients.map(normalizeIngredientObject).filter((m) => m?.name);
 const instructions = incomingSteps.map((s, i) => normalizeInstructionStep(s, i));
 const containsAllergens = Array.isArray(meta.alerjenIceriyor) ? meta.alerjenIceriyor : (Array.isArray(meta.alerjanIceriyor) ? meta.alerjanIceriyor : []);
 const safeForAllergies = ALLERGEN_OPTIONS.filter((opt) => {
 const lowered = opt.toLocaleLowerCase('tr-TR');
 return !containsAllergens.some((a) => lowered.includes(String(a || '').toLocaleLowerCase('tr-TR')));
 });

 const role = meta.stratejikRol || '';
 const cookingType = String(meta.pisirmeTuru || '').toLocaleLowerCase('tr-TR');
 const meatType = detectMeatType(malzemeler.map(buildIngredientResolverLine).join('\n'));

 return {
 name: String(meta.yemekAdi || src.name || '').trim(),
 category: deriveCategoryFromStrategicRole(role),
 serveStyle: deriveServeStyleFromStrategicRole(role),
 ingredients: '',
 malzemeler,
 instructions,
 nutritionalValues: nutritional,
 cookingModes: src.cookingModes || {},
 dominants: Array.isArray(src.dominants) ? src.dominants : [],
 chef_note: String(src.sefOnerisi || '').trim(),
 babyAdjustment: String(src.bebekNotu || '').trim(),
 difficulty: normalizeDifficulty(meta.zorluk),
 family: normalizeFamilyName(meta.davranisAile),
 meatType,
 isVegan: !!meta.veganUygun,
 isVegetarian: !!meta.vejetaryenUygun,
 isChildFriendly: parseBabyFriendly(meta.bebekDostu),
 isDiet: false,
 requiresOven: cookingType.includes('fır') || cookingType.includes('firin'),
 safeForAllergies,
 imageUrl: '',
 servings: 1
 };
};

const CANONICAL_ALLERGEN_KEYS = [
 'sut',
 'gluten',
 'yumurta',
 'fistik',
 'soya',
 'balik',
 'kabuklu deniz urunleri',
 'findik'
];

const normalizeAllergenKey = (value) => {
 const v = String(value || '').toLocaleLowerCase('tr-TR').trim();
 if (!v) return '';
 if (v.includes('süt') || v.includes('sut')) return 'sut';
 if (v.includes('gluten')) return 'gluten';
 if (v.includes('yumurta')) return 'yumurta';
 if (v.includes('fıst') || v.includes('fist')) return 'fistik';
 if (v.includes('soya')) return 'soya';
 if (v.includes('balık') || v.includes('balik')) return 'balik';
 if (v.includes('kabuklu')) return 'kabuklu deniz urunleri';
 if (v.includes('fındık') || v.includes('findik')) return 'findik';
 return '';
};

const parseCsvAllergenLine = (line) => {
 const raw = String(line || '').trim();
 if (!raw) return [];
 return raw
 .split(',')
 .map((part) => normalizeAllergenKey(part))
 .filter(Boolean);
};

const parseLayerMeta = (segment, recipeName) => {
 const readLine = (labelRegex) => {
 const m = segment.match(labelRegex);
 return m ? String(m[1] || '').trim() : '';
 };

 const strategikRol = readLine(/Stratejik Rol\s*:\s*(.+)/i);
 const tamamlayici = readLine(/Tamamlayıcı\s*:\s*(.+)/i);
 const bebekDostu = readLine(/Bebek Dostu\s*:\s*(.+)/i);
 const pisirmeTuru = readLine(/Pişirme Türü\s*:\s*(.+)/i);
 const zorluk = readLine(/Yapım Zorluk Derecesi\s*:\s*(.+)/i);
 const davranisAile = readLine(/Davranış Ailesi\s*:\s*(.+)/i);
 const veganVejetaryen = readLine(/Vegan\/Vejetaryen\s*:\s*(.+)/i);
 const alerjenGuvenligiLine = readLine(/Alerjen Güvenliği\s*:\s*(.+)/i);
 const alerjenIceriyorLine = readLine(/Alerjen İçeriyor\s*:\s*(.+)/i);

 let alerjenGuvenli = parseCsvAllergenLine(alerjenGuvenligiLine);
 if (alerjenGuvenli.length === 0 && /içermez|guvenli/i.test(alerjenGuvenligiLine)) {
 alerjenGuvenli = [...CANONICAL_ALLERGEN_KEYS];
 }
 const alerjenIceriyor = parseCsvAllergenLine(alerjenIceriyorLine);

 const veganUygun = /vegan/i.test(veganVejetaryen);
 const vejetaryenUygun = /vejetaryen/i.test(veganVejetaryen);

 return {
 yemekAdi: recipeName,
 bolge: readLine(/(?:Bölge|Bolge)\s*:\s*(.+)/i) || 'Türkiye',
 davranisAile: normalizeFamilyName(davranisAile || 'Genel Yemek Ailesi'),
 stratejikRol: strategikRol || 'ana yemek',
 tamamlayici: tamamlayici || '',
 bebekDostu: bebekDostu || 'hayır',
 pisirmeTuru: pisirmeTuru || '',
 zorluk: zorluk || 'Orta',
 alerjenGuvenli,
 alerjenIceriyor,
 veganUygun,
 vejetaryenUygun,
 alerjanIceriyorsaOneri: ''
 };
};

const safeJsonParse = (text) => {
 try {
 return JSON.parse(text);
 } catch (e) {
 return null;
 }
};

const readJsonOrThrow = async (res, fallback = 'İşlem başarısız') => {
 const contentType = String(res?.headers?.get('content-type') || '').toLocaleLowerCase('tr-TR');
 if (!contentType.includes('application/json')) {
 const rawText = await res.text().catch(() => '');
 if (res.status === 401 || res.status === 403) {
 throw new Error('Oturum doğrulaması başarısız. Lütfen tekrar giriş yapın.');
 }
 if (String(rawText).includes('<!DOCTYPE') || String(rawText).includes('<html')) {
 throw new Error(`${fallback}: Sunucu JSON yerine HTML döndü (HTTP ${res.status}).`);
 }
 throw new Error(`${fallback}: Beklenmeyen yanıt türü alındı (HTTP ${res.status}).`);
 }
 const data = await res.json();
 if (!res.ok) {
 throw new Error(buildApiErrorMessage(data, `${fallback} (HTTP ${res.status})`));
 }
 return data;
};

const extractBalancedJsonFrom = (text, fromIndex) => {
 const src = String(text || '');
 let i = Math.max(0, Number(fromIndex) || 0);
 while (i < src.length && /\s/.test(src[i])) i += 1;
 const open = src[i];
 if (open !== '{' && open !== '[') return null;
 const close = open === '{' ? '}' : ']';
 let depth = 0;
 let inString = false;
 let escaped = false;
 for (let j = i; j < src.length; j += 1) {
 const ch = src[j];
 if (inString) {
 if (escaped) {
 escaped = false;
 continue;
 }
 if (ch === '\\') {
 escaped = true;
 continue;
 }
 if (ch === '"') inString = false;
 continue;
 }
 if (ch === '"') {
 inString = true;
 continue;
 }
 if (ch === open) depth += 1;
 if (ch === close) {
 depth -= 1;
 if (depth === 0) return src.slice(i, j + 1);
 }
 }
 return null;
};

const extractJsonByHeading = (segment, headingPatterns = []) => {
 const source = String(segment || '');
 let headingIndex = -1;
 for (const pattern of headingPatterns) {
 const m = source.match(pattern);
 if (m?.index !== undefined) {
 headingIndex = m.index;
 break;
 }
 }
 if (headingIndex < 0) return null;
 const openIdx = source.slice(headingIndex).search(/[\[{]/);
 if (openIdx < 0) return null;
 const jsonText = extractBalancedJsonFrom(source, headingIndex + openIdx);
 return safeJsonParse(jsonText);
};

const parseLayeredRecipeSegment = (segment, fallbackIndex = 0) => {
 const nameMatch = segment.match(/TAR[İI]F\s*:\s*(.+)/i);
 const recipeName = String(nameMatch?.[1] || `Tarif #${fallbackIndex + 1}`).trim();
 const meta = parseLayerMeta(segment, recipeName);

 const sectionBlocks = {};
 const sectionRegex = /(?:^|\n)\s*(\d+)\.\s*([^\n]+)[\s\S]*?```(?:json)?\s*([\s\S]*?)```/gi;
 let sectionMatch;
 while ((sectionMatch = sectionRegex.exec(segment)) !== null) {
 const idx = Number(sectionMatch[1]);
 const payload = safeJsonParse(sectionMatch[3]);
 if (payload !== null) sectionBlocks[idx] = payload;
 }

 const dominantsRaw = sectionBlocks[0] ?? extractJsonByHeading(segment, [
 /JSON\s+DOM[İI]NANT\s+B[İI]LE[ŞS]ENLER[İI]/i,
 /DOM[İI]NANT/i
 ]);
 const malzemelerRaw = sectionBlocks[1] ?? extractJsonByHeading(segment, [
 /JSON\s+MALZEME\s+L[İI]STES[İI]/i,
 /MALZEME\s+L[İI]STES[İI]/i
 ]);
 const adimlarRaw = sectionBlocks[2] ?? extractJsonByHeading(segment, [
 /JSON\s+ADIMLAR/i,
 /ADIMLAR/i
 ]);
 const besinRaw = sectionBlocks[3] ?? extractJsonByHeading(segment, [
 /BES[İI]N\s+DE[ĞG]ERLER[İI]/i
 ]);
 const cookingRaw = sectionBlocks[4] ?? extractJsonByHeading(segment, [
 /P[İI][ŞS]IRME\s+MODLARI/i,
 /COOKING\s+MODES/i
 ]);

 const bebekNotu = String((segment.match(/Bebek Notu\s*:\s*(.+)/i)?.[1] || '')).trim();
 const sefOnerisi = String((segment.match(/Şef Önerisi\s*:\s*(.+)/i)?.[1] || '')).trim();

 const dominants = Array.isArray(dominantsRaw)
 ? dominantsRaw
 : Array.isArray(dominantsRaw?.dominants)
 ? dominantsRaw.dominants
 : [];

 const malzemeler = Array.isArray(malzemelerRaw) ? malzemelerRaw.map((m) => ({
 ...m,
 birimTipi: normalizeIngredientType(m?.birimTipi || m?.birim_tipi || m?.type),
 turkmutfagiMiktar: String(m?.turkmutfagiMiktar ?? m?.turkmutfagi_miktar ?? '').trim(),
 turkmutfagiBirim: String(m?.turkmutfagiBirim ?? m?.turkmutfagi_birim ?? '').trim()
 })) : [];

 const adimlar = Array.isArray(adimlarRaw) ? adimlarRaw : [];
 const besinDegerleri = {
 nutritionalValues: besinRaw?.nutritionalValues || besinRaw || {}
 };
 const cookingModes = cookingRaw && typeof cookingRaw === 'object' ? cookingRaw : {};

 return {
 tarifKimligi: meta,
 malzemeler,
 adimlar,
 besinDegerleri,
 cookingModes,
 dominants,
 bebekNotu,
 sefOnerisi
 };
};

const parsePipelineBatchInput = (rawText) => {
 const raw = String(rawText || '').trim();
 if (!raw) return [];

 const asJson = safeJsonParse(raw);
 if (asJson !== null) return Array.isArray(asJson) ? asJson : [asJson];

 const starts = [...raw.matchAll(/(?:^|\n)\s*(?:\d+\.\s*)?TAR[İI]F\s*:/gi)];
 if (starts.length === 0) {
 throw new Error('Katmanlı format veya geçerli JSON bulunamadı.');
 }

 const segments = starts.map((match, idx) => {
 const startIdx = match.index ?? 0;
 const endIdx = idx + 1 < starts.length ? (starts[idx + 1].index ?? raw.length) : raw.length;
 return raw.slice(startIdx, endIdx).trim();
 }).filter(Boolean);

 return segments.map((segment, idx) => parseLayeredRecipeSegment(segment, idx));
};

export default function AdminRecipeIngestion({ initialTab = 'INGEST', mode = 'full' } = {}) {
 const manageOnly = mode === 'manage-only';
 const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
 const [activeTab, setActiveTab] = useState(manageOnly ? 'MANAGE' : initialTab); // 'INGEST', 'MANAGE', or 'AI_BULK'

 // Ingest State
 const [jsonInput, setJsonInput] = useState('');
 const [selectedCategory, setSelectedCategory] = useState('MAIN');
 const [stagedRecipes, setStagedRecipes] = useState([]);
 const [status, setStatus] = useState({ loading: false, message: '', type: '' });
 const [pipelineJsonInput, setPipelineJsonInput] = useState('');
 const [pipelineDraft, setPipelineDraft] = useState(null);
 const [pipelineQueue, setPipelineQueue] = useState([]);
 const [pipelineActiveIndex, setPipelineActiveIndex] = useState(0);
 const [pipelineStatus, setPipelineStatus] = useState({ loading: false, message: '', type: '' });
 const [pipelineImageUploading, setPipelineImageUploading] = useState(false);
 const [pipelinePartySuitable, setPipelinePartySuitable] = useState(false);
 const [pipelinePartyTypes, setPipelinePartyTypes] = useState([]);
 const [pipelineSpecialDayLabels, setPipelineSpecialDayLabels] = useState([]);

 // Manage State
 const [allDishes, setAllDishes] = useState([]);
 const [searchTerm, setSearchTerm] = useState('');
 const [filterCategory, setFilterCategory] = useState('ALL');
 const [filterQuality, setFilterQuality] = useState('ALL'); // ALL, MASTER, SKELETON
 const [filterParty, setFilterParty] = useState('ALL'); // ALL, PARTY, birthday, gold_day
 const [filterVerification, setFilterVerification] = useState('ALL'); // ALL, NEEDS_VERIFICATION
 const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
 const [editingDish, setEditingDish] = useState(null);
 const [isDishLoading, setIsDishLoading] = useState(false);
 const [togglingId, setTogglingId] = useState(null);
 const [selectedDishIds, setSelectedDishIds] = useState([]);
 const [saveTarget, setSaveTarget] = useState('DB'); // Always DB now

 // AI Bulk State
 const [aiNamesInput, setAiNamesInput] = useState('');
 const [aiCategory, setAiCategory] = useState('AUTO');

 // Modal State (Moved to Top)
const [modalTab, setModalTab] = useState('BASIC'); // 'BASIC', 'STEPS', 'COOKING', 'JSON'
const [nutritionJsonText, setNutritionJsonText] = useState(''); // Local state for JSON editor
const [healthSignalsJsonText, setHealthSignalsJsonText] = useState('');

 // Sync local JSON text when modal opens or editingDish changes
useEffect(() => {
if (editingDish && editingDish.nutritionalValues) {
setNutritionJsonText(JSON.stringify(editingDish.nutritionalValues, null, 2));
} else {
setNutritionJsonText('{}');
}
if (editingDish && editingDish.healthSignals) {
setHealthSignalsJsonText(JSON.stringify(editingDish.healthSignals, null, 2));
} else {
setHealthSignalsJsonText('{}');
}
}, [editingDish?.id, modalTab]);

 const [aiMeatType, setAiMeatType] = useState('AUTO');
 const [generatedRecipes, setGeneratedRecipes] = useState([]);
 const [failedRecipes, setFailedRecipes] = useState([]);
 const [aiStatus, setAiStatus] = useState({ loading: false, message: '' });
 const [ingredientsViewMode, setIngredientsViewMode] = useState('LIST'); // 'LIST' or 'JSON'
 const [dominantInput, setDominantInput] = useState('');
 const [ingredientResolutions, setIngredientResolutions] = useState([]);
 const [ingredientResolveNotice, setIngredientResolveNotice] = useState(null);
 const [jsonAdvancedUnlocked, setJsonAdvancedUnlocked] = useState(false);
 const [lastProjectionVerification, setLastProjectionVerification] = useState(null);
 const [dictionaryInspectorOpen, setDictionaryInspectorOpen] = useState(false);
 const [dictionaryInspectorQuery, setDictionaryInspectorQuery] = useState('');
 const [dictionaryInspectorResults, setDictionaryInspectorResults] = useState([]);
 const [dictionaryInspectorLoading, setDictionaryInspectorLoading] = useState(false);
 const [dictionaryInspectorError, setDictionaryInspectorError] = useState('');
 const [dictionaryInspectorSelectedRow, setDictionaryInspectorSelectedRow] = useState(null);
 const [dictionaryInspectorOnlyIssues, setDictionaryInspectorOnlyIssues] = useState(false);
 const [dictionaryInspectorOnlyMissingId, setDictionaryInspectorOnlyMissingId] = useState(false);
 const [dictionaryHealth, setDictionaryHealth] = useState({
 loading: false,
 error: '',
 data: null
 });
 const [isResolving, setIsResolving] = useState(false);
 const [previewServings, setPreviewServings] = useState(1);

 useEffect(() => {
 if (manageOnly && activeTab !== 'MANAGE') {
 setActiveTab('MANAGE');
 }
 }, [manageOnly, activeTab]);

 useEffect(() => {
 if (!ingredientResolveNotice) return undefined;
 const timer = setTimeout(() => setIngredientResolveNotice(null), 2600);
 return () => clearTimeout(timer);
 }, [ingredientResolveNotice]);

 useEffect(() => {
 setJsonAdvancedUnlocked(false);
 setLastProjectionVerification(null);
 setDictionaryInspectorOpen(false);
 setDictionaryInspectorQuery('');
 setDictionaryInspectorResults([]);
 setDictionaryInspectorError('');
 setDictionaryInspectorSelectedRow(null);
 setDictionaryInspectorOnlyIssues(false);
 setDictionaryInspectorOnlyMissingId(false);
 }, [editingDish?.id]);

 // Rescue Lab State
 const [rescueIdeas, setRescueIdeas] = useState([]);
 const [rescueStatus, setRescueStatus] = useState({ loading: false, message: '' });

 // Manual Entry State
 const [manualRecipe, setManualRecipe] = useState({
 name: '',
 category: 'MAIN',
 serveStyle: 'ana_yemek',
 ingredients: '',
 instructions: '',
nutritionJSON: '',
healthSignalsJSON: '',
 isRescue: false,
 isVegan: false,
 isVegetarian: true,
 isDiet: false,
 isChildFriendly: true,
 meatType: 'Diğer',
 prepTimeEstimate: 'MEDIUM',
 requiresOven: false, safeForAllergies: [...ALLERGEN_OPTIONS],
 imageUrl: '',
 servings: 1,
 difficulty: 'Kolay',
 family: 'Genel Yemek Ailesi',
 partySuitable: false,
 partyTypes: [],
 dominants: [],
 dominantJSON: '',
 safeForAllergies: [...ALLERGEN_OPTIONS]
 });
 const [manualStatus, setManualStatus] = useState({ loading: false, message: '' });
 const [imageUploading, setImageUploading] = useState(false);
 const [rescueDictionary, setRescueDictionary] = useState([]);
 const [triggerDictionary, setTriggerDictionary] = useState([]);
 const [isTriggerLoading, setIsTriggerLoading] = useState(false);
 const [isRescueLoading, setIsRescueLoading] = useState(false);
 const [triggerLastQuery, setTriggerLastQuery] = useState('');
 const [rescueLastQuery, setRescueLastQuery] = useState('');
 const [isYieldCalculating, setIsYieldCalculating] = useState(false);
 const searchTimeoutRef = React.useRef(null);

 const handleCalculateYield = async () => {
 const ingredientsToCalc = editingDish.malzemeler?.length > 0 ? editingDish.malzemeler : editingDish.ingredients;
 if (!ingredientsToCalc || ingredientsToCalc.length === 0) {
 Swal.fire('Eksik Bilgi', 'Öncelikle reçeteye malzeme eklemelisiniz. Yoksa neye göre hesap yapılacak?', 'warning');
 return;
 }

 setIsYieldCalculating(true);
 try {
 // Reçete özelliklerine göre basit süreç tahmini
 let processType = 'common';
 if (editingDish.requiresOven) {
 processType = 'bake';
 } else if (editingDish.name?.toLowerCase().includes('su') || editingDish.name?.toLowerCase().includes('et suyu')) {
 processType = 'boil_stock';
 } else if (editingDish.name?.toLowerCase().includes('sote') || editingDish.name?.toLowerCase().includes('kavurma')) {
 processType = 'saute';
 } else if (editingDish.name?.toLowerCase().includes('sos') || editingDish.name?.toLowerCase().includes('püre')) {
 processType = 'reduce_sauce';
 }

 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/calculate-yield`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 ingredients: ingredientsToCalc,
 processType
 })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');

 if (data.ok) {
 if (data.totalInputInGrams === 0) {
 Swal.fire('Hesaplama Sıfır', 'Malzemelerin gramajları anlaşılamadı (muhtemelen sadece "adet" tabanlı ve gramaj dönüşümü olmayan öğeler var).', 'info');
 return;
 }

 setEditingDish(prev => ({
 ...prev,
 producedAmount: data.netOutput,
 producedUnit: 'gr'
 }));

 const Toast = Swal.mixin({ toast: true, position: 'bottom-end', showConfirmButton: false, timer: 3000, timerProgressBar: true });
 Toast.fire({
 icon: 'success',
 title: `Verim: %${(data.yieldRate * 100).toFixed(0)} - Yeni Miktar: ${data.netOutput}g (Girdi: ${data.totalInputInGrams}g)`
 });
 } else {
 Swal.fire('Hata', data.message || 'Hesaplama başarısız oldu.', 'error');
 }
 } catch (err) {
 console.error('Yield Calculation Error:', err);
 Swal.fire('Bağlantı Hatası', 'Sunucu ile iletişim kurulamadı.', 'error');
 } finally {
 setIsYieldCalculating(false);
 }
 };

 const fetchRescueDictionary = async () => {
 try {
 const res = await fetch(`${API_BASE_URL}/api/admin/dictionary-rescue`, {
 credentials: 'include'
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) setRescueDictionary(data.items);
 } catch (err) {
 console.error('Fetch rescue dictionary error:', err);
 }
 };

 const fetchDictionaryHealth = async () => {
 setDictionaryHealth((prev) => ({ ...prev, loading: true, error: '' }));
 try {
  const res = await fetch(`${API_BASE_URL}/api/admin-recipes/dictionary-health/latest`, {
  credentials: 'include'
  });
  const contentType = String(res.headers.get('content-type') || '').toLocaleLowerCase('tr-TR');
  if (!contentType.includes('application/json')) {
  await res.text();
  throw new Error(`Dictionary sağlık endpoint'i JSON dönmedi (HTTP ${res.status}).`);
  }
  const data = await readJsonOrThrow(res, 'İstek başarısız');
  if (!res.ok) {
  throw new Error(data?.message || `Dictionary sağlık endpoint'i hata verdi (HTTP ${res.status}).`);
  }
  if (!data.ok) {
  throw new Error(data.message || 'Dictionary sağlık raporu alınamadı.');
  }
 setDictionaryHealth({
 loading: false,
 error: '',
 data: data.dictionaryHealth || null
 });
 } catch (err) {
 setDictionaryHealth({
 loading: false,
 error: err.message || 'Dictionary sağlık raporu alınamadı.',
 data: null
 });
 }
 };

 useEffect(() => {
 fetchRescueDictionary();
 fetchDictionaryHealth();
 }, []);

 const handleImageUpload = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 setImageUploading(true);
 const formData = new FormData();
 formData.append('image', file);

 try {
 const res = await fetch(`${API_BASE_URL}/api/upload`, {
 method: 'POST',
 body: formData
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) {
 if (editingDish) {
 setEditingDish({ ...editingDish, imageUrl: data.imageUrl, image_url: data.imageUrl });
 } else {
 setManualRecipe({ ...manualRecipe, imageUrl: data.imageUrl });
 }
 Swal.fire({
 icon: 'success',
 title: 'Yüklendi!',
 text: 'Resim başarıyla sunucuya kaydedildi.',
 timer: 1500,
 showConfirmButton: false
 });
 } else {
 Swal.fire('Hata', data.message || 'Yükleme başarısız.', 'error');
 }
 } catch (err) {
 console.error('Upload error:', err);
 Swal.fire('Hata', 'Bağlantı hatası.', 'error');
 } finally {
 setImageUploading(false);
 }
 };

 useEffect(() => {
 if (activeTab === 'MANAGE') {
 fetchDishes();
 fetchDictionaryHealth();
 }
 }, [activeTab]);

 useEffect(() => {
 if (editingDish) {
 setPreviewServings(editingDish.servings || 1);
 }
 }, [editingDish?.id]);

 const handleResolveIngredients = async (dish) => {
 const rawItems = Array.isArray(dish.malzemelerRaw) && dish.malzemelerRaw.length > 0
 ? dish.malzemelerRaw
 : (Array.isArray(dish.malzemeler) && dish.malzemeler.length > 0 ? dish.malzemeler : []);
 const ings = rawItems.length > 0 ? [] : (dish.ingredients || []);

 const normalizeNameKey = (value) => String(value || '')
 .toLocaleLowerCase('tr-TR')
 .normalize('NFD')
 .replace(/[\u0300-\u036f]/g, '')
 .replace(/ı/g, 'i')
 .replace(/ç/g, 'c')
 .replace(/ğ/g, 'g')
 .replace(/ö/g, 'o')
 .replace(/ş/g, 's')
 .replace(/ü/g, 'u')
 .replace(/[^a-z0-9]+/g, ' ')
 .trim();

 // Speed optimization: if legacy malzemeler has dictionaryId, reuse it for structured ingredients
 const rawByName = new Map();
 if (Array.isArray(rawItems)) {
 rawItems.forEach((it) => {
 if (!it || typeof it !== 'object') return;
 const key = normalizeNameKey(it.malzeme || it.name || it.canonicalName || '');
 if (!key || rawByName.has(key)) return;
 rawByName.set(key, it);
 });
 }

 const entries = ings.length > 0
 ? ings.map((i) => {
 const rawName = i.dictionary?.canonicalName || i.malzeme || i.name || i.ingredient?.name || '';
 const parsedAlt = parseIngredientAlternatives(rawName);
 const name = parsedAlt.primary || rawName;
 const mappedRaw = rawByName.get(normalizeNameKey(name)) || rawByName.get(normalizeNameKey(rawName)) || null;
 const effectiveDictionaryId = i.dictionaryId || mappedRaw?.dictionaryId || null;
 const qty = i.amountRaw || i.miktar || i.quantity || '';
 const unit = i.unitRaw || i.birim || i.unit || '';
 const line = `${qty} ${unit} ${name}`.trim();
 const isPreResolved = Boolean(effectiveDictionaryId);
 const mergedAltNames = uniqTextList([
 ...(Array.isArray(i.alternativeNames) ? i.alternativeNames : []),
 ...(Array.isArray(mappedRaw?.alternativeNames) ? mappedRaw.alternativeNames : []),
 ...parsedAlt.alternatives
 ]);
 const mergedAltIds = uniqTextList([
 ...(Array.isArray(i.alternativeIds) ? i.alternativeIds : []),
 ...(Array.isArray(mappedRaw?.alternativeIds) ? mappedRaw.alternativeIds : [])
 ]);
 return {
 line,
 isPreResolved,
 prefill: {
 rowKey: String(i.rowKey || mappedRaw?.rowKey || '').trim() || undefined,
 rawInput: line,
 resolvedName: name,
 found: isPreResolved,
 status: isPreResolved ? 'RESOLVED' : 'UNKNOWN',
 dictionaryId: effectiveDictionaryId,
 canonicalName: i.dictionary?.canonicalName || i.canonicalName || mappedRaw?.canonicalName || mappedRaw?.malzeme || mappedRaw?.name || name || null,
 familyName: i.dictionary?.familyName || i.familyName || mappedRaw?.familyName || null,
 alternativeIds: mergedAltIds,
 alternativeNames: mergedAltNames
 }
 };
 }).filter((e) => e.line.length > 0)
 : (Array.isArray(rawItems) ? rawItems : []).map((raw) => {
 const normalizedRaw = normalizeIngredientObject(raw);
 const line = buildIngredientResolverLine(normalizedRaw);
 const rawInput = (typeof raw === 'string' ? raw : line).trim();
 const rawDictionaryId = (typeof raw === 'object' && raw?.dictionaryId) ? raw.dictionaryId : null;
 const isPreResolved = Boolean(rawDictionaryId);
 return {
 line,
 isPreResolved,
 prefill: {
 rowKey: String(normalizedRaw.rowKey || raw?.rowKey || '').trim() || undefined,
 rawInput: rawInput || line,
 resolvedName: normalizedRaw.name || line,
 found: isPreResolved,
 status: isPreResolved ? 'RESOLVED' : 'UNKNOWN',
 dictionaryId: rawDictionaryId,
 canonicalName: normalizedRaw.name || null,
 familyName: normalizedRaw.familyName || null,
 alternativeIds: Array.isArray(normalizedRaw.alternativeIds) ? normalizedRaw.alternativeIds : [],
 alternativeNames: Array.isArray(normalizedRaw.alternativeNames) ? normalizedRaw.alternativeNames : []
 }
 };
 }).filter((e) => e.line.length > 0);

 if (entries.length === 0) {
 setIngredientResolutions([]);
 return;
 }

 const shouldResolveEntry = (e) => {
 const missingPrimary = !e.isPreResolved;
 const altNames = Array.isArray(e?.prefill?.alternativeNames) ? e.prefill.alternativeNames : [];
 const altIds = Array.isArray(e?.prefill?.alternativeIds) ? e.prefill.alternativeIds : [];
 const missingAlternatives = altNames.length > 0 && altIds.length < altNames.length;
 return missingPrimary || missingAlternatives;
 };
 const unresolvedEntries = entries.filter(shouldResolveEntry);
 if (unresolvedEntries.length === 0) {
 setIngredientResolutions(entries.map((e) => e.prefill));
 return;
 }

 setIsResolving(true);
 try {
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/resolve-ingredients-batch`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ ingredients: unresolvedEntries.map((e) => e.line) })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) {
 let unresolvedCursor = 0;
 const backendResolutions = Array.isArray(data.resolutions) ? data.resolutions : [];
 const merged = entries.map((entry) => {
 if (!shouldResolveEntry(entry)) return entry.prefill;
 const r = backendResolutions[unresolvedCursor] || entry.prefill;
 unresolvedCursor += 1;
 return r;
 });
 // [Variant System] Merge: preserve alternativeIds added in UI, don't let backend override wipe them
 setIngredientResolutions(prev => {
 const prevByRowKey = new Map(
 (Array.isArray(prev) ? prev : [])
 .filter((r) => String(r?.rowKey || '').trim())
 .map((r) => [String(r.rowKey).trim(), r])
 );
 return merged.map((resolvedRow, i) => {
 const rowKey = String(resolvedRow?.rowKey || entries[i]?.prefill?.rowKey || '').trim();
 const prevRow = (rowKey && prevByRowKey.get(rowKey)) || prev[i] || null;
 return {
 ...resolvedRow,
 rowKey: rowKey || resolvedRow?.rowKey,
 alternativeIds: (Array.isArray(prevRow?.alternativeIds) && prevRow.alternativeIds.length > 0)
 ? prevRow.alternativeIds
 : (resolvedRow.alternativeIds || []),
 alternativeNames: (Array.isArray(prevRow?.alternativeNames) && prevRow.alternativeNames.length > 0)
 ? prevRow.alternativeNames
 : (resolvedRow.alternativeNames || [])
 };
 });
 });
 }
 } catch (err) {
 console.error('Resolve error:', err);
 } finally {
 setIsResolving(false);
 }
 };

 useEffect(() => {
 if (editingDish && modalTab === 'MALZEMELER') {
 handleResolveIngredients(editingDish);
 }
 // NOTE: Intentionally NOT including ingredients.length or malzemeler.length.
 // Those would cause re-resolve on every single ingredient edit, wiping user changes.
 // Resolve is triggered only when: tab switches to MALZEMELER, or a new dish is opened.
 }, [modalTab, editingDish?.id]);

 const fetchDishes = async () => {
 try {
 const startedAt = performance.now();
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/list-lite`);
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) {
 setAllDishes(data.dishes);
 }
 console.log(`[PERF][AdminRecipeIngestion ListLite] total=${(performance.now() - startedAt).toFixed(1)}ms count=${data?.dishes?.length || 0}`);
 } catch (err) {
 console.error('Fetch dishes error:', err);
 }
 };

 const openDishEditor = async (dishLike) => {
 if (!dishLike?.id) {
 setEditingDish(dishLike || null);
 return;
 }

 try {
 setIsDishLoading(true);
 const startedAt = performance.now();
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/${dishLike.id}`);
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (!data.ok) {
 throw new Error(data.message || 'Tarif detayı alınamadı.');
 }
 const dish = data.dish || {};
 const manualLabels = Array.isArray(dish.specialDayManualLabels)
 ? dish.specialDayManualLabels
 : parseManualSpecialDayLabelsFromTags(dish.tags || []);
 setEditingDish({
 ...dish,
 specialDayManualLabels: manualLabels
 });
 console.log(`[PERF][AdminRecipeIngestion Detail] id=${dishLike.id} total=${(performance.now() - startedAt).toFixed(1)}ms`);
 } catch (err) {
 console.error('Fetch dish detail error:', err);
 Swal.fire('Hata', err.message || 'Tarif detayı alınamadı.', 'error');
 } finally {
 setIsDishLoading(false);
 }
 };

 const handleAddToList = () => {
 try {
 if (!jsonInput.trim()) return;
 const parsed = JSON.parse(jsonInput);
 const batch = Array.isArray(parsed) ? parsed : [parsed];

 setStagedRecipes(prev => [...prev, ...batch]);
 setJsonInput('');
 setStatus({ message: `${batch.length} tarif listeye eklendi.`, type: 'success' });
 } catch (err) {
 setStatus({ message: 'Geçersiz JSON formatı!', type: 'error' });
 }
 };

 const handlePipelineAnalyze = async () => {
 if (!pipelineJsonInput.trim()) {
 Swal.fire('Eksik Veri', 'Önce JSON metnini yapıştırın.', 'warning');
 return;
 }

 setPipelineStatus({ loading: true, message: 'Katmanlı tarif metni analiz ediliyor...', type: 'info' });
 try {
 const batch = parsePipelineBatchInput(pipelineJsonInput);
 const nextManualLabels = pipelinePartySuitable ? pipelineSpecialDayLabels : [];
 const queue = [];
 const invalidNames = [];
 let unresolvedRecipeCount = 0;

 for (let i = 0; i < batch.length; i += 1) {
 const draft = mapIncomingChefJsonToDraft(batch[i]);
 if (!draft.name || !Array.isArray(draft.malzemeler) || draft.malzemeler.length === 0) {
 invalidNames.push(`Tarif #${i + 1}`);
 continue;
 }

 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/resolve-ingredients-batch`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ ingredients: draft.malzemeler.map(buildIngredientResolverLine).filter(Boolean) })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 const resolutions = data?.ok && Array.isArray(data.resolutions)
 ? data.resolutions.map((r) => ({
 ...r,
 found: r?.found ?? r?.status === 'RESOLVED'
 }))
 : [];
 const unresolved = resolutions.filter((r) => !(r?.found || r?.dictionaryId));
 if (unresolved.length > 0) unresolvedRecipeCount += 1;

 const mergedIngredients = draft.malzemeler.map((ing, idx) => {
 const rr = resolutions[idx] || {};
 const trText = String(
 ing.turkmutfagi ||
 (ing.turkmutfagiMiktar && ing.turkmutfagiBirim ? `${ing.turkmutfagiMiktar} ${ing.turkmutfagiBirim}` : '')
 ).trim();
 const parsedAlt = parseIngredientAlternatives(ing?.name || ing?.malzeme || '');
 const mergedAlternativeNames = uniqTextList([
 ...(Array.isArray(rr?.alternativeNames) ? rr.alternativeNames : []),
 ...(Array.isArray(ing?.alternativeNames) ? ing.alternativeNames : []),
 ...parsedAlt.alternatives
 ]);
 const mergedAlternativeIds = uniqTextList([
 ...(Array.isArray(rr?.alternativeIds) ? rr.alternativeIds : []),
 ...(Array.isArray(ing?.alternativeIds) ? ing.alternativeIds : [])
 ]);
  return {
  ...ing,
  name: rr?.canonicalName ?? ing?.canonicalName ?? parsedAlt.primary ?? ing?.name,
  malzeme: rr?.canonicalName ?? ing?.canonicalName ?? parsedAlt.primary ?? ing?.malzeme,
  quantity: String(ing.quantity ?? ing.miktar ?? '').trim(),
  unit: String(ing.unit ?? ing.birim ?? '').trim(),
  turkmutfagi: trText,
  dictionaryId: rr?.dictionaryId ?? ing?.dictionaryId ?? null,
  canonicalName: rr?.canonicalName ?? ing?.canonicalName ?? ing?.name,
  familyName: rr?.familyName ?? ing?.familyName ?? null,
  alternativeIds: mergedAlternativeIds,
  alternativeNames: mergedAlternativeNames,
  found: rr?.found ?? false,
  originalText: buildIngredientResolverLine(ing),
  rawInput: rr?.rawInput || buildIngredientResolverLine(ing)
  };
  });

 const nextTags = applyManualSpecialDayLabelsToTags(draft.tags || [], nextManualLabels);
 queue.push({
 ...draft,
 partySuitable: !!pipelinePartySuitable,
 partyTypes: pipelinePartySuitable ? pipelinePartyTypes : [],
 specialDayManualLabels: nextManualLabels,
 tags: nextTags,
 ingredientResolutions: resolutions,
 ingredients: mergedIngredients,
 unresolvedCount: unresolved.length
 });
 }

 if (queue.length === 0) {
 setPipelineQueue([]);
 setPipelineDraft(null);
 setIngredientResolutions([]);
 setPipelineStatus({
 loading: false,
 message: 'Format çözümlendi ama geçerli yemek adı veya malzeme bulunamadı. Manuel kontrol ekranına geçin.',
 type: 'error'
 });
 return;
 }

 setPipelineQueue(queue);
 setPipelineActiveIndex(0);
 setPipelineDraft(queue[0]);
 setIngredientResolutions(queue[0].ingredientResolutions || []);

 if ((queue[0].unresolvedCount || 0) > 0) {
 setEditingDish({
 ...queue[0],
 isTemporary: true,
 ingredients: queue[0].ingredients || []
 });
 setModalTab('MALZEMELER');
 setPipelineStatus({
 loading: false,
 message: `${queue.length} tarif kuyruğa alındı. İlk tarifte ${queue[0].unresolvedCount} eşleşmeyen malzeme var, manuel kontrol ekranı açıldı.`,
 type: 'error'
 });
 return;
 }

 const invalidText = invalidNames.length > 0 ? ` Geçersiz atlanan: ${invalidNames.length}.` : '';
 const unresolvedText = unresolvedRecipeCount > 0 ? ` ${unresolvedRecipeCount} tarifte eşleşmeyen malzeme var.` : '';
 setPipelineStatus({
 loading: false,
 message: `${queue.length} tarif kuyruğa alındı.${invalidText}${unresolvedText} Görsel ekleyip kaydedin; hata varsa Manuel Kontrol ile düzeltin.`,
 type: 'success'
 });
 } catch (err) {
 setPipelineStatus({ loading: false, message: `Format hatası: ${err.message}. Manuel giriş ekranından kontrol ederek düzeltin.`, type: 'error' });
 }
};

 const handlePipelineImageUpload = async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;
 if (!pipelineDraft) return;

 setPipelineImageUploading(true);
 const formData = new FormData();
 formData.append('image', file);
 try {
 const res = await fetch(`${API_BASE_URL}/api/upload`, {
 method: 'POST',
 body: formData
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok && data.imageUrl) {
 setPipelineDraft((prev) => ({ ...prev, imageUrl: data.imageUrl }));
 setPipelineQueue((prev) => prev.map((item, idx) => (
 idx === pipelineActiveIndex ? { ...item, imageUrl: data.imageUrl } : item
 )));
 Swal.fire({
 icon: 'success',
 title: 'Görsel Yüklendi',
 text: 'Tarif görseli hazır, şimdi kaydedebilirsiniz.',
 timer: 1400,
 showConfirmButton: false
 });
 } else {
 throw new Error(data.message || 'Yükleme başarısız.');
 }
 } catch (err) {
 Swal.fire('Hata', err.message || 'Görsel yüklenemedi.', 'error');
 } finally {
 setPipelineImageUploading(false);
 }
 };

 const handlePipelineSave = async () => {
 if (!pipelineDraft) return;
 if (!pipelineDraft.imageUrl) {
 Swal.fire('Görsel Gerekli', 'Kaydetmeden önce ürün görseli yükleyin.', 'warning');
 return;
 }

 setPipelineStatus({ loading: true, message: 'Tarif kaydediliyor...', type: 'info' });
 try {
 const payload = {
 ...pipelineDraft,
 partySuitable: !!pipelinePartySuitable,
 partyTypes: pipelinePartySuitable ? pipelinePartyTypes : [],
 specialDayManualLabels: pipelinePartySuitable ? pipelineSpecialDayLabels : [],
 tags: applyManualSpecialDayLabelsToTags(
 pipelineDraft.tags || [],
 pipelinePartySuitable ? pipelineSpecialDayLabels : []
 )
 };
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/create?syncJson=false`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(payload)
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');

 if (!data.ok) {
 if (res.status === 422 && Array.isArray(data.errors) && data.errors.length > 0) {
 const detail = data.errors
 .slice(0, 5)
 .map((e) => `- ${e?.message || e?.code || 'bilinmeyen_hata'}`)
 .join('\n');
 setEditingDish({
 ...pipelineDraft,
 isTemporary: true,
 ingredients: (pipelineDraft.malzemeler || []).map((ing) => ({
 ...ing,
 originalText: buildIngredientResolverLine(ing),
 rawInput: buildIngredientResolverLine(ing)
 }))
 });
 setModalTab('MALZEMELER');
 setPipelineStatus({
 loading: false,
 message: `Malzeme kontratı hatası var:\n${detail}\nDüzeltme ekranı açıldı.`,
 type: 'error'
 });
 return;
 }
 throw new Error(buildApiErrorMessage(data, 'Kayıt başarısız.'));
 }

 setAllDishes((prev) => [data.dish, ...prev]);
 const remainingQueue = pipelineQueue.filter((_, idx) => idx !== pipelineActiveIndex);
 const nextIndex = Math.max(0, Math.min(pipelineActiveIndex, remainingQueue.length - 1));
 setPipelineQueue(remainingQueue);
 setPipelineActiveIndex(nextIndex);
 setPipelineDraft(remainingQueue[nextIndex] || null);
 setIngredientResolutions(remainingQueue[nextIndex]?.ingredientResolutions || []);
 const hasNext = remainingQueue.length > 0;
 setPipelineStatus({
 loading: false,
 message: hasNext
 ? `Tarif kaydedildi. Kuyrukta ${remainingQueue.length} tarif kaldı.`
 : 'Tarif başarıyla sisteme kaydedildi.',
 type: 'success'
 });
 if (!hasNext) {
 setPipelineJsonInput('');
 setPipelinePartySuitable(false);
 setPipelinePartyTypes([]);
 setPipelineSpecialDayLabels([]);
 }
 Swal.fire({
 icon: 'success',
 title: 'Kaydedildi',
 text: `"${data.dish?.name || payload.name}" sisteme eklendi.`,
 timer: 1400,
 showConfirmButton: false
 });
 } catch (err) {
 setPipelineStatus({ loading: false, message: err.message || 'Kayıt sırasında hata oluştu.', type: 'error' });
 }
 };

 const handleSelectPipelineDraft = (index) => {
 if (index < 0 || index >= pipelineQueue.length) return;
 setPipelineActiveIndex(index);
 const selected = pipelineQueue[index];
 setPipelineDraft(selected || null);
 setIngredientResolutions(selected?.ingredientResolutions || []);
 if ((selected?.unresolvedCount || 0) > 0) {
 setEditingDish({
 ...selected,
 isTemporary: true,
 ingredients: selected.ingredients || []
 });
 setModalTab('MALZEMELER');
 }
 };

 const handleExecute = async () => {
 if (stagedRecipes.length === 0) return;
 setStatus({ loading: true, message: 'Veriler işleniyor...', type: 'info' });
 try {
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/ingest-batch`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ recipes: stagedRecipes, defaultCategory: selectedCategory })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) {
 setStatus({ message: data.summary, type: 'success' });
 setStagedRecipes([]);
 } else {
 setStatus({ message: data.message || 'Bir hata oluştu.', type: 'error' });
 }
 } catch (err) {
 setStatus({ message: 'Sunucu hatası!', type: 'error' });
 } finally {
 setStatus(prev => ({ ...prev, loading: false }));
 }
 };

 const handleToggleActive = async (dish) => {
 if (togglingId) return;
 try {
 setTogglingId(dish.id);
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/toggle/${dish.id}`, {
 method: 'PATCH'
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) {
 setAllDishes(prev => prev.map(d => d.id === dish.id ? { ...d, isActive: data.dish.isActive } : d));
 } else {
 alert('Durum değiştirilemedi: ' + (data.message || 'Bilinmeyen hata'));
 }
 } catch (err) {
 console.error('Toggle error:', err);
 alert('Bağlantı hatası!');
 } finally {
 setTogglingId(null);
 }
 };


 const handleSort = (key) => {
 let direction = 'asc';
 if (sortConfig.key === key && sortConfig.direction === 'asc') {
 direction = 'desc';
 }
 setSortConfig({ key, direction });
 };

 const handleSelectDish = (id) => {
 setSelectedDishIds(prev =>
 prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
 );
 };

 const handleSelectAllFiltered = (filtered) => {
 const filteredIds = filtered.map(d => d.id);
 const allIn = filteredIds.every(id => selectedDishIds.includes(id));

 if (allIn) {
 setSelectedDishIds(prev => prev.filter(id => !filteredIds.includes(id)));
 } else {
 setSelectedDishIds(prev => Array.from(new Set([...prev, ...filteredIds])));
 }
 };

 const handleBulkDelete = async () => {
 if (selectedDishIds.length === 0) return;

 const result = await Swal.fire({
 title: 'Toplu Silme?',
 text: `${selectedDishIds.length} adet tarifi kalıcı olarak silmek istediğinize emin misiniz?`,
 icon: 'warning',
 showCancelButton: true,
 confirmButtonColor: '#ef4444',
 cancelButtonColor: '#64748b',
 confirmButtonText: 'Evet, Hepsini Sil!',
 cancelButtonText: 'Vazgeç'
 });

 if (result.isConfirmed) {
 setStatus({ loading: true, message: 'Toplu silme işlemi yapılıyor...', type: 'info' });
 try {
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/bulk-delete`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ ids: selectedDishIds })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) {
 setAllDishes(prev => prev.filter(d => !selectedDishIds.includes(d.id)));
 setSelectedDishIds([]);
 Swal.fire('Başarılı!', data.message, 'success');
 } else {
 Swal.fire('Hata!', data.message || 'Silme işlemi başarısız.', 'error');
 }
 } catch (err) {
 console.error('Bulk delete error:', err);
 Swal.fire('Hata!', 'Bağlantı hatası!', 'error');
 } finally {
 setStatus({ loading: false, message: '', type: '' });
 }
 }
 };

 const filteredDishes = allDishes
 .filter(d => {
 const matchesSearch = (d.name || '').toLowerCase().includes(searchTerm.toLowerCase());
 const matchesCategory = filterCategory === 'ALL' || d.category === filterCategory;
 const isMaster = d.instructions && Array.isArray(d.instructions) && d.instructions.length > 0;
 const matchesQuality = filterQuality === 'ALL' || (filterQuality === 'MASTER' && isMaster) || (filterQuality === 'SKELETON' && !isMaster);
 const dishPartyTypes = Array.isArray(d.partyTypes) ? d.partyTypes : [];
 const matchesParty = filterParty === 'ALL'
 || (filterParty === 'PARTY' && !!d.partySuitable)
 || dishPartyTypes.includes(filterParty);
 const hasVerificationIssue = Number(d.unresolvedIngredientCount || 0) > 0 || !!d.hasVerificationIssue;
 const matchesVerification = filterVerification === 'ALL' || (filterVerification === 'NEEDS_VERIFICATION' && hasVerificationIssue);
 return matchesSearch && matchesCategory && matchesQuality && matchesParty && matchesVerification;
 })
 .sort((a, b) => {
 const key = sortConfig.key;
 const dir = sortConfig.direction === 'asc' ? 1 : -1;

 if (key === 'name') {
 return (a.name || '').localeCompare((b.name || ''), 'tr') * dir;
 }
 if (key === 'category') {
 const catA = CATEGORY_LABELS[a.category] || a.category || '';
 const catB = CATEGORY_LABELS[b.category] || b.category || '';
 return catA.localeCompare(catB, 'tr') * dir;
 }
 if (key === 'createdAt') {
 const dateA = new Date(a.createdAt || 0).getTime();
 const dateB = new Date(b.createdAt || 0).getTime();
 return (dateA - dateB) * dir;
 }
 return 0;
 });


 const handleGenerateAI = async () => {
 if (!aiNamesInput.trim()) return;
 const names = aiNamesInput.split('\n').map(n => n.trim()).filter(n => n.length > 0);
 if (names.length === 0) return;

 setAiStatus({ loading: true, message: `Hazırlanıyor: 0 / ${names.length} tarif...` });
 setGeneratedRecipes([]);
 setFailedRecipes([]);

 let successCount = 0;
 let failCount = 0;

 for (let i = 0; i < names.length; i++) {
 const name = names[i];
 setAiStatus({ loading: true, message: `İşleniyor (${i + 1}/${names.length}): ${name}...` });

 try {
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/bulk-generate`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 recipeNames: [name],
 category: aiCategory === 'AUTO' ? null : aiCategory,
 meatType: aiMeatType === 'AUTO' ? null : aiMeatType
 })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');

 if (data.ok) {
 if (data.recipes && data.recipes.length > 0) {
 setGeneratedRecipes(prev => [...prev, ...data.recipes]);
 successCount++;
 }
 if (data.failures && data.failures.length > 0) {
 setFailedRecipes(prev => [...prev, ...data.failures]);
 failCount += data.failures.length;
 }
 } else {
 setFailedRecipes(prev => [...prev, { name, error: data.message || 'Bilinmeyen hata' }]);
 failCount++;
 }
 } catch (err) {
 setFailedRecipes(prev => [...prev, { name, error: 'Bağlantı Hatası' }]);
 failCount++;
 }
 }

 setAiStatus({
 loading: false,
 message: `Tamamlandı! ✅ Başarılı: ${successCount}, ❌ Hatalı: ${failCount}`
 });
 };

 const handleSaveAiRecipe = (recipe, originalName) => {
 setStagedRecipes(prev => [...prev, recipe]);
 const nameToRemove = originalName || recipe.name;
 setGeneratedRecipes(prev => prev.filter(r => r.name !== nameToRemove));
 setStatus({ message: `"${recipe.name}" kuyruğa eklendi.`, type: 'success' });
 setActiveTab('INGEST');
 };

 const handleDownloadJson = (recipes) => {
 const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(recipes, null, 2));
 const downloadAnchorNode = document.createElement('a');
 downloadAnchorNode.setAttribute("href", dataStr);
 downloadAnchorNode.setAttribute("download", `master_recipes_${new Date().toISOString().slice(0, 10)}.json`);
 document.body.appendChild(downloadAnchorNode);
 downloadAnchorNode.click();
 downloadAnchorNode.remove();
 };

 const handleProcessManualEntry = async () => {
 if (!manualRecipe.name || !manualRecipe.ingredients) {
 Swal.fire('Hata!', 'Lütfen yemek adını ve malzemeleri doldurun.', 'error');
 return;
 }

 setManualStatus({ loading: true, message: 'Veriler işleniyor...' });
 try {
 let finalDraft = { ...manualRecipe };

 // 1. Smart Ingredients Parsing
 if (typeof manualRecipe.ingredients === 'string') {
 const trimmed = manualRecipe.ingredients.trim();
 if (trimmed.startsWith('[') || (trimmed.startsWith('{') && !trimmed.includes('\n'))) {
 try {
 const parsed = JSON.parse(trimmed);
 finalDraft.malzemeler = Array.isArray(parsed) ? parsed : [parsed];
 } catch (e) {
 finalDraft.malzemeler = manualRecipe.ingredients.split('\n').filter(Boolean);
 }
 } else {
 finalDraft.malzemeler = manualRecipe.ingredients.split('\n').filter(Boolean);
 }
 }

 // 2. Smart Instructions Parsing (NEW)
 if (manualRecipe.instructions) {
 const trimmed = typeof manualRecipe.instructions === 'string' ? manualRecipe.instructions.trim() : '';
 if (trimmed.startsWith('[') || (trimmed.startsWith('{') && !trimmed.includes('\n'))) {
 try {
 const parsed = JSON.parse(trimmed);
 finalDraft.instructions = Array.isArray(parsed) ? parsed : [parsed];
 } catch (e) {
 // Keep as string
 finalDraft.instructions = trimmed;
 }
 } else {
 finalDraft.instructions = trimmed;
 }
 }

// 3. Handle Nutrition (support both flat and nested formats)
if (manualRecipe.nutritionJSON) {
 try {
 const parsed = JSON.parse(manualRecipe.nutritionJSON);
 // Handle both {calories: ...} and {nutritionalValues: {calories: ...}} formats
 if (parsed.nutritionalValues) {
 finalDraft.manualNutrition = parsed.nutritionalValues;
 } else {
 finalDraft.manualNutrition = parsed;
 }
 } catch (e) {
 Swal.fire('JSON Hatası', 'Besin değerleri JSON formatı hatalı.', 'error');
 setManualStatus({ loading: false, message: '' });
 return;
 }
}

// 3.5 Handle Health Signals JSON (optional manual override)
if (manualRecipe.healthSignalsJSON) {
try {
const parsed = JSON.parse(manualRecipe.healthSignalsJSON);
finalDraft.healthSignals = parsed;
} catch (e) {
Swal.fire('JSON Hatası', 'Sağlık etkisi JSON formatı hatalı.', 'error');
setManualStatus({ loading: false, message: '' });
return;
}
}

// 4. Pass all manual metadata
finalDraft.isRescue = manualRecipe.isRescue;
 finalDraft.isVegan = manualRecipe.isVegan;
 finalDraft.isVegetarian = manualRecipe.isVegetarian;
 finalDraft.isDiet = manualRecipe.isDiet;
 finalDraft.isChildFriendly = manualRecipe.isChildFriendly;
 finalDraft.meatType = manualRecipe.meatType;
 finalDraft.prepTimeEstimate = manualRecipe.prepTimeEstimate;
 finalDraft.requiresOven = manualRecipe.requiresOven;
 finalDraft.imageUrl = manualRecipe.imageUrl;
 finalDraft.serveStyle = manualRecipe.serveStyle;
 finalDraft.difficulty = manualRecipe.difficulty;
 finalDraft.family = manualRecipe.family || 'Genel Yemek Ailesi';
 finalDraft.dominants = manualRecipe.dominants || [];
 finalDraft.servings = parseInt(manualRecipe.servings) || 1;

 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/manual-generate`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ draft: finalDraft })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok && data.recipes && data.recipes.length > 0) {
 const enriched = data.recipes[0];

 // CRITICAL: Ensure manual nutrition is NOT lost if AI failed or returned empty
 if (!enriched.nutritionalValues || Object.keys(enriched.nutritionalValues).length === 0) {
 if (finalDraft.manualNutrition) enriched.nutritionalValues = finalDraft.manualNutrition;
 }

 setEditingDish({ ...enriched, isTemporary: true });
 setModalTab('BASIC');
 setManualStatus({ loading: false, message: '' });

 if (enriched.enrichmentFailed) {
 Swal.fire({
 icon: 'warning',
 title: 'Modala Aktarıldı',
 text: 'Verileriniz modal içine aktarıldı. Lütfen kontrol edip eksikleri modal üzerinde tamamlayın.',
 confirmButtonText: 'Düzenlemeye Başla'
 });
 } else {
 Swal.fire({
 icon: 'success',
 title: 'Başarılı!',
 text: 'Veriler modal düzenleme ekranına yüklendi.',
 timer: 2000,
 showConfirmButton: false
 });
 }
 } else {
 throw new Error(data.message || 'İşlem başarısız oldu.');
 }
 } catch (err) {
 console.error('Manual processing error:', err);
 setManualStatus({ loading: false, message: '' });
 Swal.fire('Hata!', err.message || 'Bağlantı hatası!', 'error');
 }
 };




 /**
 * Unified Save Handler
 * Respects 'saveTarget' state { db, json }
 */
 const handleUpdateDish = async (e) => {
 e.preventDefault();

 const isNewRecipe = !editingDish || !editingDish.id;
 const isIngredientsJsonEditMode = modalTab === 'MALZEMELER' && ingredientsViewMode === 'JSON';

 // 1. Prepare Payload
 const payload = { ...editingDish };
 const activeRowsForSave = (Array.isArray(activeIngredientRows) ? activeIngredientRows : []).map((row, idx) =>
 toMalzemeProjection(row, getResolutionForRow(row, idx, ingredientResolutions))
 );
 if (activeRowsForSave.length > 0) {
 payload.ingredients = activeRowsForSave.map((row) => ({ ...row }));
 payload.malzemeler = activeRowsForSave.map((row) => ({ ...row }));
 }
 if (!isIngredientsJsonEditMode) {
 const listSourceForProjection = (Array.isArray(payload.malzemeler) && payload.malzemeler.length > 0)
 ? payload.malzemeler
 : (Array.isArray(payload.ingredients) ? payload.ingredients : []);
 if (listSourceForProjection.length > 0) {
 payload.malzemeler = buildMalzemelerProjectionFromRows(listSourceForProjection, ingredientResolutions);
 }
 }
 const manualLabels = Array.isArray(payload.specialDayManualLabels)
 ? payload.specialDayManualLabels
 : parseManualSpecialDayLabelsFromTags(payload.tags || []);
 payload.specialDayManualLabels = manualLabels;
 payload.tags = applyManualSpecialDayLabelsToTags(payload.tags || [], manualLabels);

 // Common Parsing Logic for JSON fields
const jsonFields = ['nutritionalValues', 'healthSignals', 'malzemeler', 'cookingModes', 'pairings'];
 jsonFields.forEach(field => {
 if (typeof payload[field] === 'string') {
 try {
 let parsed = JSON.parse(payload[field]);

 // ⚠️ SPECIAL FIX FOR COOKING MODES: Automatic Array -> Object Conversion
 // The UI expects { "oven": { "time": "20" } } but users often paste [{ "mode": "oven", "time": "20" }]
 if (field === 'cookingModes' && Array.isArray(parsed)) {
 console.warn('⚠️ Converting cookingModes from Array to Object...');
 const converted = {};
 parsed.forEach(item => {
 if (item.mode) {
 converted[item.mode] = item;
 }
 });
 parsed = converted;
 }

 payload[field] = parsed;
 } catch (e) {
 console.error(`Failed to parse ${field}:`, e);
 payload[field] = undefined; // If invalid JSON, don't send it or send as undefined
 }
 }
 });

 // Instructions Special Handling (Hybrid: JSON Array OR String Fallback)
 if (typeof payload.instructions === 'string') {
 const trimmed = payload.instructions.trim();
 // Try parsing as JSON first
 try {
 const parsed = JSON.parse(trimmed);
 if (Array.isArray(parsed)) {
 payload.instructions = parsed;
 } else {
 throw new Error('Not an array');
 }
 } catch (e) {
 // Fallback: Treat as single step plain text
 payload.instructions = trimmed ? [{ adim: 1, islem: "Hazırlık", detay: trimmed }] : [];
 }
 } else if (!Array.isArray(payload.instructions)) {
 payload.instructions = [];
 }

 if (payload.nutritionalValues) {
 const parseMacro = (val) => {
 if (!val) return null;
 const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
 return isNaN(num) ? null : num;
 };
 payload.totalCalories = parseInt(payload.nutritionalValues.calories) || null;
 payload.proteinGrams = parseMacro(payload.nutritionalValues.protein);
 payload.fatGrams = parseMacro(payload.nutritionalValues.fat);
 payload.carbGrams = parseMacro(payload.nutritionalValues.carbs);
 }

 payload.chef_note = payload.chef_note || '';
 delete payload.enrichmentFailed;
 delete payload.errorMessage;
 delete payload.isTemporary;
 delete payload.originalName;
 delete payload.specialDayLabels;
 delete payload.specialDayLabelsManual;

        console.log('Final Payload for DB:', payload);

 try {
 let savedDishFromDb = null;

 // 2. Save to DB (if selected)
 if (saveTarget === 'DB') {
                console.log('Saving to DB...');
 let res;
 if (isNewRecipe) {
 res = await fetch(`${API_BASE_URL}/api/admin-recipes/create?syncJson=false`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(payload)
 });
 } else {
 res = await fetch(`${API_BASE_URL}/api/admin-recipes/update/${editingDish.id}?syncJson=false`, {
 method: 'PATCH',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(payload)
 });
 }

                console.log('Response status:', res.status);
 const data = await readJsonOrThrow(res, 'DB Kaydı Başarısız');
                console.log('Response data:', data);

 if (!data.ok) throw new Error(buildApiErrorMessage(data, 'DB Kaydı Başarısız'));
 savedDishFromDb = data.dish;
 setLastProjectionVerification(data?.projectionVerification || null);

 // Update Local State with DB Result
 if (isNewRecipe) {
 setAllDishes(prev => [savedDishFromDb, ...prev]);
 } else {
 setAllDishes(prev => prev.map(d => String(d.id) === String(editingDish.id) ? savedDishFromDb : d));
 }
 }

 // 3. Save to JSON - DEPRECATED

 const isPipelineFlow = !!editingDish?.isTemporary && pipelineQueue.length > 0;
 if (isPipelineFlow) {
 const remainingQueue = pipelineQueue.filter((_, idx) => idx !== pipelineActiveIndex);
 const nextIndex = Math.max(0, Math.min(pipelineActiveIndex, remainingQueue.length - 1));
 const nextDraft = remainingQueue[nextIndex] || null;

 setPipelineQueue(remainingQueue);
 setPipelineActiveIndex(nextIndex);
 setPipelineDraft(nextDraft);
 setIngredientResolutions(nextDraft?.ingredientResolutions || []);

 if (nextDraft && (nextDraft.unresolvedCount || 0) > 0) {
 setEditingDish({
 ...nextDraft,
 isTemporary: true,
 ingredients: nextDraft.ingredients || []
 });
 setModalTab('MALZEMELER');
 setPipelineStatus({
 loading: false,
 message: `Tarif kaydedildi. Kuyrukta ${remainingQueue.length} tarif kaldı, sıradaki tarifte eşleşmeyen malzeme var.`,
 type: 'error'
 });
 } else {
 setEditingDish(null);
 setModalTab('BASIC');
 setPipelineStatus({
 loading: false,
 message: nextDraft
 ? `Tarif kaydedildi. Kuyrukta ${remainingQueue.length} tarif kaldı.`
 : 'Kuyruktaki tüm tarifler tamamlandı.',
 type: 'success'
 });
 }
 } else {
 // Success!
 setEditingDish(null);
 setModalTab('BASIC');
 }
 Swal.fire({
 icon: 'success',
 title: 'Kaydedildi!',
 html: 'Veritabanı güncellendi',
 timer: 1500,
 showConfirmButton: false
 });

 } catch (err) {
 console.error('Save Error:', err);
 Swal.fire({
 icon: 'error',
 title: 'Kayıt Hatası',
 text: err.message || 'Bir hata oluştu.',
 confirmButtonColor: '#10b981'
 });
 }
 };

 const getIngredientResolutionState = (resolution = {}) => {
 const status = String(resolution?.status || '').toUpperCase();
 const confidence = Number(resolution?.confidence ?? 0);
 const hasCandidates = Array.isArray(resolution?.candidates) && resolution.candidates.length > 0;
 const hasDictionaryId = Boolean(resolution?.dictionaryId && String(resolution.dictionaryId) !== 'unknown');
 const found = Boolean(resolution?.found || hasDictionaryId);

 if (!found) return 'failed';
 if (status === 'AMBIGUOUS' || hasCandidates || (confidence > 0 && confidence < 0.92)) return 'needs_verification';
 return 'matched';
 };

 const activeIngredientRows = (() => {
 if (!editingDish) return [];
 if (Array.isArray(editingDish.malzemeler) && editingDish.malzemeler.length > 0) return editingDish.malzemeler;
 if (Array.isArray(editingDish.malzemelerRaw) && editingDish.malzemelerRaw.length > 0) return editingDish.malzemelerRaw;
 if (Array.isArray(editingDish.ingredients) && editingDish.ingredients.length > 0) return editingDish.ingredients;
 return [];
 })();

 const dictionaryInspectorRows = activeIngredientRows.map((item, index) => {
 const resolution = getResolutionForRow(item, index, ingredientResolutions) || {};
 const state = getIngredientResolutionState(resolution);
 const rawLine = buildIngredientResolverLine(item) || resolution?.rawInput || '';
 const rowName =
 item?.malzeme ||
 item?.name ||
 resolution?.canonicalName ||
 resolution?.resolvedName ||
 `Satir ${index + 1}`;

 return {
 index,
 rowName,
 rawLine,
 state,
 dictionaryId: resolution?.dictionaryId || item?.dictionaryId || '',
 canonicalName: resolution?.canonicalName || item?.dictionaryName || item?.canonicalName || '',
 familyName: resolution?.familyName || item?.familyName || '',
 confidence: Number(resolution?.confidence ?? 0),
 rawItem: item,
 resolution
 };
 });

 const normalizedDictionaryInspectorQuery = String(dictionaryInspectorQuery || '')
 .toLocaleLowerCase('tr-TR')
 .trim();

 const filteredDictionaryInspectorRows = dictionaryInspectorRows.filter((row) => {
 if (dictionaryInspectorOnlyIssues && row.state === 'matched') return false;
 if (dictionaryInspectorOnlyMissingId) {
 const dictId = String(row.dictionaryId || '').trim().toLocaleLowerCase('tr-TR');
 if (dictId && dictId !== 'unknown' && dictId !== 'null' && dictId !== 'undefined') return false;
 }
 if (!normalizedDictionaryInspectorQuery) return true;
 const haystack = [
 row.rowName,
 row.rawLine,
 row.dictionaryId,
 row.canonicalName,
 row.familyName
 ].join(' ').toLocaleLowerCase('tr-TR');
 return haystack.includes(normalizedDictionaryInspectorQuery);
 });

 const selectedDictionaryInspectorRow =
 dictionaryInspectorSelectedRow === null || dictionaryInspectorSelectedRow === undefined
 ? null
 : dictionaryInspectorRows.find((row) => row.index === dictionaryInspectorSelectedRow) || null;

 useEffect(() => {
 if (!dictionaryInspectorOpen) return undefined;
 const q = String(dictionaryInspectorQuery || '').trim();
 if (q.length < 2) {
 setDictionaryInspectorResults([]);
 setDictionaryInspectorError('');
 setDictionaryInspectorLoading(false);
 return undefined;
 }

 const timer = setTimeout(async () => {
 setDictionaryInspectorLoading(true);
 setDictionaryInspectorError('');
 try {
 const res = await fetch(`${API_BASE_URL}/api/inventory/search-ontology?q=${encodeURIComponent(q)}`);
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 setDictionaryInspectorResults(Array.isArray(data) ? data : []);
 } catch (err) {
 console.error(err);
 setDictionaryInspectorError('Dictionary aramasi yapilamadi.');
 setDictionaryInspectorResults([]);
 } finally {
 setDictionaryInspectorLoading(false);
 }
 }, 220);

 return () => clearTimeout(timer);
 }, [dictionaryInspectorOpen, dictionaryInspectorQuery]);

 const handleCopyDictionaryValue = async (value) => {
 const text = String(value || '').trim();
 if (!text) return;
 try {
 await navigator.clipboard.writeText(text);
 setIngredientResolveNotice({ type: 'success', text: `Dictionary ID kopyalandi: ${text}` });
 } catch (err) {
 console.error(err);
 setIngredientResolveNotice({ type: 'warning', text: 'Dictionary ID kopyalanamadi.' });
 }
 };

 const getNextDictionaryInspectorIssueRow = (currentIndex, rows = dictionaryInspectorRows, skipIndex = null) => {
 const issueRows = rows.filter((row) => row.state !== 'matched' && row.index !== skipIndex);
 if (issueRows.length === 0) return null;
 const currentIssuePosition = issueRows.findIndex((row) => row.index === currentIndex);
 if (currentIssuePosition === -1) return issueRows[0]?.index ?? null;
 return issueRows[currentIssuePosition + 1]?.index ?? issueRows[currentIssuePosition]?.index ?? null;
 };

 const applyResolvedIngredient = (idx, resolvedData) => {
 const newRes = [...ingredientResolutions];
 const previousRes = newRes[idx] || {};
 newRes[idx] = { ...(newRes[idx] || {}), ...resolvedData };
 setIngredientResolutions(newRes);

 const dictionaryTouched = resolvedData.dictionaryId !== undefined || resolvedData.canonicalName !== undefined;
 const dictionaryChanged =
 dictionaryTouched &&
 (String(previousRes?.dictionaryId || '') !== String(newRes[idx]?.dictionaryId || '') ||
 String(previousRes?.canonicalName || '') !== String(newRes[idx]?.canonicalName || ''));

 if (dictionaryChanged) {
 setIngredientResolveNotice({
 index: idx,
 message: 'İsim güncellendi, dictionary bağlantısı güncellendi.'
 });
 }

 setEditingDish(prev => {
 let next = { ...prev };

 let baseIngs = Array.isArray(prev.malzemeler) && prev.malzemeler.length > 0
 ? prev.malzemeler.map(m => (typeof m === 'object' && m !== null ? { ...m } : { originalText: String(m) }))
 : (Array.isArray(prev.ingredients) ? [...prev.ingredients] : []);

 if (baseIngs.length > 0) {
 const currentIng = baseIngs[idx] || {};
 const nextName = resolvedData.canonicalName !== undefined
 ? resolvedData.canonicalName
 : (currentIng.name ?? currentIng.malzeme ?? currentIng.canonicalName);
 baseIngs[idx] = {
 ...currentIng,
 name: nextName,
 malzeme: nextName,
 canonicalName: resolvedData.canonicalName !== undefined ? resolvedData.canonicalName : currentIng.canonicalName,
 familyName: resolvedData.familyName !== undefined ? resolvedData.familyName : currentIng.familyName,
 dictionaryId: resolvedData.dictionaryId !== undefined ? resolvedData.dictionaryId : currentIng.dictionaryId,
 quantity: resolvedData.quantity !== undefined ? resolvedData.quantity : currentIng.quantity,
 unit: resolvedData.unit !== undefined ? resolvedData.unit : currentIng.unit,
 amountRaw: resolvedData.quantity !== undefined ? String(resolvedData.quantity) : currentIng.amountRaw,
 unitRaw: resolvedData.unit !== undefined ? resolvedData.unit : currentIng.unitRaw,
 miktar: resolvedData.quantity !== undefined ? resolvedData.quantity : currentIng.miktar,
 birim: resolvedData.unit !== undefined ? resolvedData.unit : currentIng.birim,
 turkmutfagi: resolvedData.turkmutfagi !== undefined ? resolvedData.turkmutfagi : currentIng.turkmutfagi,
 turkmutfagiMiktar: resolvedData.turkmutfagiMiktar !== undefined ? resolvedData.turkmutfagiMiktar : (currentIng.turkmutfagiMiktar ?? currentIng.turkmutfagi_miktar),
 turkmutfagiBirim: resolvedData.turkmutfagiBirim !== undefined ? resolvedData.turkmutfagiBirim : (currentIng.turkmutfagiBirim ?? currentIng.turkmutfagi_birim),
 turkmutfagi_miktar: resolvedData.turkmutfagiMiktar !== undefined ? resolvedData.turkmutfagiMiktar : (currentIng.turkmutfagi_miktar ?? currentIng.turkmutfagiMiktar),
 turkmutfagi_birim: resolvedData.turkmutfagiBirim !== undefined ? resolvedData.turkmutfagiBirim : (currentIng.turkmutfagi_birim ?? currentIng.turkmutfagiBirim),
 originalText: resolvedData.turkmutfagi !== undefined ? resolvedData.turkmutfagi : currentIng.originalText,
 alternativeIds: resolvedData.alternativeIds !== undefined ? resolvedData.alternativeIds : (currentIng.alternativeIds || []),
 alternativeNames: resolvedData.alternativeNames !== undefined ? resolvedData.alternativeNames : (currentIng.alternativeNames || [])
 };
 next.ingredients = baseIngs;
 }

 if (Array.isArray(prev.malzemeler) && prev.malzemeler.length > 0) {
 const newMalzemeler = [...prev.malzemeler];
 const it = newMalzemeler[idx];
 const currentItem = (typeof it === 'object' && it !== null) ? it : { originalText: String(it) };
 const nextName = resolvedData.canonicalName !== undefined
 ? resolvedData.canonicalName
 : (currentItem.name ?? currentItem.malzeme ?? currentItem.canonicalName);

 newMalzemeler[idx] = {
 ...currentItem,
 name: nextName,
 malzeme: nextName,
 canonicalName: resolvedData.canonicalName !== undefined ? resolvedData.canonicalName : currentItem.canonicalName,
 familyName: resolvedData.familyName !== undefined ? resolvedData.familyName : currentItem.familyName,
 miktar: resolvedData.quantity !== undefined ? resolvedData.quantity : currentItem.miktar,
 birim: resolvedData.unit !== undefined ? resolvedData.unit : currentItem.birim,
 quantity: resolvedData.quantity !== undefined ? resolvedData.quantity : currentItem.quantity,
 unit: resolvedData.unit !== undefined ? resolvedData.unit : currentItem.unit,
 dictionaryId: resolvedData.dictionaryId !== undefined ? resolvedData.dictionaryId : currentItem.dictionaryId,
 turkmutfagi: resolvedData.turkmutfagi !== undefined ? resolvedData.turkmutfagi : currentItem.turkmutfagi,
 turkmutfagiMiktar: resolvedData.turkmutfagiMiktar !== undefined ? resolvedData.turkmutfagiMiktar : (currentItem.turkmutfagiMiktar ?? currentItem.turkmutfagi_miktar),
 turkmutfagiBirim: resolvedData.turkmutfagiBirim !== undefined ? resolvedData.turkmutfagiBirim : (currentItem.turkmutfagiBirim ?? currentItem.turkmutfagi_birim),
 turkmutfagi_miktar: resolvedData.turkmutfagiMiktar !== undefined ? resolvedData.turkmutfagiMiktar : (currentItem.turkmutfagi_miktar ?? currentItem.turkmutfagiMiktar),
 turkmutfagi_birim: resolvedData.turkmutfagiBirim !== undefined ? resolvedData.turkmutfagiBirim : (currentItem.turkmutfagi_birim ?? currentItem.turkmutfagiBirim),
 originalText: resolvedData.turkmutfagi !== undefined ? resolvedData.turkmutfagi : currentItem.originalText,
 alternativeIds: resolvedData.alternativeIds !== undefined ? resolvedData.alternativeIds : (currentItem.alternativeIds || []),
 alternativeNames: resolvedData.alternativeNames !== undefined ? resolvedData.alternativeNames : (currentItem.alternativeNames || [])
 };
 next.malzemeler = newMalzemeler;
 }

 const projectionSource = (Array.isArray(next.malzemeler) && next.malzemeler.length > 0)
 ? next.malzemeler
 : (Array.isArray(next.ingredients) ? next.ingredients : []);
 if (projectionSource.length > 0) {
 next.malzemeler = buildMalzemelerProjectionFromRows(projectionSource, newRes);
 }

 return next;
 });
 };

 const handleApplyDictionaryInspectorResult = (result) => {
 if (dictionaryInspectorSelectedRow === null || dictionaryInspectorSelectedRow === undefined) {
 setIngredientResolveNotice({ type: 'warning', text: 'Önce soldan bir tarif satırı seç.' });
 return;
 }

 const currentRowIndex = dictionaryInspectorSelectedRow;
 applyResolvedIngredient(dictionaryInspectorSelectedRow, {
 dictionaryId: result?.id || result?.dictionaryId,
 canonicalName: result?.name || result?.canonicalName || result?.label,
 familyName: result?.familyName || result?.family || '',
 found: true,
 status: 'MATCHED',
 confidence: 1
 });
 const nextIssueRow = getNextDictionaryInspectorIssueRow(currentRowIndex, dictionaryInspectorRows, currentRowIndex);
 setDictionaryInspectorSelectedRow(nextIssueRow);
 setIngredientResolveNotice({ type: 'success', text: 'Seçilen dictionary kaydı satıra uygulandı.' });
 };

const ingredientResolutionSummary = (() => {
 const total = ingredientResolutions.length;
 let matched = 0;
 let needsVerification = 0;
 let failed = 0;
 const failedRows = [];
 const verificationRows = [];

 ingredientResolutions.forEach((resolution, index) => {
 const state = getIngredientResolutionState(resolution);
 const rawItem = activeIngredientRows[index];
 const label =
 resolution?.canonicalName ||
 resolution?.resolvedName ||
 resolution?.rawInput ||
 buildIngredientResolverLine(rawItem) ||
 `Satır ${index + 1}`;

 if (state === 'matched') matched += 1;
 if (state === 'needs_verification') {
 needsVerification += 1;
 verificationRows.push({ index, label });
 }
 if (state === 'failed') {
 failed += 1;
 failedRows.push({ index, label });
 }
 });

 return {
 total,
 matched,
 needsVerification,
 failed,
 failedRows,
 verificationRows
 };
})();

 const rowValidationSummary = (() => {
 const rows = Array.isArray(activeIngredientRows) ? activeIngredientRows : [];
 let missingDictionaryId = 0;
 let missingName = 0;

 rows.forEach((row) => {
 const name = String(row?.name || row?.malzeme || row?.canonicalName || '').trim();
 const dictionaryId = String(row?.dictionaryId || '').trim();
 if (!name) missingName += 1;
 if (!dictionaryId) missingDictionaryId += 1;
 });

 return {
 totalRows: rows.length,
 missingDictionaryId,
 missingName
 };
})();

 const applyActiveFilters = (dishes) => (Array.isArray(dishes) ? dishes : [])
 .filter(d => {
 const matchesSearch = (d.name || '').toLowerCase().includes(searchTerm.toLowerCase());
 const matchesCategory = filterCategory === 'ALL' || d.category === filterCategory;
 const isMaster = d.instructions && Array.isArray(d.instructions) && d.instructions.length > 0;
 const matchesQuality = filterQuality === 'ALL' || (filterQuality === 'MASTER' && isMaster) || (filterQuality === 'SKELETON' && !isMaster);
 const dishPartyTypes = Array.isArray(d.partyTypes) ? d.partyTypes : [];
 const matchesParty = filterParty === 'ALL'
 || (filterParty === 'PARTY' && !!d.partySuitable)
 || dishPartyTypes.includes(filterParty);
 const hasVerificationIssue = Number(d.unresolvedIngredientCount || 0) > 0 || !!d.hasVerificationIssue;
 const matchesVerification = filterVerification === 'ALL' || (filterVerification === 'NEEDS_VERIFICATION' && hasVerificationIssue);
 return matchesSearch && matchesCategory && matchesQuality && matchesParty && matchesVerification;
 });

const buildExportRows = (dishes) => applyActiveFilters(dishes).map((dish) => ({
 id: normalizeExportText(dish.id ?? ''),
 name: normalizeExportText(dish.name ?? ''),
 category: normalizeExportText(dish.category ?? ''),
 serveStyle: normalizeExportText(dish.serveStyle ?? ''),
 family: normalizeExportText(dish.family ?? ''),
 quality: normalizeExportText((dish.instructions && Array.isArray(dish.instructions) && dish.instructions.length > 0) ? 'MASTER' : 'SKELETON'),
 isActive: normalizeExportText(dish.isActive ? 'true' : 'false')
 }));

 const runUtf8ScanOrThrow = (rows) => {
 const findings = [];
 rows.forEach((row, index) => {
 Object.entries(row || {}).forEach(([key, raw]) => {
 const value = String(raw ?? '');
 if (UTF8_SUSPECT_PATTERN.test(value)) {
 findings.push(`Satır ${index + 1} / ${key}: ${value.slice(0, 100)}`);
 }
 UTF8_SUSPECT_PATTERN.lastIndex = 0;
 });
 });
 if (findings.length > 0) {
 throw new Error(`UTF-8 taraması başarısız:\n${findings.slice(0, 5).join('\n')}`);
 }
 };

 const handleExportCsv = async () => {
 try {
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/list-lite`);
 const data = await readJsonOrThrow(res, 'Tarif listesi alınamadı');
 const latestDishes = Array.isArray(data?.dishes) ? data.dishes : [];
 const rows = buildExportRows(latestDishes);
 runUtf8ScanOrThrow(rows);
 const headers = Object.keys(rows[0] || { id: '', name: '', category: '', serveStyle: '', family: '', quality: '', isActive: '' });
 const lines = rows.map((row) => headers.map((h) => escapeCsvCell(row[h])).join(';'));
 const csv = `sep=;\r\n${headers.join(';')}\r\n${lines.join('\r\n')}`;
 const blob = buildWindows1254CsvBlob(csv);
 const url = URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = 'admin-recipes-manage-export.csv';
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 URL.revokeObjectURL(url);
 Swal.fire('Başarılı', `CSV dışa aktarımı tamamlandı. Satır: ${rows.length} / Toplam: ${latestDishes.length}`, 'success');
 } catch (err) {
 Swal.fire('UTF-8 Hatası', err.message || 'CSV dışa aktarımı başarısız.', 'error');
 }
 };

 const hasIngredientData = activeIngredientRows.length > 0 || ingredientResolutionSummary.total > 0;
 const isJsonIngredientMode = modalTab === 'BASIC' && ingredientsViewMode === 'JSON' && hasIngredientData;
 const hasBlockingResolution = ingredientResolutionSummary.failed > 0 || ingredientResolutionSummary.needsVerification > 0;
 const hasRowLevelBlocking =
 rowValidationSummary.missingDictionaryId > 0 ||
 rowValidationSummary.missingName > 0;
 const normalizeRecipeAdminUnit = (value) => {
 const unit = String(value || '').trim();
 if (unit === 'g') return 'gr';
 return unit;
 };
 const looksLikeDictionaryId = (value) => /^c[a-z0-9]{10,}$/i.test(String(value || '').trim());
 const producedDictionaryMatch = editingDish?.producedDictionaryId
 ? rescueDictionary.find(d => d.id === editingDish.producedDictionaryId)
 : null;
 const rescueUnitMismatch = !!(
 producedDictionaryMatch &&
 editingDish?.producedUnit &&
 normalizeRecipeAdminUnit(producedDictionaryMatch.canonicalUnit) !== normalizeRecipeAdminUnit(editingDish.producedUnit)
 );
 const rescueContractMissing = !!editingDish?.isRescue && (
 !looksLikeDictionaryId(editingDish.rescueTrigger) ||
 !(Number(editingDish.producedAmount) > 0) ||
 !editingDish.producedUnit ||
 !looksLikeDictionaryId(editingDish.producedDictionaryId) ||
 rescueUnitMismatch
 );
 const isSaveBlocked = !editingDish || isJsonIngredientMode || hasBlockingResolution || hasRowLevelBlocking || rescueContractMissing;

 const saveBlockReason = !editingDish
 ? 'Düzenlenecek tarif bulunamadı.'
 : isJsonIngredientMode
 ? 'Liste moduna dönüp malzeme doğrulamasını tamamlayın.'
 : ingredientResolutionSummary.failed > 0
 ? `${ingredientResolutionSummary.failed} satırda eşleşme başarısız.`
 : ingredientResolutionSummary.needsVerification > 0
 ? `${ingredientResolutionSummary.needsVerification} satır doğrulama bekliyor.`
 : rowValidationSummary.missingDictionaryId > 0
 ? `${rowValidationSummary.missingDictionaryId} satırda dictionary seçimi eksik.`
 : rowValidationSummary.missingName > 0
 ? `${rowValidationSummary.missingName} satırda malzeme adı eksik.`
 : rescueContractMissing
 ? (
 !looksLikeDictionaryId(editingDish.rescueTrigger)
 ? 'Kurtarma tetikleyicisi eski metin değil, dictionary seçimi olmalı.'
 : !(Number(editingDish.producedAmount) > 0)
 ? 'Kurtarma üretim miktarı 0’dan büyük olmalı.'
 : !editingDish.producedUnit
 ? 'Kurtarma üretim birimi seçilmeli.'
 : !looksLikeDictionaryId(editingDish.producedDictionaryId)
 ? 'Kurtarma stok dictionary eşleşmesi seçilmeli.'
 : 'Kurtarma üretim birimi dictionary birimiyle uyumlu olmalı.'
 )
 : '';

 const handleModalTabChange = async (tabId) => {
 if (tabId !== 'JSON') {
 setModalTab(tabId);
 return;
 }

 if (jsonAdvancedUnlocked) {
 setModalTab('JSON');
 return;
 }

 const result = await Swal.fire({
 icon: 'warning',
 title: 'Gelişmiş JSON Kilidi',
 text: 'Bu alan doğrudan ham veri düzenler. Yanlış değişiklik senkronu bozabilir.',
 confirmButtonText: 'Kilidi Aç',
 cancelButtonText: 'Vazgeç',
 showCancelButton: true
 });

 if (result.isConfirmed) {
 setJsonAdvancedUnlocked(true);
 setModalTab('JSON');
 }
 };

 const dictionaryHealthData = dictionaryHealth.data;
 const dictionaryHealthUpdatedAt = dictionaryHealthData?.generatedAt
 ? new Date(dictionaryHealthData.generatedAt).toLocaleString('tr-TR')
 : '-';
 const dishesNeedingVerificationCount = allDishes.filter(
 (dish) => Number(dish?.unresolvedIngredientCount || 0) > 0 || !!dish?.hasVerificationIssue
 ).length;




 return (
 <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
 <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />

 <main className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-[var(--admin-sidebar-width,18rem)] relative transition-[margin] duration-300">
 <AdminTopBar title="Mutfak Yönetimi" onToggleMobile={() => setMobileSidebarOpen(open => !open)} />

 <div className="flex-1 overflow-y-auto p-4 sm:p-8">
 <div className="max-w-[1200px] mx-auto space-y-8 pb-10">

 {/* Header & Tabs */}
 <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
 <div>
                                <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{manageOnly ? 'Tarifleri Yönet' : "Chef's Entry & Control"}</h1>
 <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">{manageOnly ? 'Tarifleri listele, düzenle, güncelle ve sil.' : 'Tarif girişlerini yap ve mevcutları yönet.'}</p>
 </div>

 {!manageOnly && (
 <div className="flex p-1 bg-slate-200 dark:bg-slate-800 rounded-xl self-start md:self-center">
 <button
 onClick={() => setActiveTab('INGEST')}
 className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'INGEST' ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
 >
 JSON Pipeline
 </button>
 <button
 onClick={() => setActiveTab('AI_BULK')}
 className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'AI_BULK' ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
 >
                                    Toplu AI Oluştur
 </button>
 <button
 onClick={() => setActiveTab('MANUAL')}
 className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'MANUAL' ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
 >
                                    Manuel Giriş
 </button>
 <button
 onClick={() => setActiveTab('MANAGE')}
 className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'MANAGE' ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
 >
 Tarifleri Yönet
 </button>
 <button
 onClick={() => setActiveTab('RESCUE_LAB')}
 className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'RESCUE_LAB' ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
 >
                                    Rescue Lab
 </button>
 </div>
 )}
 </div>

 <div className="bg-white dark:bg-[#101922] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-4 sm:p-5">
 <div className="flex items-start justify-between gap-4">
 <div>
 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Dictionary Uyum Skoru</p>
 <h3 className="text-xl font-black text-slate-900 dark:text-white mt-1">
 {dictionaryHealth.loading ? 'Yükleniyor...' : `${dictionaryHealthData?.score ?? '-'} / 100`}
 </h3>
 <p className={`text-xs font-bold mt-1 ${dictionaryHealthData?.status === 'ALERT' ? 'text-rose-500' : 'text-emerald-500'}`}>
 {dictionaryHealth.loading
 ? 'Nightly rapor okunuyor'
 : dictionaryHealthData?.status === 'ALERT'
 ? 'ALERT: Eşik dışı metrik var'
 : 'PASS: Eşik metrikleri sağlandı'}
 </p>
 </div>
 <div className="flex items-center gap-2">
 <button
 type="button"
 onClick={() => setFilterVerification((prev) => (prev === 'NEEDS_VERIFICATION' ? 'ALL' : 'NEEDS_VERIFICATION'))}
 disabled={filterVerification === 'ALL' && dishesNeedingVerificationCount === 0}
 className={`px-3 py-1.5 rounded-lg text-[11px] font-black border transition-all ${
 filterVerification === 'NEEDS_VERIFICATION'
 ? 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-400'
 : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
 } disabled:opacity-50 disabled:cursor-not-allowed`}
 >
 {filterVerification === 'NEEDS_VERIFICATION'
 ? 'Doğrulama Filtresi Açık'
 : `Doğrulama Gerekenleri Göster (${dishesNeedingVerificationCount})`}
 </button>
 <button
 type="button"
 onClick={fetchDictionaryHealth}
 className="px-3 py-1.5 rounded-lg text-[11px] font-black border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
 >
 Yenile
 </button>
 <button
 type="button"
 onClick={handleExportCsv}
 className="px-3 py-1.5 rounded-lg text-[11px] font-black border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center gap-1.5"
 >
 <span className="material-symbols-outlined text-xs">download</span> CSV
 </button>
 </div>
 </div>

 {dictionaryHealth.error ? (
 <p className="mt-3 text-xs font-semibold text-rose-500">{dictionaryHealth.error}</p>
 ) : (
 <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
 <div className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Eksik ID</p>
 <p className="text-sm font-black text-slate-900 dark:text-white">{dictionaryHealthData?.metrics?.missing_json_dictionary_id ?? '-'}</p>
 </div>
 <div className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">İsim-ID Uyuşmazlığı</p>
 <p className="text-sm font-black text-slate-900 dark:text-white">{dictionaryHealthData?.metrics?.name_dictionary_mismatch ?? '-'}</p>
 </div>
 <div className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Manuel İnceleme</p>
 <p className="text-sm font-black text-slate-900 dark:text-white">{dictionaryHealthData?.metrics?.manual_review_backlog ?? '-'}</p>
 </div>
 </div>
 )}

 <p className="mt-3 text-[11px] text-slate-500">
 Son rapor: <span className="font-bold text-slate-700 dark:text-slate-300">{dictionaryHealthUpdatedAt}</span>
 </p>
 </div>

{activeTab === 'INGEST' ? (
<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in duration-300">
<div className="bg-white dark:bg-[#101922] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
 <div className="p-4 rounded-xl border border-fuchsia-200/60 dark:border-fuchsia-800/50 bg-fuchsia-50/40 dark:bg-fuchsia-900/10 space-y-3">
 <label className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${pipelinePartySuitable ? 'bg-fuchsia-100/70 dark:bg-fuchsia-900/30 border-fuchsia-300 dark:border-fuchsia-700 text-fuchsia-700 dark:text-fuchsia-300' : 'bg-white/70 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}>
 <input
 type="checkbox"
 className="hidden"
 checked={pipelinePartySuitable}
 onChange={(e) => {
 const checked = e.target.checked;
 setPipelinePartySuitable(checked);
 if (!checked) {
 setPipelinePartyTypes([]);
 setPipelineSpecialDayLabels([]);
 }
 }}
 />
 <span className="material-symbols-outlined text-lg">celebration</span>
 <div>
 <p className="text-xs font-black uppercase tracking-wider">Partiye Uygun</p>
 <p className="text-[10px] opacity-80">Açılırsa parti türü ve tatlı/tuzlu/yemekli/meze etiketleri kayda işlenir.</p>
 </div>
 </label>

 {pipelinePartySuitable && (
 <div className="space-y-3">
 <div>
 <p className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 dark:text-fuchsia-300 mb-2">Uygun Parti Türleri</p>
 <div className="flex flex-wrap gap-2">
 {PARTY_TYPE_OPTIONS.map((opt) => {
 const active = pipelinePartyTypes.includes(opt.value);
 return (
 <button
 key={opt.value}
 type="button"
 onClick={() => {
 setPipelinePartyTypes((prev) =>
 prev.includes(opt.value) ? prev.filter((v) => v !== opt.value) : [...prev, opt.value]
 );
 }}
 className={`px-3 py-1.5 rounded-lg text-[11px] font-black border transition-all ${active ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'}`}
 >
 {opt.label}
 </button>
 );
 })}
 </div>
 </div>

 <div>
 <p className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 dark:text-fuchsia-300 mb-2">Seçili Özel Gün Etiketleri</p>
 <div className="flex flex-wrap gap-2">
 {Object.entries(SPECIAL_DAY_LABELS).map(([key, label]) => {
 const active = pipelineSpecialDayLabels.includes(key);
 return (
 <button
 key={key}
 type="button"
 onClick={() => {
 setPipelineSpecialDayLabels((prev) =>
 prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key]
 );
 }}
 className={`px-3 py-1.5 rounded-lg text-[11px] font-black border transition-all ${active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'}`}
 >
 {label}
 </button>
 );
 })}
 </div>
 </div>
 </div>
 )}
 </div>

<div>
<h2 className="text-lg font-black text-slate-900 dark:text-white">Katmanlı Tarif Girişi</h2>
<p className="text-xs text-slate-500 mt-1">Tarifi verdiğiniz katmanlı formatta yapıştırın. Sistem otomatik çözer, hata varsa manuel kontrole yönlendirir, sağdan görsel isteyip kaydeder.</p>
</div>
 <textarea
 value={pipelineJsonInput}
 onChange={(e) => setPipelineJsonInput(e.target.value)}
 placeholder="Katmanlı tarif metnini veya JSON tarifini buraya yapıştır..."
 className="w-full h-96 px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-mono focus:ring-2 focus:ring-primary/50"
 />
 {pipelineStatus.message && (
 <div className={`p-3 rounded-xl text-sm font-bold ${pipelineStatus.type === 'success' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : pipelineStatus.type === 'error' ? 'bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-400' : 'bg-blue-50 text-blue-700 dark:bg-blue-900/10 dark:text-blue-400'}`}>
 {pipelineStatus.message}
 </div>
 )}
 <button
 onClick={handlePipelineAnalyze}
 disabled={pipelineStatus.loading}
 className="w-full py-3 bg-slate-900 dark:bg-slate-700 text-white rounded-xl font-bold hover:bg-black transition flex items-center justify-center gap-2 disabled:opacity-60"
 >
 <span className={`material-symbols-outlined ${pipelineStatus.loading ? 'animate-spin' : ''}`}>{pipelineStatus.loading ? 'sync' : 'data_object'}</span>
 TARİFİ ANALİZ ET
 </button>
 </div>

 <div className="bg-white dark:bg-[#101922] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col gap-4">
 <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
 <span className="material-symbols-outlined text-primary">upload_file</span>
 Görsel ve Kayıt
 </h2>

 {!pipelineDraft ? (
 <div className="flex-1 flex flex-col items-center justify-center text-slate-400 italic min-h-[280px]">
 <span className="material-symbols-outlined text-5xl mb-2 opacity-25">image_search</span>
 Önce JSON analizi yapın.
 </div>
 ) : (
 <>
 {pipelineQueue.length > 1 && (
 <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-2 max-h-44 overflow-auto">
 <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
 Kuyruk ({pipelineQueue.length})
 </p>
 {pipelineQueue.map((item, idx) => {
 const active = idx === pipelineActiveIndex;
 return (
 <button
 key={`${item.name}-${idx}`}
 type="button"
 onClick={() => handleSelectPipelineDraft(idx)}
 className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${active ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}
 >
 <div className="flex items-center justify-between gap-2">
 <span className="text-xs font-black truncate">{item.name || `Tarif #${idx + 1}`}</span>
 <div className="flex items-center gap-1 shrink-0">
 {(item.unresolvedCount || 0) > 0 && (
 <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 font-black">
 {item.unresolvedCount} eşleşmedi
 </span>
 )}
 {item.imageUrl && (
 <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 font-black">
 görsel
 </span>
 )}
 </div>
 </div>
 </button>
 );
 })}
 </div>
 )}

 <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
 <p className="text-sm font-black text-slate-800 dark:text-slate-200">{pipelineDraft.name}</p>
 <p className="text-[11px] text-slate-500 mt-1 uppercase tracking-wide">
 {CATEGORY_LABELS[pipelineDraft.category] || pipelineDraft.category} • {pipelineDraft.serveStyle} • {pipelineDraft.family}
 </p>
 <p className="text-[11px] text-slate-500 mt-1">Malzeme: {pipelineDraft.malzemeler?.length || 0} • Adım: {pipelineDraft.instructions?.length || 0}</p>
 </div>

 <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Ürün Görseli</label>
 <input
 type="file"
 accept="image/*"
 onChange={handlePipelineImageUpload}
 className="w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-slate-100 dark:file:bg-slate-800 file:font-bold"
 />

 {pipelineDraft.imageUrl && (
 <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
 <img src={resolveImageUrl(pipelineDraft.imageUrl)} alt="Tarif görseli" className="w-full h-44 object-cover" />
 </div>
 )}

 <button
 onClick={handlePipelineSave}
 disabled={pipelineStatus.loading || pipelineImageUploading || !pipelineDraft.imageUrl}
 className="w-full py-4 bg-primary text-white rounded-xl font-black shadow-lg shadow-primary/30 disabled:opacity-50 hover:bg-primary-dark transition-all flex items-center justify-center gap-2"
 >
 <span className="material-symbols-outlined">{pipelineStatus.loading ? 'sync' : 'rocket_launch'}</span>
 FOTOĞRAF İLE KAYDET
 </button>
 </>
 )}
 </div>
 </div>
 ) : activeTab === 'AI_BULK' ? (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-right-4 duration-300">
 {/* AI Left Side */}
 <div className="space-y-6">
 <div className="bg-white dark:bg-[#101922] p-8 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden group">
 <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
 <span className="material-symbols-outlined text-[100px]">psychology</span>
 </div>
 <h2 className="text-xl font-black text-slate-900 dark:text-white mb-2 flex items-center gap-3">
 <span className="material-symbols-outlined text-primary bg-primary/10 p-2 rounded-xl">auto_awesome</span>
 Yemek Listesi Girin
 </h2>
 <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mb-6">Her satıra bir yemek adı gelecek şekilde listenizi oluşturun.</p>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
 <div>
 <label className="block text-xs font-black text-slate-400 uppercase mb-2 ml-1 tracking-widest">Hedef Kategori (Stil)</label>
 <select
 value={aiCategory}
 onChange={(e) => setAiCategory(e.target.value)}
 className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-black focus:ring-4 focus:ring-primary/20 outline-none transition-all"
 >
 {CATEGORY_KEYS.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-xs font-black text-slate-400 uppercase mb-2 ml-1 tracking-widest">Hedef Protein (İçerik)</label>
 <select
 value={aiMeatType}
 onChange={(e) => setAiMeatType(e.target.value)}
 className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-black focus:ring-4 focus:ring-primary/20 outline-none transition-all"
 >
 {MEAT_TYPE_KEYS.map(m => <option key={m} value={m}>{MEAT_TYPE_LABELS[m]}</option>)}
 </select>
 </div>
 </div>

 <textarea
 value={aiNamesInput}
 onChange={(e) => setAiNamesInput(e.target.value)}
 placeholder="Örn:&#10;Kuzu Etli Keşkek&#10;Tavuklu Pilav&#10;Nohut Yemeği..."
 className="w-full h-80 px-5 py-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl text-sm font-bold focus:ring-4 focus:ring-primary/20 outline-none transition-all placeholder:italic"
 />
 <div className="mt-6">
 <button
 onClick={handleGenerateAI}
 disabled={aiStatus.loading || !aiNamesInput.trim()}
 className="w-full py-5 bg-gradient-to-r from-primary to-primary-dark text-white rounded-[24px] font-black shadow-2xl shadow-primary/40 flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
 >
 {aiStatus.loading ? (
 <span className="material-symbols-outlined animate-spin">sync</span>
 ) : (
 <span className="material-symbols-outlined">magic_button</span>
 )}
                                                {aiStatus.loading ? 'ANALİZ EDİLİYOR...' : 'YAPAY ZEKA İLE OLUŞTUR'}
 </button>
 </div>
 {aiStatus.message && (
 <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-2xl text-[11px] font-black text-primary text-center uppercase tracking-widest animate-pulse">
 {aiStatus.message}
 </div>
 )}
 </div>
 <div className="bg-amber-50 dark:bg-amber-900/10 p-6 rounded-3xl border border-amber-100 dark:border-amber-900/40">
 <p className="text-[11px] font-black text-amber-700 dark:text-amber-400 flex items-center gap-2 uppercase tracking-widest mb-1">
 <span className="material-symbols-outlined text-[16px]">verified_user</span> Master Kalite Güvencesi
 </p>
 <p className="text-xs text-amber-900/70 dark:text-amber-200/50 font-medium italic">Oluşturulan tarifler fazlı yapıda (`Hazırlık`, `Pişirme`), bebek dostu ({"{bebek_ismi}"}) ve gerçekçi besin değerlerine sahip olacaktır.</p>
 </div>
 </div>

 {/* AI Right Side */}
 <div className="space-y-6">
 <div className="bg-white dark:bg-[#101922] p-8 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col min-h-[600px] max-h-[850px]">
 <div className="flex items-center justify-between mb-8">
 <div>
 <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-3">
 <span className="material-symbols-outlined text-emerald-500">check_circle</span>
 AI Master Önerileri ({generatedRecipes.length})
 </h2>
 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Halisünasyon Kontrolü & Kalite Onayı</p>
 </div>
 {generatedRecipes.length > 0 && (
 <button onClick={() => handleDownloadJson(generatedRecipes)} className="p-2 text-primary hover:bg-primary/10 rounded-xl transition-all" title="Toplu JSON İndir">
 <span className="material-symbols-outlined text-[24px]">download</span>
 </button>
 )}
 </div>

 <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar">
 {generatedRecipes.map((r, i) => (
 <div key={i} className={`p-5 bg-slate-50 dark:bg-slate-900/40 rounded-3xl border border-dashed hover:border-primary/50 transition-all group relative ${r.isIncomplete ? 'border-amber-400 bg-amber-50/10' : 'border-slate-200 dark:border-slate-800'}`}>
 <div className="flex justify-between items-start mb-3">
 <div className="flex gap-4">
 {(r.imageUrl || r.image_url) && (
 <div className="size-16 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shrink-0 shadow-sm">
 <img src={resolveImageUrl(r.imageUrl || r.image_url)} className="w-full h-full object-cover" alt="Thumb" />
 </div>
 )}
 <div>
 <div className="flex items-center gap-2">
 <h3 className="font-black text-slate-900 dark:text-white text-base">{r.name}</h3>
 {r.isIncomplete && (
 <span
 className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-500 text-[9px] font-black rounded-full border border-amber-500/30 cursor-help"
                                                                            title={`Şema Hatası: ${r.validationErrors}`}
 >
 <span className="material-symbols-outlined text-[12px]">warning</span> EKSİK VERİ
 </span>
 )}
 </div>
 <div className="flex gap-1.5 mt-1">
 <span className="text-[9px] font-black bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-500 dark:text-slate-400 uppercase">{CATEGORY_LABELS[r.category] || r.category}</span>
 {r.meatType && (
 <span className="text-[9px] font-black bg-primary/10 px-1.5 py-0.5 rounded text-primary uppercase flex items-center gap-1">
 <span className="material-symbols-outlined text-[10px]">restaurant</span> {r.meatType}
 </span>
 )}
 </div>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <button
 onClick={() => setEditingDish({ ...r, originalName: r.name, isTemporary: true })}
 className={`px-3 py-2 text-[10px] font-black rounded-xl transition-all uppercase tracking-widest ${r.isIncomplete ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-300'}`}
 >
                                                                {r.isIncomplete ? 'VERİYİ TAMAMLA' : 'GÖZAT'}
 </button>
 {!r.isIncomplete && (
 <button
 onClick={() => handleSaveAiRecipe(r)}
 className="px-4 py-2 bg-emerald-500 text-white text-[10px] font-black rounded-xl shadow-lg shadow-emerald-500/30 hover:bg-emerald-600 transition-all active:scale-95 uppercase tracking-widest"
 >
 ONAYLA
 </button>
 )}
 </div>
 </div>
 <div className="space-y-2">
 <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500">
 <span className="material-symbols-outlined text-[14px]">local_fire_department</span>
 {r.nutritionalValues?.calories} | {r.instructions?.length} Adım
 </div>
 <div className="flex flex-wrap gap-1">
 {r.malzemeler?.slice(0, 3).map((m, mi) => (
 <span key={mi} className={`px-2 py-0.5 text-[9px] rounded-lg ${r.isIncomplete ? 'bg-amber-500/10 text-amber-600' : 'bg-white dark:bg-black/20 text-slate-400'}`}>{m}</span>
 ))}
 {r.malzemeler?.length > 3 && <span className="text-[9px] text-slate-300">+{r.malzemeler.length - 3}</span>}
 </div>
 </div>
 </div>
 ))}
 {generatedRecipes.length === 0 && !aiStatus.loading && (
 <div className="h-full flex flex-col items-center justify-center text-slate-300 italic py-24 text-center">
 <div className="size-24 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mb-6 border border-slate-100 dark:border-slate-800 shadow-inner">
 <span className="material-symbols-outlined text-5xl opacity-20">hourglass_empty</span>
 </div>
 <p className="text-sm font-bold opacity-30">Henüz oluşturulmuş tarif yok.</p>
 <p className="max-w-[200px] text-[10px] uppercase tracking-widest mt-2">Soldaki listeden başla şefim!</p>
 </div>
 )}
 </div>
 </div>

 {/* Failed Recipes Card */}
 {failedRecipes.length > 0 && (
 <div className="bg-white dark:bg-[#101922] p-8 rounded-[32px] border border-red-200 dark:border-red-900/30 shadow-xl border-t-4 border-t-red-500 mt-6 relative overflow-hidden group">
 <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
 <span className="material-symbols-outlined text-[60px] text-red-500">warning</span>
 </div>
 <div className="flex items-center justify-between mb-6 relative z-10">
 <div>
 <h2 className="text-lg font-black text-red-600 dark:text-red-400 flex items-center gap-3">
 <span className="material-symbols-outlined bg-red-100 dark:bg-red-900/40 p-1.5 rounded-lg">report_problem</span>
 Hatalı Master Önerileri ({failedRecipes.length})
 </h2>
 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Bu tarifler üretilirken teknik sorun oluştu</p>
 </div>
 <button
 onClick={() => setFailedRecipes([])}
 className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-[10px] font-black text-slate-500 hover:text-red-500 rounded-lg uppercase tracking-widest transition-all"
 >
 TEMİZLE
 </button>
 </div>

 <div className="space-y-3 relative z-10">
 {failedRecipes.map((f, i) => (
 <div key={i} className="p-4 bg-red-50/50 dark:bg-red-900/10 rounded-2xl border border-red-100 dark:border-red-900/20 flex items-start gap-4 hover:shadow-sm transition-all">
 <div className="size-8 flex items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/40 text-red-600 font-black text-xs shrink-0">
 {i + 1}
 </div>
 <div className="flex-1 min-w-0">
 <p className="font-black text-slate-900 dark:text-white text-sm truncate">{f.name}</p>
 <p className="text-[11px] text-red-500/80 font-bold mt-1 line-clamp-2 italic">❌{f.error}❌</p>
 </div>
 <button
 onClick={() => {
 setAiNamesInput(f.name + (aiNamesInput ? '\n' + aiNamesInput : ''));
 setFailedRecipes(prev => prev.filter((_, idx) => idx !== i));
 }}
 className="px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-black rounded-lg hover:shadow-md hover:bg-red-500 hover:text-white transition-all uppercase whitespace-nowrap"
 >
 LİSTEYE EKLE ➕
 </button>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 </div>
 ) : activeTab === 'MANUAL' ? (
 <div className="bg-white dark:bg-[#101922] p-8 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-xl animate-in fade-in zoom-in duration-300">
 <div className="max-w-4xl mx-auto space-y-10">
 <div className="flex items-center gap-4 mb-2">
 <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
 <span className="material-symbols-outlined text-primary text-2xl">edit_note</span>
 </div>
 <div>
 <h2 className="text-xl font-black text-slate-900 dark:text-white">Manuel Tarif Girişi</h2>
 <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Temel bilgileri girin, gerisini Yapay Zeka halletsin.</p>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6 border-b border-slate-100 dark:border-slate-800/50">
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Yemek Adı</label>
 <input
 type="text"
 placeholder="Örn: Fırında Soslu Tavuk"
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none transition-all"
 value={manualRecipe.name}
 onChange={(e) => setManualRecipe({ ...manualRecipe, name: e.target.value })}
 />
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Görsel (Yükle veya URL)</label>
 <div className="flex gap-2">
 <input
 type="text"
 placeholder="https://..."
 className="flex-1 px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none transition-all"
 value={manualRecipe.imageUrl}
 onChange={(e) => setManualRecipe({ ...manualRecipe, imageUrl: e.target.value })}
 />
 <label className={`w-14 flex items-center justify-center bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl cursor-pointer hover:bg-slate-200 transition-all ${imageUploading ? 'opacity-50 pointer-events-none' : ''}`}>
 <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={imageUploading} />
 <span className={`material-symbols-outlined text-[20px] ${imageUploading ? 'animate-spin' : ''}`}>
 {imageUploading ? 'sync' : 'upload'}
 </span>
 </label>
 </div>
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Kategori</label>
 <select
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none transition-all"
 value={manualRecipe.category}
 onChange={(e) => {
 const newCat = e.target.value;
 let autoStyle = manualRecipe.serveStyle;
 if (['SALAD', 'VEGAN_DIET'].includes(newCat)) autoStyle = 'meze';
 else if (['DESSERT'].includes(newCat)) autoStyle = 'tatli';
 else if (['BREAKFAST'].includes(newCat)) autoStyle = 'kahvaltı';
 else if (['PASTRY'].includes(newCat)) autoStyle = 'yan_yemek';
 else if (['SOUP'].includes(newCat)) autoStyle = 'corba';
 else if (['MAIN', 'LEGUME', 'CHICKEN', 'MEAT', 'SEAFOOD', 'PASTA'].includes(newCat)) autoStyle = 'ana_yemek';
 setManualRecipe({ ...manualRecipe, category: newCat, serveStyle: autoStyle });
 }}
 >
 {CATEGORY_KEYS.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
 </select>
 </div>
 </div>

 {/* ROW 2: Culinary Settings */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6 border-b border-slate-100 dark:border-slate-800/50">
 <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Sunum Şekli (Rol)</label>
 <select
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none"
 value={manualRecipe.serveStyle}
 onChange={(e) => setManualRecipe({ ...manualRecipe, serveStyle: e.target.value })}
 >
                                                <option value="ana_yemek">Ana Yemek</option>
                                                <option value="kahvalti">Kahvaltı</option>
                                                <option value="corba">Çorba</option>
                                                <option value="salata">Salata</option>
                                                <option value="meze">Meze</option>
                                                <option value="yan_yemek">Yan Yemek / Eşlikçi</option>
                                                <option value="hamur_isi">Hamur İşi / Börek</option>
                                                <option value="tatli">Tatlı</option>
                                                <option value="atıştırmalık">Atıştırmalık</option>
                                                <option value="harc">Sos / Harç / Bağlayıcı</option>
                                                <option value="ara_urun">Ara Ürün / Temel Ürün</option>
 </select>
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Et / Protein Tipi</label>
 <select
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none"
 value={manualRecipe.meatType}
 onChange={(e) => setManualRecipe({ ...manualRecipe, meatType: e.target.value })}
 >
 {MEAT_TYPE_KEYS.map(k => <option key={k} value={k}>{MEAT_TYPE_LABELS[k]}</option>)}
 </select>
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Zorluk Seviyesi</label>
 <select
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none"
 value={manualRecipe.difficulty}
 onChange={(e) => setManualRecipe({ ...manualRecipe, difficulty: e.target.value })}
 >
 <option value="Kolay">Kolay</option>
 <option value="Orta">Orta</option>
 <option value="Zor">Zor</option>
 </select>
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Zorluk / Süre</label>
 <select
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none"
 value={manualRecipe.prepTimeEstimate}
 onChange={(e) => setManualRecipe({ ...manualRecipe, prepTimeEstimate: e.target.value })}
 >
 <option value="SHORT">Kısa / Kolay</option>
 <option value="MEDIUM">Orta</option>
 <option value="LONG">Uzun / Zor</option>
 </select>
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-rose-500 uppercase tracking-widest px-1">Porsiyon (Standard: 1 Kişilik)</label>
 <input
 type="number"
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none transition-all"
 value={manualRecipe.servings || 1}
 onChange={(e) => setManualRecipe({ ...manualRecipe, servings: parseInt(e.target.value) })}
 />
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Yemek Ailesi (7 Gün Kilidi)</label>
 <select
 className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none"
 value={manualRecipe.family}
 onChange={(e) => setManualRecipe({ ...manualRecipe, family: e.target.value })}
 >
 {FAMILY_OPTIONS.map((fam) => (
 <option key={fam} value={fam}>{fam}</option>
 ))}
 </select>
 </div>
 </div>

 {/* ROW 3: Nutrition (JSON-only now) */}
<div className="bg-slate-50/50 dark:bg-slate-950/50 p-6 rounded-[24px] border border-slate-100 dark:border-slate-800 space-y-4">
<div className="flex items-center gap-2 mb-2">
<span className="material-symbols-outlined text-amber-500 text-lg">bolt</span>
<label className="text-xs font-black text-slate-500 uppercase tracking-widest">Besin Değerleri (JSON Opsiyonel)</label>
</div>
<textarea
rows="3"
placeholder='{"calories": 500, "protein": 30, "carbs": 40, "fat": 15} veya {"nutritionalValues": {...}}'
className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-mono focus:ring-2 focus:ring-primary/30 outline-none"
value={manualRecipe.nutritionJSON}
onChange={(e) => setManualRecipe({ ...manualRecipe, nutritionJSON: e.target.value })}
/>
</div>

<div className="bg-slate-50/50 dark:bg-slate-950/50 p-6 rounded-[24px] border border-slate-100 dark:border-slate-800 space-y-4">
<div className="flex items-center gap-2 mb-2">
<span className="material-symbols-outlined text-cyan-500 text-lg">monitoring</span>
<label className="text-xs font-black text-slate-500 uppercase tracking-widest">Sağlık Etkisi (JSON Opsiyonel)</label>
</div>
<textarea
rows="3"
placeholder='{"healthSignals":{"fatLossSupport":0.6},"advancedHealthEffects":{"ironSupport":0.8}}'
className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-mono focus:ring-2 focus:ring-primary/30 outline-none"
value={manualRecipe.healthSignalsJSON || ''}
onChange={(e) => setManualRecipe({ ...manualRecipe, healthSignalsJSON: e.target.value })}
/>
</div>

{/* ROW 3.5: Dominant Ingredients */}
 <div className="bg-slate-50/50 dark:bg-slate-950/50 p-6 rounded-[24px] border border-slate-100 dark:border-slate-800 space-y-4">
 <div className="flex items-center gap-2 mb-2">
 <span className="material-symbols-outlined text-indigo-500 text-lg">analytics</span>
 <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Dominant Malzemeler (JSON Array)</label>
 </div>
 <textarea
 rows="2"
 placeholder='["Dana Kıyma", "Un", "Domates"]'
 className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-mono focus:ring-2 focus:ring-primary/30 outline-none"
 value={manualRecipe.dominantJSON}
 onChange={(e) => {
 const val = e.target.value;
 let parsed = [];
 try {
 const p = JSON.parse(val);
 if (Array.isArray(p)) parsed = p;
 } catch (e) { }
 setManualRecipe({ ...manualRecipe, dominantJSON: val, dominants: parsed });
 }}
 />
 </div>

 {/* ROW 4: Flags & Features */}
 <div className="flex flex-wrap items-center justify-between gap-6 p-2">
 <div className="flex flex-wrap gap-4">
 {[
                                                { label: 'VEGAN', key: 'isVegan' },
                                                { label: 'VEJETARYEN', key: 'isVegetarian' },
                                                { label: 'BEBEK DOSTU', key: 'isChildFriendly' },
                                                { label: 'BESLENME', key: 'isLunchbox' }
 ].map(flag => (
 <label key={flag.key} className={`flex items-center gap-2 cursor-pointer px-4 py-2.5 rounded-2xl border transition-all ${manualRecipe[flag.key] ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-400' : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 opacity-60'}`}>
 <input
 type="checkbox"
 className="hidden"
 checked={manualRecipe[flag.key]}
 onChange={(e) => setManualRecipe({ ...manualRecipe, [flag.key]: e.target.checked })}
 />
 <span className="text-[10px] font-black tracking-tight">{flag.label}</span>
 </label>
 ))}
 </div>

 <div className="flex gap-4">
 <button
 onClick={() => setManualRecipe({ ...manualRecipe, requiresOven: !manualRecipe.requiresOven })}
 className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl border transition-all ${manualRecipe.requiresOven ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400' : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 opacity-60'}`}
 >
 <span className="material-symbols-outlined text-lg">oven_gen</span>
 <span className="text-[10px] font-black uppercase tracking-tight">Fırın Gerekir</span>
 </button>

 <button
 onClick={() => setManualRecipe({ ...manualRecipe, isRescue: !manualRecipe.isRescue })}
 className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl border transition-all ${manualRecipe.isRescue ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 font-black shadow-sm ring-1 ring-emerald-500/10' : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 opacity-40 hover:opacity-100'}`}
 >
 <span className="material-symbols-outlined text-lg">bolt</span>
 <span className="text-[10px] font-black uppercase tracking-tight">Kurtarma</span>
 </button>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
 <div className="space-y-2">
 <div className="flex items-center justify-between px-1">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Malzemeler (Liste veya JSON)</label>
 <span className="text-[9px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-full">SMART PARSE</span>
 </div>
 <textarea
 rows="6"
 placeholder='["500gr Tavuk", "1 soğan"] veya alt alta liste...'
 className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none min-h-[160px]"
 value={manualRecipe.ingredients}
 onChange={(e) => {
 const val = e.target.value;
 const detected = detectMeatType(val);
 setManualRecipe({
 ...manualRecipe,
 ingredients: val,
 meatType: detected !== 'Diğer' ? detected : manualRecipe.meatType
 });
 }}
 />
 </div>
 <div className="space-y-2">
 <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Hazırlık Adımları (Opsiyonel)</label>
 <textarea
 rows="6"
 placeholder="Tavukları doğrayın, sosla karıştırıp fırına verin..."
 className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl text-sm font-bold focus:ring-2 focus:ring-primary/50 outline-none min-h-[160px]"
 value={manualRecipe.instructions}
 onChange={(e) => setManualRecipe({ ...manualRecipe, instructions: e.target.value })}
 />
 </div>
 </div>


 <div className="flex flex-col sm:flex-row gap-4 mt-6">
 <button
 onClick={handleProcessManualEntry}
 disabled={manualStatus.loading}
 className="flex-1 py-5 bg-gradient-to-r from-primary to-indigo-600 text-white rounded-[24px] font-black text-lg shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
 >
 {manualStatus.loading ? (
 <>
 <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
 {manualStatus.message}
 </>
 ) : (
 <>
 <span className="material-symbols-outlined">auto_awesome</span>
                                                    TARİFİ OLUŞTUR VE KONTROL ET
 </>
 )}
 </button>
 </div>
 </div>
 </div>
 ) : activeTab === 'RESCUE_LAB' ? (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-bottom-4 duration-300">
 {/* Rescue Left Side - Scanner */}
 <div className="space-y-6">
 <div className="bg-white dark:bg-[#101922] p-8 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden">
 <div className="absolute top-0 right-0 p-4 opacity-10">
 <span className="material-symbols-outlined text-[100px]">recycling</span>
 </div>
 <h2 className="text-xl font-black text-slate-900 dark:text-white mb-2 flex items-center gap-3">
 <span className="material-symbols-outlined text-emerald-500 bg-emerald-500/10 p-2 rounded-xl">compost</span>
 Rescue Lab Scanner
 </h2>
 <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mb-6">Mevcut "Ana Yemek"leri tarar ve artanlarından yapılabilecek tarifleri önerir.</p>

 <button
 onClick={async () => {
 setRescueStatus({ loading: true, message: 'Ana yemekler taranıyor...' });
 try {
 // 1. Fetch Main Dishes
 const resList = await fetch(`${API_BASE_URL}/api/admin-recipes/list-lite`);
 const dataList = await resList.json();
 if (!dataList.ok) throw new Error('Yemek listesi alınamadı');

 const sources = dataList.dishes
 .filter(d => d.category === 'MAIN' || d.serveStyle === 'ana_yemek')
 .map(d => d.name)
 .slice(0, 20); // Limit user input to top 20 for now to stay within context limits

 if (sources.length === 0) throw new Error('Taranacak Ana Yemek bulunamadı.');

 setRescueStatus({ loading: true, message: `${sources.length} yemek için fikirler üretiliyor...` });

 // 2. Analyze
 const resAnalyze = await fetch(`${API_BASE_URL}/api/recipes/analyze-rescue`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ sources })
 });
 const dataAnalyze = await resAnalyze.json();

 if (dataAnalyze.success) {
 setRescueIdeas(dataAnalyze.ideas);
 setRescueStatus({ loading: false, message: '' });
 } else {
                                                        throw new Error(dataAnalyze.error || 'Analiz başarısız');
 }

 } catch (err) {
 setRescueStatus({ loading: false, message: '', error: err.message });
 alert('Hata: ' + err.message);
 }
 }}
 disabled={rescueStatus.loading}
 className="w-full py-5 bg-gradient-to-r from-emerald-500 to-emerald-700 text-white rounded-[24px] font-black shadow-xl shadow-emerald-500/30 flex items-center justify-center gap-3 hover:scale-[1.02] transition-all disabled:opacity-50"
 >
 {rescueStatus.loading ? (
 <span className="material-symbols-outlined animate-spin">sync</span>
 ) : (
 <span className="material-symbols-outlined">network_intelligence</span>
 )}
                                            {rescueStatus.loading ? rescueStatus.message : 'KAYNAKLARI TARA & FİKİR ÜRET'}
 </button>
 </div>
 </div>

 {/* Rescue Right Side - Ideas */}
 <div className="space-y-6">
 <div className="bg-white dark:bg-[#101922] p-8 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col min-h-[600px]">
 <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-3 mb-6">
 <span className="material-symbols-outlined text-emerald-500">lightbulb</span>
 Kurtarıcı Fikirler ({rescueIdeas.length})
 </h2>

 <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar">
 {rescueIdeas.map((idea, i) => (
 <div key={i} className="p-5 bg-slate-50 dark:bg-slate-900/40 rounded-3xl border border-slate-200 dark:border-slate-800 transition-all hover:border-emerald-500/50 group">
 <div className="flex justify-between items-start mb-2">
 <div>
 <div className="flex items-center gap-2 mb-1">
 <span className="text-[10px] font-black bg-slate-200 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded uppercase">{idea.source}</span>
 <span className="material-symbols-outlined text-xs text-slate-300">arrow_forward</span>
 </div>
 <h3 className="font-black text-slate-900 dark:text-white text-base">{idea.idea}</h3>
 </div>
 <button
 onClick={async () => {
                                                                setRescueStatus({ loading: true, message: `"${idea.idea}" taslağı hazırlanıyor...` });
 try {
 const res = await fetch(`${API_BASE_URL}/api/recipes/generate-rescue-draft`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ idea: idea.idea, source: idea.source })
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.success) {
 setEditingDish({ ...data.recipe, originalName: data.recipe.name, isTemporary: true, safeForAllergies: data.recipe.safeForAllergies || [...ALLERGEN_OPTIONS] });
 setRescueStatus({ loading: false, message: '' });
 } else {
 throw new Error(data.message);
 }
 } catch (err) {
 setRescueStatus({ loading: false, message: '' });
 alert('Hata: ' + err.message);
 }
 }}
 disabled={rescueStatus.loading}
 className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[10px] font-black rounded-xl hover:scale-105 transition-all shadow-lg uppercase tracking-widest disabled:opacity-50"
 >
                                                            TASLAK OLUŞTUR
 </button>
 </div>
 <p className="text-xs text-slate-500 dark:text-slate-400 italic">{idea.description}</p>
 </div>
 ))}
 {rescueIdeas.length === 0 && (
 <div className="h-full flex flex-col items-center justify-center text-slate-300 italic py-24 text-center">
 <span className="material-symbols-outlined text-5xl opacity-20 mb-4">science</span>
                                                    <p>Henüz fikir yok. Tarama başlat!</p>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 ) : (
 <div className="space-y-6 animate-in fade-in duration-300">
 {/* Manage Tab Content */}
 <div className="bg-white dark:bg-[#101922] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
 <div className="flex flex-col md:flex-row gap-4 mb-6">
 {selectedDishIds.length > 0 && (
 <button
 onClick={handleBulkDelete}
 className="px-4 py-2 bg-red-500 text-white text-xs font-black rounded-xl shadow-lg shadow-red-500/30 hover:bg-red-600 transition-all flex items-center gap-2 animate-in slide-in-from-left-2 shrink-0"
 >
 <span className="material-symbols-outlined text-sm">delete_sweep</span>
 {selectedDishIds.length} TARİFİ SİL
 </button>
 )}
 <div className="flex-1 relative">
 <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">search</span>
 <input
 type="text"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 placeholder="Tarif adı ara..."
 className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-primary/50"
 />
 </div>
 <div className="w-full md:w-64">
 <select
 value={filterCategory}
 onChange={(e) => setFilterCategory(e.target.value)}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary/50"
 >
 <option value="ALL">Tüm Kategoriler</option>
 {CATEGORY_KEYS.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
 </select>
 </div>
 <div className="w-full md:w-48">
 <select
 value={filterQuality}
 onChange={(e) => setFilterQuality(e.target.value)}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary/50"
 >
 <option value="ALL">Tüm Kaliteler</option>
 <option value="MASTER">Sadece MASTER</option>
 <option value="SKELETON">Sadece İSKELET</option>
 </select>
 </div>
 <div className="w-full md:w-52">
 <select
 value={filterParty}
 onChange={(e) => setFilterParty(e.target.value)}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary/50"
 >
 <option value="ALL">Tüm Tarifler</option>
 <option value="PARTY">Sadece Özel Gün</option>
 <option value="birthday">Doğum Günü</option>
 <option value="gold_day">Altın Günü</option>
 </select>
 </div>
 <button onClick={fetchDishes} className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl font-bold hover:bg-slate-200 transition flex items-center gap-2">
 <span className="material-symbols-outlined text-sm">refresh</span> Yenile
 </button>
 <button
 onClick={() => {
 setEditingDish({
 id: null,
 name: '',
 category: 'MAIN',
 serveStyle: 'ana_yemek',
 malzemeler: [],
 tags: [],
 isVegan: false,
 isChildFriendly: true,
 meatType: 'Diğer',
 requiresOven: false,
 partySuitable: false,
 partyTypes: [],
 specialDayManualLabels: [],
 safeForAllergies: [...ALLERGEN_OPTIONS]
 });
 setModalTab('BASIC');
 }}
 className="px-6 py-3 bg-gradient-to-r from-primary to-indigo-600 text-white rounded-xl font-bold hover:scale-105 transition flex items-center gap-2 shadow-lg"
 >
 <span className="material-symbols-outlined text-sm">add</span> Yeni Tarif
 </button>
 </div>

 <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800">
 <table className="w-full text-left border-collapse">
 <thead className="bg-slate-50 dark:bg-slate-900/50">
 <tr>
 <th className="px-6 py-4 w-10">
 <input
 type="checkbox"
 checked={filteredDishes.length > 0 && filteredDishes.every(d => selectedDishIds.includes(d.id))}
 onChange={() => handleSelectAllFiltered(filteredDishes)}
 className="size-4 accent-primary rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 cursor-pointer"
 />
 </th>
 <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-left">Görsel</th>
 <th
 onClick={() => handleSort('name')}
 className="px-6 py-4 text-xs font-bold text-slate-500 uppercase cursor-pointer hover:text-primary transition-colors flex items-center gap-1"
 >
 Tarif Adı
 {sortConfig.key === 'name' && (
 <span className="material-symbols-outlined text-sm">
 {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
 </span>
 )}
 </th>
 <th
 onClick={() => handleSort('category')}
 className="px-6 py-4 text-xs font-bold text-slate-500 uppercase cursor-pointer hover:text-primary transition-colors"
 >
 <div className="flex items-center gap-1">
 Kategori
 {sortConfig.key === 'category' && (
 <span className="material-symbols-outlined text-sm">
 {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
 </span>
 )}
 </div>
 </th>
 <th
 onClick={() => handleSort('createdAt')}
 className="px-6 py-4 text-xs font-bold text-slate-500 uppercase cursor-pointer hover:text-primary transition-colors text-center"
 >
 <div className="flex items-center justify-center gap-1">
 Tarih
 {sortConfig.key === 'createdAt' && (
 <span className="material-symbols-outlined text-sm">
 {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
 </span>
 )}
 </div>
 </th>
 <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-center">Veri Kalitesi</th>
 <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-center">Durum</th>
 <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-right">İşlemler</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
 {filteredDishes.map(dish => (
 <tr key={dish.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors cursor-pointer ${selectedDishIds.includes(dish.id) ? 'bg-primary/5 dark:bg-primary/10' : ''}`} onClick={() => openDishEditor(dish)}>
 <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
 <input
 type="checkbox"
 checked={selectedDishIds.includes(dish.id)}
 onChange={() => handleSelectDish(dish.id)}
 className="size-4 accent-primary rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 cursor-pointer"
 />
 </td>
 <td className="px-6 py-4">
 <div className="size-10 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
 {(dish.imageUrl || dish.image_url) ? (
 <img src={resolveImageUrl(dish.imageUrl || dish.image_url)} className="w-full h-full object-cover" alt="Thumb" />
 ) : (
 <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-lg">image</span>
 )}
 </div>
 </td>
 <td className="px-6 py-4">
 <div className="space-y-2">
 <div className="font-bold text-slate-800 dark:text-slate-200 text-sm">{dish.name}</div>
 {!!dish.partySuitable && (
 <div className="flex flex-wrap gap-1.5">
 <span className="px-2 py-0.5 bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300 border border-fuchsia-500/20 rounded text-[9px] font-black uppercase tracking-wider">
 Özel Gün
 </span>
 {(Array.isArray(dish.partyTypes) ? dish.partyTypes : []).map((type) => (
 <span
 key={type}
 className="px-2 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-300 border border-amber-500/20 rounded text-[9px] font-black uppercase tracking-wider"
 >
 {type === 'birthday' ? 'Doğum Günü' : type === 'gold_day' ? 'Altın Günü' : type}
 </span>
 ))}
 </div>
 )}
 </div>
 </td>
 <td className="px-6 py-4"><span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-black text-slate-500 uppercase">{CATEGORY_LABELS[dish.category] || dish.category}</span></td>
 <td className="px-6 py-4 text-center">
 <div className="flex flex-col">
 <span className="text-[10px] font-black text-slate-400 uppercase leading-tight">
 {(() => {
 const rawDate = dish.createdAt || dish.created_at;
 if (!rawDate) return '-';
 const d = new Date(rawDate);
 return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' });
 })()}
 </span>
 <span className="text-[9px] font-bold text-slate-300">
 {(() => {
 const rawDate = dish.createdAt || dish.created_at;
 if (!rawDate) return '';
 const d = new Date(rawDate);
 return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
 })()}
 </span>
 </div>
 </td>
 <td className="px-6 py-4 text-center">
 {dish.instructions && Array.isArray(dish.instructions) && dish.instructions.length > 0 ? (
 <span className="px-2 py-0.5 bg-primary/20 text-primary text-[9px] font-black tracking-widest rounded flex items-center justify-center gap-1 mx-auto w-fit border border-primary/30 shadow-sm">
 <span className="material-symbols-outlined text-[12px]">verified</span> MASTER
 </span>
 ) : (
 <span className="px-2 py-0.5 bg-slate-100 text-slate-400 text-[9px] font-black tracking-widest rounded flex items-center justify-center gap-1 mx-auto w-fit border border-slate-200 shadow-sm">
 <span className="material-symbols-outlined text-[12px]">skeleton</span> İSKELET
 </span>
 )}
 </td>
 <td className="px-6 py-4 text-center">
 <button
 onClick={(e) => { e.stopPropagation(); handleToggleActive(dish); }}
 disabled={togglingId === dish.id}
 className={`px-3 py-1 rounded-full text-[10px] font-black tracking-widest transition-all active:scale-95 shadow-sm border flex items-center gap-1 mx-auto ${dish.isActive ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600' : 'bg-red-500 text-white border-red-600 hover:bg-red-600'} disabled:opacity-50`}
 >
 {togglingId === dish.id && <span className="material-symbols-outlined text-xs animate-spin">sync</span>}
 {dish.isActive ? 'YAYINDA' : 'GİZLENDİ'}
 </button>
 </td>
 <td className="px-6 py-4 text-right">
 <div className="flex items-center justify-end gap-2">
 <button
 onClick={(e) => {
 e.stopPropagation();
 // Parse nutritionalValues if it's a string
 const dishToEdit = { ...dish };
 if (typeof dishToEdit.nutritionalValues === 'string') {
 try {
 let parsed = JSON.parse(dishToEdit.nutritionalValues);
 // Flatten if nested: { nutritionalValues: { calories: ... } }
 if (parsed && parsed.nutritionalValues && !parsed.calories) {
 parsed = parsed.nutritionalValues;
 }
 dishToEdit.nutritionalValues = parsed;
 } catch (err) {
 console.error('Failed to parse nutritionalValues:', err);
 }
 } else if (dishToEdit.nutritionalValues && dishToEdit.nutritionalValues.nutritionalValues && !dishToEdit.nutritionalValues.calories) {
 // Already an object but nested
 dishToEdit.nutritionalValues = dishToEdit.nutritionalValues.nutritionalValues;
 }
 openDishEditor(dish);
 }}
 className="p-2 text-slate-400 hover:text-primary transition"
 title="Düzenle"
 >
 <span className="material-symbols-outlined text-lg">edit</span>
 </button>
 <button
 onClick={async (e) => {
 e.stopPropagation();
 const result = await Swal.fire({
 title: 'Tarifi Sil?',
 html: `<strong>${dish.name}</strong> tarifini kalıcı olarak silmek istediğinize emin misiniz?`,
 icon: 'warning',
 showCancelButton: true,
 confirmButtonColor: '#ef4444',
 cancelButtonColor: '#64748b',
 confirmButtonText: 'Evet, Sil!',
 cancelButtonText: 'Vazgeç'
 });
 if (result.isConfirmed) {
 try {
 const deleteIdentifier = dish.id || encodeURIComponent(dish.name);
 const res = await fetch(`${API_BASE_URL}/api/admin-recipes/${deleteIdentifier}`, {
 method: 'DELETE'
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) {
 setAllDishes(prev => prev.filter(d => d.name !== dish.name && String(d.id) !== String(dish.id)));
 Swal.fire('Silindi!', 'Tarif başarıyla silindi.', 'success');
 } else {
 Swal.fire('Hata!', data.message || 'Tarif silinemedi.', 'error');
 }
 } catch (err) {
 console.error('Delete error:', err);
 Swal.fire('Hata!', 'Bağlantı hatası!', 'error');
 }
 }
 }}
 className="p-2 text-slate-400 hover:text-red-500 transition"
 title="Sil"
 >
 <span className="material-symbols-outlined text-lg">delete</span>
 </button>
 </div>
 </td>
 </tr>
 ))}
 {filteredDishes.length === 0 && (
 <tr>
 <td colSpan="4" className="px-6 py-12 text-center text-slate-400 italic text-sm">Arama kriterine uygun tarif bulunamadı.</td>
 </tr>
 )}
 </tbody>
 </table>
 </div>
 </div>
 </div>
 )
 }
 </div>
 </div>
 </main>

 {/* Editing Modal - Tabbed Layout */}
 {
 editingDish && (
 <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-200 overflow-hidden">
 <div className="bg-white dark:bg-[#0B1219] w-full max-w-4xl max-h-[90vh] rounded-[32px] flex flex-col shadow-2xl border border-white/10 overflow-hidden">

 {/* Modal Header */}
 <div className="p-6 sm:p-8 flex items-start justify-between border-b border-slate-100 dark:border-slate-800 shrink-0">
 <div>
 <div className="flex items-center gap-3 mb-1">
 <span className="px-2 py-0.5 bg-primary/10 text-primary text-[10px] font-black tracking-widest rounded uppercase">RECAPRIVE MASTER</span>
 {/* NEW: ID Display for Manual Pairing */}
 <div className="flex items-center gap-2 px-2 py-0.5 bg-red-500/10 border border-red-500/20 rounded cursor-pointer group hover:bg-red-500/20 transition-all"
 onClick={() => {
 navigator.clipboard.writeText(String(editingDish.id));
 // Minimal toast could be added here
 }}
 title="ID'yi Kopyala">
 <span className="text-[10px] font-black text-red-500 font-mono">ID: {editingDish.id}</span>
 <span className="material-symbols-outlined text-[10px] text-red-400 group-hover:text-red-500">content_copy</span>
 </div>
 <h3 className="text-xl font-black text-slate-900 dark:text-white truncate max-w-md ml-2">{editingDish.name}</h3>
 </div>
 <p className="text-slate-500 dark:text-slate-400 text-xs font-medium">Tarif kartını güncelle ve zengin detayları yönet.</p>
 </div>
 <button type="button" onClick={() => { setEditingDish(null); setModalTab('BASIC'); }} className="size-10 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-white transition-all">
 <span className="material-symbols-outlined">close</span>
 </button>
 </div>

 {/* Modal Tabs */}
 <div className="px-8 border-b border-slate-100 dark:border-slate-800 shrink-0 overflow-x-auto scrollbar-hide">
 <div className="flex gap-8">
 {[
 { id: 'BASIC', label: 'Genel Bilgiler', icon: 'info' },
 { id: 'MALZEMELER', label: 'Malzemeler', icon: 'shopping_basket' },
 { id: 'STEPS', label: 'Talimatlar & Asistan', icon: 'assignment' },
 { id: 'NUTRITION', label: 'Besin Değerleri', icon: 'nutrition' },
 { id: 'COOKING', label: 'Pişirme & Hazırlık', icon: 'oven_gen' },
 { id: 'ALLERGENS', label: 'Alerjen Mutfağı', icon: 'shield' },
 { id: 'JSON', label: 'Gelişmiş JSON', icon: 'code' },
 ].map(tab => (
 <button
 key={tab.id}
 type="button"
 onClick={() => handleModalTabChange(tab.id)}
 className={`py-4 flex items-center gap-2 text-sm font-bold transition-all border-b-2 whitespace-nowrap ${modalTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'}`}
 >
 <span className="material-symbols-outlined text-[20px]">{tab.icon}</span>
 {tab.label}
 </button>
 ))}
 </div>
 </div>

 {/* Modal Body - Content Area */}
 <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
 <form id="editDishForm" onSubmit={handleUpdateDish} className="space-y-6">


 {modalTab === 'BASIC' && (
 <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
 {/* Visual Preview Area */}
 <div className="flex flex-col md:flex-row gap-6 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-[24px] border border-slate-100 dark:border-slate-800">
 <div className="w-full md:w-48 aspect-square bg-white dark:bg-slate-800 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 relative shadow-inner shrink-0 group">
 {(editingDish.imageUrl || editingDish.image_url) ? (
 <img
 src={resolveImageUrl(editingDish.imageUrl || editingDish.image_url)}
 className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
 alt="Recipe Preview"
 />
 ) : (
 <div className="w-full h-full flex flex-col items-center justify-center text-slate-300 dark:text-slate-600">
 <span className="material-symbols-outlined text-4xl mb-2">image_not_supported</span>
                                                            <span className="text-[10px] uppercase font-black px-2 py-0.5 bg-slate-200 dark:bg-slate-700/50 rounded">KİŞİSEL GÖRSEL YOK</span>
 {/* Fallback info */}
 <p className="text-[9px] mt-2 text-slate-400 font-medium italic">Kategori görseli kullanılacak</p>
 </div>
 )}
 </div>
 <div className="flex-1 space-y-3 py-1">
 <div className="flex items-center gap-2">
 <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-800 text-slate-500 rounded text-[9px] font-black uppercase tracking-widest">{editingDish.category || 'GENEL'}</span>
                                                        <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-[9px] font-black uppercase tracking-widest">ÖNİZLEME</span>
 </div>
 <h4 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">{editingDish.name || 'İsimsiz Tarif'}</h4>
 <div className="flex flex-wrap gap-2">
 <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
 <span className="material-symbols-outlined text-[16px] text-slate-400">restaurant</span>
 <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300">{editingDish.meatType || 'Yok/Sebze'}</span>
 </div>
 <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
 <span className="material-symbols-outlined text-[16px] text-slate-400">schedule</span>
 <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300">{editingDish.prepTimeEstimate || 'NORMAL'} HAZIRLIK</span>
 </div>
 </div>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Yemek Adı</label>
 <input
 type="text"
 value={editingDish.name}
 onChange={e => setEditingDish({ ...editingDish, name: e.target.value })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 transition-all outline-none text-slate-900 dark:text-white"
 />
 </div>
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Görsel (Yükle veya URL)</label>
 <div className="flex gap-3">
 <input
 type="text"
 value={editingDish.imageUrl || editingDish.image_url || ''}
 onChange={e => setEditingDish({ ...editingDish, imageUrl: e.target.value, image_url: e.target.value })}
 placeholder="https://..."
 className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none text-slate-900 dark:text-white"
 />
 <label className={`w-12 h-[46px] flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-2xl cursor-pointer hover:bg-slate-50 transition-all ${imageUploading ? 'opacity-50 pointer-events-none' : ''} shadow-sm`}>
 <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={imageUploading} />
 <span className={`material-symbols-outlined text-[20px] ${imageUploading ? 'animate-spin' : 'text-primary'}`}>
 {imageUploading ? 'sync' : 'cloud_upload'}
 </span>
 </label>
 {(editingDish.imageUrl || editingDish.image_url) && (
 <div className="size-12 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-100 shrink-0">
 <img src={resolveImageUrl(editingDish.imageUrl || editingDish.image_url)} className="w-full h-full object-cover" alt="Small Preview" />
 </div>
 )}
 </div>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Kategori</label>
 <select
 value={editingDish.category}
 onChange={e => setEditingDish({ ...editingDish, category: e.target.value })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none text-slate-900 dark:text-white appearance-none"
 >
 {CATEGORY_KEYS.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
 </select>
 </div>
 <div>
                                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Orijinal Porsiyon (Kişilik)</label>
 <input
 type="number"
 value={editingDish.servings || 1}
 onChange={e => setEditingDish({ ...editingDish, servings: parseInt(e.target.value) })}
 placeholder="1"
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none text-slate-900 dark:text-white"
 />
 </div>
 </div>

                                            {/* Sunum Şekli - NEW */}
 <div className="space-y-2 pt-4 border-t border-slate-50 dark:border-slate-800/50">
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">
                                                    Sunum Şekli
 </label>
 <select
 value={editingDish.serveStyle || ''}
 onChange={e => setEditingDish({ ...editingDish, serveStyle: e.target.value })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none"
 >
                                                    <option value="">Kategori Seçin</option>
                                                    <option value="ana_yemek">Ana Yemek</option>
                                                    <option value="kahvalti">Kahvaltı</option>
                                                    <option value="corba">Çorba</option>
                                                    <option value="salata">Salata</option>
                                                    <option value="meze">Meze</option>
                                                    <option value="yan_yemek">Yan Yemek / Hafif Öğün</option>
                                                    <option value="hamur_isi">Hamur İşi / Börek</option>
                                                    <option value="tatli">Tatlı</option>
                                                    <option value="atıştırmalık">Atıştırmalık</option>
                                                    <option value="harc">Sos / Harç / Bağlayıcı</option>
                                                    <option value="ara_urun">Ara Ürün / Temel Ürün</option>
 </select>
 {!editingDish.serveStyle && (
 <p className="text-xs text-amber-600 dark:text-amber-400 italic ml-1">
                                                        Uyarı: AI tahmini yok, lütfen manuel seçin
 </p>
 )}
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-50 dark:border-slate-800/50">
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Et Tipi</label>
 <select
 value={editingDish.meatType || ''}
 onChange={e => setEditingDish({ ...editingDish, meatType: e.target.value })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none"
 >
 <option value="">Yok / Sebze</option>
 <option value="dana">Dana</option>
 <option value="kuzu">Kuzu</option>
 <option value="tavuk">Tavuk</option>
 <option value="balik">Balık / Deniz</option>
 <option value="hindi">Hindi</option>
 </select>
 </div>
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Zorluk Seviyesi</label>
 <select
 value={editingDish.difficulty || 'Kolay'}
 onChange={e => setEditingDish({ ...editingDish, difficulty: e.target.value })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none"
 >
 <option value="Kolay">Kolay</option>
 <option value="Orta">Orta</option>
 <option value="Zor">Zor</option>
 </select>
 </div>
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Pişirme Derecesi</label>
 <select
 value={editingDish.cookingDegree || ''}
 onChange={e => setEditingDish({ ...editingDish, cookingDegree: e.target.value })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none"
 >
 <option value="">N/A</option>
 <option value="az">Az (Rare)</option>
 <option value="orta">Orta (Medium)</option>
 <option value="iyi">İyi (Well-Done)</option>
 </select>
 </div>
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Porsiyonlama Günü</label>
 <input
 type="number"
 value={editingDish.lockOutDays}
 onChange={e => setEditingDish({ ...editingDish, lockOutDays: parseInt(e.target.value) })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none"
 />
 </div>
 <div className="md:col-span-2">
 <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1 tracking-wider">Yemek Ailesi (7 Gün Tekrar Kilidi)</label>
 <select
 value={editingDish.family || 'Genel Yemek Ailesi'}
 onChange={e => setEditingDish({ ...editingDish, family: e.target.value })}
 className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/30 outline-none"
 >
 {FAMILY_OPTIONS.map((fam) => (
 <option key={fam} value={fam}>{fam}</option>
 ))}
 </select>
 </div>
 </div>

 <div className="flex flex-col xl:flex-row gap-6 pt-4 border-t border-slate-50 dark:border-slate-800/50">
 <div className="flex-1 space-y-4">
 <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Karakteristik & Eşlikçiler</label>

 {/* Rescue & Oven */}
 <div className="grid grid-cols-2 gap-3">
 <label className={`flex items-center gap-3 p-3 rounded-2xl border transition-all cursor-pointer ${editingDish.isRescue ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400' : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-60'}`}>
 <input type="checkbox" className="hidden" checked={!!editingDish.isRescue} onChange={e => setEditingDish({ ...editingDish, isRescue: e.target.checked })} />
 <span className="material-symbols-outlined text-lg">bolt</span>
 <span className="text-[10px] font-black uppercase">Kurtarma</span>
 </label>
 <label className={`flex items-center gap-3 p-3 rounded-2xl border transition-all cursor-pointer ${editingDish.requiresOven ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400' : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-60'}`}>
 <input type="checkbox" className="hidden" checked={!!editingDish.requiresOven} onChange={e => setEditingDish({ ...editingDish, requiresOven: e.target.checked })} />
 <span className="material-symbols-outlined text-lg">oven_gen</span>
 <span className="text-[10px] font-black uppercase">Fırın</span>
 </label>
 </div>

                                                    {/* Rescue Linking - only visible when isRescue = true */}
 {editingDish.isRescue && (
 <div className="mt-2 p-4 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl space-y-3 animate-in slide-in-from-top-2 duration-200">
 <p className="text-[10px] font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-widest flex items-center gap-1.5">
 <span className="material-symbols-outlined text-[14px]">link</span>
 Kurtarma Bağlantısı
 </p>
                                                            {/* Ana Tarif - baseDishId */}
 <div className="space-y-1.5">
 <label className="block text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
 Takip Eden Ana Tarif (baseDishId)
 </label>
 <p className="text-[9px] text-slate-500 dark:text-slate-400 italic -mt-1">
 Bu kurtarma yapıldıktan sonra ertesi gün kullanıcıya hangi tarif önerilsin?
 </p>
 <select
 value={editingDish.baseDishId ? String(editingDish.baseDishId) : ''}
 onChange={e => setEditingDish({ ...editingDish, baseDishId: e.target.value ? Number(e.target.value) : null })}
 className="w-full px-3 py-2.5 bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800/50 rounded-xl text-xs font-bold focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 dark:text-white"
 >
                                                                    <option value="">Bağlı ana tarif yok</option>
 {allDishes
 .filter(d => String(d.id) !== String(editingDish.id) && !d.isRescue)
 .map(d => (
 <option key={d.id} value={String(d.id)}>
 [{d.category}] {d.name}
 </option>
 ))}
 </select>
 {editingDish.baseDishId && (() => {
 const linked = allDishes.find(d => String(d.id) === String(editingDish.baseDishId));
 return linked ? (
 <p className="text-[9px] text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
 <span className="material-symbols-outlined text-[11px]">check_circle</span>
 Bağlı: {linked.name} (ID: {linked.id})
 </p>
 ) : (
 <p className="text-[9px] text-amber-500 font-bold flex items-center gap-1">
 <span className="material-symbols-outlined text-[11px]">warning</span>
                                                                            ID {editingDish.baseDishId} liste dışı (kayıtlı ama filtrelenmemiş olabilir)
 </p>
 );
 })()}
 </div>
                                                            {/* Rescue Trigger - rescueTrigger */}
 <div className="space-y-1.5 relative">
 <label className="block text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
 Tetikleyici Malzeme (rescueTrigger)
 </label>
 <p className="text-[9px] text-slate-500 dark:text-slate-400 italic -mt-1">
 Hangi sözlük öğesinin SKT'si yaklaştığında bu tarif tetiklensin? (Ara: min 2 karakter)
 </p>

 {/* Seçili öğeyi göster */}
 {editingDish.rescueTrigger && (() => {
 const selected = rescueDictionary.find(d => d.id === editingDish.rescueTrigger) || triggerDictionary.find(d => d.id === editingDish.rescueTrigger);
 if (selected) return (
 <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700/50 rounded-xl text-xs font-bold">
<span className="flex-1 text-emerald-900 dark:text-emerald-100">{selected.canonicalName} <span className="text-emerald-600/60 dark:text-emerald-400/60">({normalizeAdminDisplayUnit(selected.canonicalUnit)})</span></span>
 <button
 type="button"
 onClick={() => setEditingDish({ ...editingDish, rescueTrigger: null })}
 className="text-red-400 hover:text-red-600 text-[11px]"
 >❌ Sil</button>
 </div>
 );
 // Fallback: eski manuel metin girdisi kalmışsa
 return (
 <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-bold">
 <span className="flex-1 text-slate-500 line-through">{editingDish.rescueTrigger} (Eski Metin)</span>
 <button
 type="button"
 onClick={() => setEditingDish({ ...editingDish, rescueTrigger: null })}
 className="text-red-400 hover:text-red-600 text-[11px]"
 >❌ Sil/Yenile</button>
 </div>
 );
 })()}

 {/* Arama Input */}
 {!editingDish.rescueTrigger && (
 <div className="relative">
 <input
 type="text"
 placeholder="Stoğunuzu taramak için sözlükte ara..."
 className="w-full px-3 py-2.5 bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800/50 rounded-xl text-xs font-bold focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 dark:text-white placeholder:text-slate-400 placeholder:font-normal"
 onChange={(e) => {
 const q = e.target.value.trim();
 setTriggerLastQuery(q);
 if (q.length < 2) {
 setTriggerDictionary([]);
 setIsTriggerLoading(false);
 return;
 }

 setIsTriggerLoading(true);
 if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

 searchTimeoutRef.current = setTimeout(async () => {
 try {
 const res = await fetch(`${API_BASE_URL}/api/admin/dictionary-rescue?q=${encodeURIComponent(q)}`, {
 credentials: 'include'
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) setTriggerDictionary(data.items);
 } catch (err) {
 console.error('Trigger search err:', err);
 } finally {
 setIsTriggerLoading(false);
 }
 }, 400); // 400ms debounce
 }}
 />
 {/* Loading Indicator */}
 {isTriggerLoading && (
 <div className="absolute right-3 top-1/2 -translate-y-1/2">
 <span className="material-symbols-outlined text-emerald-400 animate-spin text-sm">sync</span>
 </div>
 )}
 {/* Canlı Sonuç Listesi */}
 {triggerDictionary.length > 0 && (
 <div className="absolute top-full left-0 w-full max-h-[200px] overflow-y-auto bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-700 rounded-xl shadow-xl z-[150] mt-1 p-1">
 {triggerDictionary.map(item => (
 <button
 key={item.id}
 type="button"
 onClick={() => {
 setEditingDish({ ...editingDish, rescueTrigger: item.id });
 if (!rescueDictionary.some(d => d.id === item.id)) {
 setRescueDictionary(prev => [...prev, item]);
 }
 setTriggerDictionary([]);
 }}
 className="w-full text-left px-3 py-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg text-xs font-bold flex items-center justify-between gap-2 transition-all"
 >
 <span>{item.canonicalName}</span>
<span className="text-[10px] text-emerald-600/60 dark:text-emerald-400/60 bg-emerald-50 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full">{normalizeAdminDisplayUnit(item.canonicalUnit)}</span>
 </button>
 ))}
 </div>
 )}

 {/* No results indicator */}
 {!isTriggerLoading && triggerLastQuery.length >= 2 && triggerDictionary.length === 0 && (
 <div className="absolute top-full left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl shadow-lg z-[150] mt-1 p-4 text-center">
 <span className="material-symbols-outlined text-slate-300 mb-1">search_off</span>
 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Sonuç bulunamadı</p>
 </div>
 )}
 </div>
 )}
 </div>

 {/* Production Settings (ERP Logic) */}
 <div className="pt-3 border-t border-emerald-100 dark:border-emerald-800/30 space-y-4">
 <p className="text-[10px] font-black text-emerald-800 dark:text-emerald-300 uppercase tracking-widest flex items-center gap-1.5">
 <span className="material-symbols-outlined text-[14px]">factory</span>
 Üretim Parametreleri (Standard Yield)
 </p>

 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center justify-between">
 <span>Net Üretim Miktarı</span>
 <button
 type="button"
 onClick={handleCalculateYield}
 disabled={isYieldCalculating}
 title="İşlem tipine (Verim Oranına) göre gramajları toplar ve net üretim miktarını belirler"
 className="text-emerald-500 hover:text-emerald-600 dark:text-emerald-400 font-black tracking-widest flex items-center gap-1 transition-all disabled:opacity-50"
 >
 <span className={`material-symbols-outlined text-[13px] ${isYieldCalculating ? 'animate-spin' : ''}`}>{isYieldCalculating ? 'sync' : 'auto_fix_high'}</span>
 OTOMATİK (YIELD)
 </button>
 </label>
 <input
 type="number"
 step="0.01"
 value={editingDish.producedAmount || ''}
 onChange={e => setEditingDish({ ...editingDish, producedAmount: parseFloat(e.target.value) })}
 className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold"
 placeholder="Örn: 500"
 />
 </div>
 <div className="space-y-1.5">
 <label className="block text-[10px] font-bold text-slate-500 uppercase">Üretim Birimi</label>
 <select
 value={editingDish.producedUnit || ''}
 onChange={e => setEditingDish({ ...editingDish, producedUnit: e.target.value })}
 className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold"
 >
 <option value="">⚙️ Birim Seç ⚙️</option>
 <option value="gr">Gram (gr)</option>
 <option value="ml">Mililitre (ml)</option>
 </select>
 </div>
 </div>

 <div className="space-y-1.5 relative">
 <label className="block text-[10px] font-bold text-slate-500 uppercase">Stok Dictionary Eşleşmesi</label>
 <p className="text-[9px] text-slate-500 italic -mt-1">Bu tarif onaylandığında hangi stok ismiyle girsin? (Ara: min 2 karakter)</p>

 {/* Seçili öğeyi göster */}
 {editingDish.producedDictionaryId && (() => {
 const selected = rescueDictionary.find(d => d.id === editingDish.producedDictionaryId);
 if (selected) return (
 <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-300 dark:border-indigo-700 rounded-xl text-xs font-bold">
<span className="flex-1">{selected.canonicalName} <span className="text-slate-400">({normalizeAdminDisplayUnit(selected.canonicalUnit)})</span></span>
 <button
 type="button"
 onClick={() => setEditingDish({ ...editingDish, producedDictionaryId: null })}
 className="text-red-400 hover:text-red-600 text-[11px]"
 >❌ Sil</button>
 </div>
 );
 })()}

 {/* Arama input */}
 {!editingDish.producedDictionaryId && (
 <div className="relative">
 <input
 type="text"
 placeholder="Sözlükte ara... (örn: Yoğurt, Sos, Harç)"
 className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500/30 outline-none"
 onChange={(e) => {
 const q = e.target.value.trim();
 setRescueLastQuery(q);
 if (q.length < 2) {
 setRescueDictionary([]);
 setIsRescueLoading(false);
 return;
 }

 setIsRescueLoading(true);
 if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

 searchTimeoutRef.current = setTimeout(async () => {
 try {
 const res = await fetch(`${API_BASE_URL}/api/admin/dictionary-rescue?q=${encodeURIComponent(q)}`, {
 credentials: 'include'
 });
 const data = await readJsonOrThrow(res, 'İstek başarısız');
 if (data.ok) setRescueDictionary(data.items);
 } catch (err) {
 console.error('Dictionary rescue search err:', err);
 } finally {
 setIsRescueLoading(false);
 }
 }, 400);
 }}
 />
 {/* Loading Indicator */}
 {isRescueLoading && (
 <div className="absolute right-3 top-1/2 -translate-y-1/2">
 <span className="material-symbols-outlined text-indigo-400 animate-spin text-sm">sync</span>
 </div>
 )}
 {/* Canlı sonuç listesi */}
 {rescueDictionary.length > 0 && (
 <div className="absolute top-full left-0 w-full max-h-[200px] overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-[150] mt-1 p-1">
 {rescueDictionary.map(item => (
 <button
 key={item.id}
 type="button"
 onClick={() => {
 setEditingDish({ ...editingDish, producedDictionaryId: item.id });
 setRescueDictionary([]);
 }}
 className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg text-xs font-bold flex items-center justify-between gap-2 transition-all"
 >
 <span>{item.canonicalName}</span>
<span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">{normalizeAdminDisplayUnit(item.canonicalUnit)}</span>
 </button>
 ))}
 </div>
 )}

 {/* No results indicator */}
 {!isRescueLoading && rescueLastQuery.length >= 2 && rescueDictionary.length === 0 && !editingDish.producedDictionaryId && (
 <div className="absolute top-full left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl shadow-lg z-[150] mt-1 p-4 text-center">
 <span className="material-symbols-outlined text-slate-300 mb-1">search_off</span>
 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Sonuç bulunamadı</p>
 </div>
 )}
 </div>
 )}

 {/* Birim uyumsuzluğu uyarısı */}
 {editingDish.producedDictionaryId && (() => {
 const allItems = rescueDictionary;
 const match = allItems.find(d => d.id === editingDish.producedDictionaryId);
 if (match && editingDish.producedUnit && match.canonicalUnit !== editingDish.producedUnit) {
 return (
 <p className="text-[9px] text-red-500 font-bold flex items-center gap-1 mt-1">
 <span className="material-symbols-outlined text-[12px]">warning</span>
 Birim Uyumsuzluğu: Dictionary {match.canonicalUnit} bekliyor!
 </p>
 );
 }
 return null;
 })()}
 </div>

 </div>
 </div>
 )}

 {/* Compact Pairings Search */}
 <div className="relative group">
 <input
 type="text"
 placeholder="Eşlikçi ekle (ara...)"
 className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500/20 outline-none"
 onChange={(e) => {
 const term = e.target.value.toLowerCase();
 const listEl = document.getElementById('compact-pairing-list');
 if (listEl) {
 if (!term) {
 listEl.classList.add('hidden');
 return;
 }
 listEl.classList.remove('hidden');
 const items = listEl.getElementsByTagName('button');
 for (let item of items) {
 const txt = item.textContent.toLowerCase();
 const isVisible = txt.includes(term);
 item.classList.toggle('flex', isVisible);
 item.classList.toggle('hidden', !isVisible);
 }
 }
 }}
 onFocus={() => document.getElementById('compact-pairing-list')?.classList.remove('hidden')}
 onBlur={() => setTimeout(() => document.getElementById('compact-pairing-list')?.classList.add('hidden'), 200)}
 />
 {/* Pairing Suggestions Dropdown */}
 <div id="compact-pairing-list" className="hidden absolute top-auto bottom-full mb-2 left-0 w-full max-h-[200px] overflow-y-auto bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl z-50 p-1 custom-scrollbar">
 {allDishes
 .filter(d => String(d.id) !== String(editingDish.id) && !(Array.isArray(editingDish.pairings) ? editingDish.pairings : []).includes(String(d.id)))
 .map(dish => (
 <button
 key={dish.id}
 type="button"
 onClick={() => {
 const current = Array.isArray(editingDish.pairings) ? editingDish.pairings : [];
 setEditingDish({ ...editingDish, pairings: [...current, String(dish.id)] });
 // Clear input manually if needed or just let it exist
 }}
 className="flex w-full items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg group text-left"
 >
 <div className="size-6 rounded bg-slate-100 dark:bg-slate-700 shrink-0 overflow-hidden">
 {dish.imageUrl && <img src={resolveImageUrl(dish.imageUrl)} className="w-full h-full object-cover" />}
 </div>
 <div>
 <p className="text-[10px] font-bold text-slate-700 dark:text-slate-200">{dish.name}</p>
 <p className="text-[9px] text-slate-400">{dish.category}</p>
 </div>
 </button>
 ))}
 </div>
 </div>

 {/* Selected Chips */}
 <div className="flex flex-wrap gap-2">
 {(Array.isArray(editingDish.pairings) ? editingDish.pairings : []).map((pid, idx) => {
 const partner = allDishes.find(d => String(d.id) === String(pid));
 return (
 <div key={idx} className="flex items-center gap-1 pl-2 pr-1 py-1 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
 <span className="material-symbols-outlined text-[10px] text-indigo-400">link</span>
 <span className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300">
 {partner ? partner.name : pid}
 </span>
 <button
 type="button"
 onClick={() => {
 const currentPairings = Array.isArray(editingDish.pairings) ? editingDish.pairings : [];
 const newPairings = currentPairings.filter(p => p !== pid);
 setEditingDish({ ...editingDish, pairings: newPairings });
 }}
 className="size-4 flex items-center justify-center rounded-full hover:bg-rose-100 dark:hover:bg-rose-900/50 hover:text-rose-500 transition-colors ml-1"
 >
 <span className="material-symbols-outlined text-[10px]">close</span>
 </button>
 </div>
 );
 })}
 </div>
 </div>

 <div className="flex-1 space-y-3">
 <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Güvenlik & Diyet</label>
 <div className="flex flex-wrap gap-2">
 {[
 { label: 'VEGAN', key: 'isVegan' },
 { label: 'VEJETARYEN', key: 'isVegetarian' },
 { label: 'DİYET', key: 'isDiet' },
 { label: 'BEBEK', key: 'isChildFriendly' },
 { label: 'BESLENME', key: 'isLunchbox' }
 ].map(flag => (
 <label key={flag.key} className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-xl border transition-all ${editingDish[flag.key] ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-400' : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-50'}`}>
 <input type="checkbox" className="hidden" checked={!!editingDish[flag.key]} onChange={e => setEditingDish({ ...editingDish, [flag.key]: e.target.checked })} />
 <span className="text-[9px] font-black tracking-tighter">{flag.label}</span>
 </label>
 ))}
 </div>
 </div>
 </div>

 <div className="space-y-4 pt-4 border-t border-slate-50 dark:border-slate-800/50">
 <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Özel Gün Uygunluğu</label>

 <label className={`flex items-center gap-4 p-4 rounded-2xl border transition-all cursor-pointer ${editingDish.partySuitable ? 'bg-fuchsia-50 dark:bg-fuchsia-900/20 border-fuchsia-200 dark:border-fuchsia-800 text-fuchsia-700 dark:text-fuchsia-300' : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800'}`}>
 <input
 type="checkbox"
 className="w-5 h-5 accent-fuchsia-500 rounded"
 checked={!!editingDish.partySuitable}
 onChange={e => {
 const checked = e.target.checked;
 setEditingDish({
 ...editingDish,
 partySuitable: checked,
 partyTypes: checked ? (Array.isArray(editingDish.partyTypes) ? editingDish.partyTypes : []) : []
 });
 }}
 />
 <div>
 <p className="text-sm font-bold text-slate-800 dark:text-white">Partiye Uygun</p>
 <p className="text-[10px] text-slate-500 font-medium">Bu tarif Özel Günler Mutfağı içinde doğum günü veya altın günü menülerinde kullanılabilir.</p>
 </div>
 </label>

 {editingDish.partySuitable && (
 <div className="space-y-4 animate-in fade-in duration-200">
 <div className="space-y-3">
 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Uygun Parti Türleri</p>
 <div className="flex flex-wrap gap-2">
 {[
 { label: 'Doğum Günü', value: 'birthday' },
 { label: 'Altın Günü', value: 'gold_day' }
 ].map(option => {
 const currentTypes = Array.isArray(editingDish.partyTypes) ? editingDish.partyTypes : [];
 const active = currentTypes.includes(option.value);
 return (
 <label key={option.value} className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-xl border transition-all ${active ? 'bg-fuchsia-50 dark:bg-fuchsia-900/20 border-fuchsia-200 dark:border-fuchsia-800 text-fuchsia-700 dark:text-fuchsia-300' : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-70'}`}>
 <input
 type="checkbox"
 className="hidden"
 checked={active}
 onChange={e => {
 const nextTypes = e.target.checked
 ? [...currentTypes, option.value]
 : currentTypes.filter(type => type !== option.value);
 setEditingDish({ ...editingDish, partyTypes: nextTypes });
 }}
 />
 <span className="text-[10px] font-black tracking-tight">{option.label}</span>
 </label>
 );
 })}
 </div>
 </div>

 <div className="space-y-3">
 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Seçili Özel Gün Etiketleri</p>
 <div className="flex flex-wrap gap-2">
 {Object.entries(SPECIAL_DAY_LABELS).map(([key, label]) => {
 const manualLabels = Array.isArray(editingDish.specialDayManualLabels) ? editingDish.specialDayManualLabels : [];
 const active = manualLabels.includes(key);
 return (
 <button
 key={key}
 type="button"
 className={`px-3 py-2 rounded-xl border text-[11px] font-black tracking-tight transition-all ${active ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300' : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 text-slate-500'}`}
 onClick={() => {
 const current = Array.isArray(editingDish.specialDayManualLabels) ? editingDish.specialDayManualLabels : [];
 const nextLabels = current.includes(key)
 ? current.filter((item) => item !== key)
 : [...current, key];
 setEditingDish({
 ...editingDish,
 specialDayManualLabels: nextLabels,
 tags: applyManualSpecialDayLabelsToTags(editingDish.tags || [], nextLabels)
 });
 }}
 >
 {label}
 </button>
 );
 })}
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
 <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Manuel Etiket</p>
 <div className="flex flex-wrap gap-1.5">
 {(Array.isArray(editingDish.specialDayManualLabels) ? editingDish.specialDayManualLabels : []).length > 0 ? (
 (editingDish.specialDayManualLabels || []).map((labelKey) => (
 <span key={labelKey} className="px-2 py-0.5 rounded border border-emerald-300/70 text-emerald-600 dark:text-emerald-300 bg-emerald-50/80 dark:bg-emerald-900/20 text-[10px] font-black">
 {SPECIAL_DAY_LABELS[labelKey] || labelKey}
 </span>
 ))
 ) : (
 <span className="text-[11px] text-slate-500">Seçim yok</span>
 )}
 </div>
 </div>

 <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
 <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Sistem Algısı</p>
 <div className="flex flex-wrap gap-1.5">
 {Object.entries(deriveSystemSpecialDayLabels(editingDish))
 .filter(([, active]) => !!active)
 .map(([labelKey]) => (
 <span key={labelKey} className="px-2 py-0.5 rounded border border-indigo-300/70 text-indigo-600 dark:text-indigo-300 bg-indigo-50/80 dark:bg-indigo-900/20 text-[10px] font-black">
 {SPECIAL_DAY_LABELS[labelKey] || labelKey}
 </span>
 ))}
 {Object.values(deriveSystemSpecialDayLabels(editingDish)).every((active) => !active) && (
 <span className="text-[11px] text-slate-500">Sinyal bulunamadı</span>
 )}
 </div>
 </div>
 </div>
 </div>
 )}
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
 <label className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-all">
 <input
 type="checkbox"
 checked={editingDish.quickSearchEligible}
 onChange={e => setEditingDish({ ...editingDish, quickSearchEligible: e.target.checked })}
 className="w-5 h-5 accent-primary rounded"
 />
 <div>
 <p className="text-sm font-bold text-slate-800 dark:text-white">QuickSearch (Instant)</p>
 <p className="text-[10px] text-slate-500 font-medium">Bu tarif anlık arama sonuçlarında çıkabilir.</p>
 </div>
 </label>
 <label className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-all">
 <input
 type="checkbox"
 checked={editingDish.alacarteOnly}
 onChange={e => setEditingDish({ ...editingDish, alacarteOnly: e.target.checked })}
 className="w-5 h-5 accent-primary rounded"
 />
 <div>
 <p className="text-sm font-bold text-slate-800 dark:text-white">Sadece Alakart</p>
 <p className="text-[10px] text-slate-500 font-medium">Otomatik haftalık plana dahil edilmez.</p>
 </div>
 </label>
 </div>
 </div>
 )}

 {modalTab === 'NUTRITION' && (
 <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 {[
 { id: 'calories', label: 'Kalori (kcal)', icon: 'local_fire_department' },
 { id: 'protein', label: 'Protein (g)', icon: 'muscle' },
 { id: 'carbs', label: 'Karbonhidrat (g)', icon: 'bakery_dining' },
 { id: 'fat', label: 'Yağ (g)', icon: 'water_drop' },
 ].map(nut => (
 <div key={nut.id} className="p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl">
 <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
 <span className="material-symbols-outlined text-xs">{nut.icon}</span>
 {nut.label}
 </label>
 <input
 type="text"
 value={editingDish.nutritionalValues?.[nut.id] || ''}
 onChange={e => {
 const newVal = e.target.value;
 const newValues = {
 ...(editingDish.nutritionalValues || {}),
 [nut.id]: newVal
 };
 setEditingDish({ ...editingDish, nutritionalValues: newValues });
 setNutritionJsonText(JSON.stringify(newValues, null, 2)); // Sync JSON text
 }}
 placeholder="-"
 className="w-full bg-transparent border-none text-base font-black text-slate-900 dark:text-white focus:ring-0 p-0 outline-none"
 />
 </div>
 ))}
 </div>

<div className="pt-6 border-t border-slate-50 dark:border-slate-800/50">
<label className="block text-xs font-bold text-slate-400 uppercase mb-3 ml-1 tracking-wider">Tüm Besin Değerleri (JSON)</label>
<div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
 <textarea
 value={nutritionJsonText}
 onChange={e => {
 const val = e.target.value;
 setNutritionJsonText(val); // Always update text to allow typing
 try {
 let parsed = JSON.parse(val);
 // Flatten if nested structure is pasted
 if (parsed && parsed.nutritionalValues && !parsed.calories) {
 parsed = parsed.nutritionalValues;
 }
 setEditingDish({ ...editingDish, nutritionalValues: parsed });
 } catch (err) {
 // Invalid JSON while typing
 }
 }}
 rows={8}
 className="w-full bg-transparent border-none text-xs font-mono text-slate-600 dark:text-slate-400 focus:ring-0 outline-none resize-none"
 />
</div>
<p className="text-[10px] text-slate-400 mt-2 ml-1 italic">Not: Vitaminleri veya mineralleri de JSON içine ekleyebilirsiniz. "Protein" vb. alanlar otomatik algılanır.</p>
</div>

<div className="pt-6 border-t border-slate-50 dark:border-slate-800/50">
<label className="block text-xs font-bold text-slate-400 uppercase mb-3 ml-1 tracking-wider">Sağlık Etkisi (JSON)</label>
<div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
<textarea
value={healthSignalsJsonText}
onChange={e => {
const val = e.target.value;
setHealthSignalsJsonText(val);
try {
const parsed = JSON.parse(val);
setEditingDish({ ...editingDish, healthSignals: parsed });
} catch (err) {
// Invalid JSON while typing
}
}}
rows={8}
className="w-full bg-transparent border-none text-xs font-mono text-slate-600 dark:text-slate-400 focus:ring-0 outline-none resize-none"
/>
</div>
<p className="text-[10px] text-slate-400 mt-2 ml-1 italic">Bu alan boş kalırsa sistem otomatik sağlık sinyali üretir.</p>
</div>
</div>
)}

 {modalTab === 'MALZEMELER' && (
 <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
 <div className="flex items-center justify-between mb-2">
 <label className="text-xs font-bold text-slate-400 uppercase ml-1 tracking-wider">Malzeme Listesi</label>
 <div className="flex items-center gap-6">
 <button
 type="button"
 onClick={() => setDictionaryInspectorOpen(true)}
 className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-cyan-200/70 dark:border-cyan-700/70 bg-cyan-50/80 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-200 text-[11px] font-black uppercase tracking-wide hover:shadow-sm transition-all"
 title="Tarifteki malzemelerin dictionary ID ve eslesme durumunu kontrol et"
 >
 <span className="material-symbols-outlined text-[16px]">manage_search</span>
 Dictionary Kontrol
 <span className="px-1.5 py-0.5 rounded-lg bg-white/80 dark:bg-slate-800/80 text-[10px] text-cyan-700 dark:text-cyan-200 border border-cyan-200/70 dark:border-cyan-700/70">
 {filteredDictionaryInspectorRows.length}
 </span>
 </button>
 {/* Portion Preview Selector */}
 <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 px-3 py-1.5 rounded-2xl border border-slate-100 dark:border-slate-700">
 <span className="material-symbols-outlined text-slate-400 text-[18px]">group</span>
 <div className="flex items-center gap-2">
 <button
 type="button"
 onClick={() => setPreviewServings(Math.max(1, previewServings - 1))}
 className="size-6 flex items-center justify-center bg-white dark:bg-slate-700 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600 hover:text-primary transition-all"
 >-</button>
 <span className="text-xs font-black min-w-[20px] text-center">{previewServings}</span>
 <button
 type="button"
 onClick={() => setPreviewServings(previewServings + 1)}
 className="size-6 flex items-center justify-center bg-white dark:bg-slate-700 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600 hover:text-primary transition-all"
 >+</button>
 </div>
 <span className="text-[10px] font-bold text-slate-400 uppercase ml-1">Kişilik Önizleme</span>
 </div>

 <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
 <button
 type="button"
 onClick={() => setIngredientsViewMode('LIST')}
 className={`px-3 py-1 text-[10px] font-black rounded-lg transition-all ${ingredientsViewMode === 'LIST' ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500'}`}
 >LİSTE</button>
 <button
 type="button"
 onClick={() => setIngredientsViewMode('JSON')}
 className={`px-3 py-1 text-[10px] font-black rounded-lg transition-all ${ingredientsViewMode === 'JSON' ? 'bg-white dark:bg-slate-700 text-rose-500 shadow-sm' : 'text-slate-500'}`}
 >JSON</button>
 </div>
 {editingDish.isTemporary && (
 <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-black rounded uppercase miniature-shadow">AI Taslağı - Kayıtta Ayrıştırılacak</span>
 )}
 </div>
 </div>
 {dictionaryInspectorOpen && (
 <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-in fade-in duration-200">
 <div
 className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
 onClick={() => setDictionaryInspectorOpen(false)}
 />
 <div className="relative w-full max-w-6xl h-[88vh] overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl flex flex-col">
 <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-800/60">
 <div>
 <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
 <span className="material-symbols-outlined text-cyan-500">manage_search</span>
 Dictionary Kontrol Merkezi
 </h3>
 <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
 JSON satirlarini, mevcut dictionary kimligini ve sozluk arama sonucunu ayni yerde karsilastir.
 </p>
 </div>
 <button
 type="button"
 onClick={() => setDictionaryInspectorOpen(false)}
 className="size-10 rounded-xl bg-slate-200/70 dark:bg-slate-700/70 text-slate-600 dark:text-slate-200 hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center"
 >
 <span className="material-symbols-outlined">close</span>
 </button>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-0 flex-1 min-h-0">
 <div className="p-6 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800 overflow-y-auto min-h-0">
 <div className="flex items-center gap-3 mb-4">
 <div className="relative flex-1">
 <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
 <input
 type="text"
 value={dictionaryInspectorQuery}
 onChange={(e) => setDictionaryInspectorQuery(e.target.value)}
 placeholder="Tarif satiri, malzeme adi, dictionary ID veya aile ara..."
 className="w-full pl-10 pr-4 py-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
 />
 </div>
 <button
 type="button"
 onClick={() => setDictionaryInspectorQuery('')}
 className="px-3 py-3 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 text-xs font-black uppercase"
 >
 Temizle
 </button>
 <button
 type="button"
 onClick={() => setDictionaryInspectorOnlyIssues((prev) => !prev)}
 className={`px-3 py-3 rounded-2xl text-xs font-black uppercase border transition-all ${
 dictionaryInspectorOnlyIssues
 ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-200'
 : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300'
 }`}
 >
 Sadece Sorunlu
 </button>
 <button
 type="button"
 onClick={() => setDictionaryInspectorOnlyMissingId((prev) => !prev)}
 className={`px-3 py-3 rounded-2xl text-xs font-black uppercase border transition-all ${
 dictionaryInspectorOnlyMissingId
 ? 'bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-200'
 : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300'
 }`}
 >
 Sadece ID Bos
 </button>
 </div>

 <div className="flex items-center gap-3 mb-4 text-[11px] font-bold text-slate-500 dark:text-slate-400">
 <span className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800">
 Satir: {filteredDictionaryInspectorRows.length}/{dictionaryInspectorRows.length}
 </span>
 <span className="px-2 py-1 rounded-lg bg-emerald-100/80 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200">
 Eslesmis: {dictionaryInspectorRows.filter((row) => row.state === 'matched').length}
 </span>
 <span className="px-2 py-1 rounded-lg bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200">
 Dogrulama: {dictionaryInspectorRows.filter((row) => row.state === 'needs_verification').length}
 </span>
 <span className="px-2 py-1 rounded-lg bg-rose-100/80 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200">
 Eksik: {dictionaryInspectorRows.filter((row) => row.state === 'failed').length}
 </span>
 {dictionaryInspectorOnlyIssues && (
 <span className="px-2 py-1 rounded-lg bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200">
 Filtre: Sorunlu Satırlar
 </span>
 )}
 {dictionaryInspectorOnlyMissingId && (
 <span className="px-2 py-1 rounded-lg bg-rose-100/80 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200">
 Filtre: Dictionary ID Bos
 </span>
 )}
 </div>

 <div className="space-y-3">
 {filteredDictionaryInspectorRows.map((row) => (
 <button
 key={`dict-row-${row.index}`}
 type="button"
 onClick={() => setDictionaryInspectorSelectedRow(row.index)}
 className={`w-full text-left rounded-2xl border p-4 transition-all ${
 dictionaryInspectorSelectedRow === row.index
 ? 'border-cyan-400 bg-cyan-50/80 dark:bg-cyan-950/20 dark:border-cyan-700 shadow-sm'
 : 'border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-950/60'
 }`}
 >
 <div className="flex items-start justify-between gap-4">
 <div className="min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <span className="text-sm font-black text-slate-900 dark:text-white">{row.rowName}</span>
 <span className="px-2 py-0.5 rounded-lg bg-slate-200/80 dark:bg-slate-800 text-[10px] font-black uppercase text-slate-600 dark:text-slate-200">
 Satır {row.index + 1}
 </span>
 <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase ${
 row.state === 'matched'
 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'
 : row.state === 'needs_verification'
 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
 : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200'
 }`}>
 {row.state === 'matched' ? 'Eslesmis' : row.state === 'needs_verification' ? 'Dogrulama Bekliyor' : 'Eksik'}
 </span>
 </div>
 <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 break-all">{row.rawLine || 'Ham satir bilgisi yok.'}</p>
 </div>
 <button
 type="button"
 onClick={() => handleCopyDictionaryValue(row.dictionaryId)}
 className="px-2.5 py-1.5 rounded-xl bg-slate-200/80 dark:bg-slate-800 text-[10px] font-black uppercase text-slate-600 dark:text-slate-200"
 >
 ID Kopyala
 </button>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
 <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3">
 <div className="text-[10px] font-black uppercase text-slate-400 mb-1">Dictionary ID</div>
 <div className="text-xs font-mono break-all text-slate-700 dark:text-slate-200">{row.dictionaryId || '-'}</div>
 </div>
 <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3">
 <div className="text-[10px] font-black uppercase text-slate-400 mb-1">Canonical / Aile</div>
 <div className="text-xs text-slate-700 dark:text-slate-200">{row.canonicalName || '-'}</div>
 <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{row.familyName || 'Aile yok'}</div>
 </div>
 </div>
 </button>
 ))}
 {filteredDictionaryInspectorRows.length === 0 && (
 <div className="rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 p-8 text-center text-sm text-slate-500 dark:text-slate-400">
 Aramana uyan satir bulunamadi.
 </div>
 )}
 </div>
 </div>

 <div className="p-6 overflow-y-auto bg-slate-50/60 dark:bg-slate-950/40 min-h-0">
 <div className="flex items-center justify-between gap-3 mb-4">
 <div>
 <h4 className="text-sm font-black text-slate-900 dark:text-white">Sozluk Arama</h4>
 <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
 Urunu yaz, mevcut recipe satiri ile ayni anda dogru dictionary kaydini karsilastir.
 </p>
 <div className="mt-2 text-[11px] font-bold text-slate-600 dark:text-slate-300">
 {selectedDictionaryInspectorRow
 ? `Secili satir: ${selectedDictionaryInspectorRow.rowName}`
 : 'Secili satir yok. Soldan bir satir sec.'}
 </div>
 </div>
 {dictionaryInspectorLoading && (
 <span className="text-[11px] font-bold text-cyan-600 dark:text-cyan-300">Araniyor...</span>
 )}
 </div>

 {selectedDictionaryInspectorRow && (
 <div className="mb-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
 <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 flex items-center justify-between gap-3">
 <div>
 <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Ham JSON Parçası</div>
 <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mt-1">
 {selectedDictionaryInspectorRow.rowName}
 </div>
 </div>
 <button
 type="button"
 onClick={() => handleCopyDictionaryValue(JSON.stringify(selectedDictionaryInspectorRow.rawItem || {}, null, 2))}
 className="px-2.5 py-1.5 rounded-xl bg-slate-200/80 dark:bg-slate-800 text-[10px] font-black uppercase text-slate-600 dark:text-slate-200"
 >
 JSON Kopyala
 </button>
 </div>
 <pre className="px-4 py-4 text-[11px] leading-relaxed overflow-x-auto max-h-52 bg-slate-950 text-slate-200 font-mono whitespace-pre-wrap break-words">
 {JSON.stringify(selectedDictionaryInspectorRow.rawItem || {}, null, 2)}
 </pre>
 </div>
 )}

 <div className="space-y-3">
 {dictionaryInspectorError && (
 <div className="rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
 {dictionaryInspectorError}
 </div>
 )}
 {!dictionaryInspectorError && !dictionaryInspectorLoading && dictionaryInspectorQuery.trim().length > 0 && dictionaryInspectorResults.length === 0 && (
 <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
 Arama sonucu bulunamadi.
 </div>
 )}
 {dictionaryInspectorResults.map((item, idx) => (
 <div key={`dict-search-${item.id || item.dictionaryId || idx}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
 <div className="flex items-start justify-between gap-3">
 <div className="min-w-0">
 <div className="text-sm font-black text-slate-900 dark:text-white break-words">
 {item.name || item.canonicalName || item.label || 'Isimsiz urun'}
 </div>
 <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
 {(item.familyName || item.family || 'Aile yok')} {item.canonicalUnit ? `· ${item.canonicalUnit}` : ''}
 </div>
 </div>
 <button
 type="button"
 onClick={() => handleCopyDictionaryValue(item.id || item.dictionaryId)}
 className="px-2.5 py-1.5 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 text-[10px] font-black uppercase text-cyan-700 dark:text-cyan-200"
 >
 ID Kopyala
 </button>
 </div>
 <div className="mt-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-3">
 <div className="text-[10px] font-black uppercase text-slate-400 mb-1">Dictionary ID</div>
 <div className="text-xs font-mono break-all text-slate-700 dark:text-slate-200">{item.id || item.dictionaryId || '-'}</div>
 </div>
 <button
 type="button"
 onClick={() => handleApplyDictionaryInspectorResult(item)}
 disabled={dictionaryInspectorSelectedRow === null || dictionaryInspectorSelectedRow === undefined}
 className={`mt-3 w-full px-3 py-2.5 rounded-xl text-[11px] font-black uppercase transition-all ${
 dictionaryInspectorSelectedRow === null || dictionaryInspectorSelectedRow === undefined
 ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
 : 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-sm'
 }`}
 >
 Secili Satira Uygula
 </button>
 </div>
 ))}
 <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-4 text-[11px] text-slate-500 dark:text-slate-400">
 Not: Bu ilk surumda kontrol ve kopyalama araci eklenmis oldu. Istersen bir sonraki adimda bu modalin icinden secilen dictionary kaydini dogrudan ilgili satira da yazalim.
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 )}
 {ingredientsViewMode === 'LIST' ? (
 <div className="animate-in slide-in-from-right-4 duration-300">
 {/* NEW: Unified Ingredient Resolver Table */}
 <IngredientResolverTable
 apiBaseUrl={API_BASE_URL}
 ingredients={(() => {
 const parseIfJson = (input) => {
 if (typeof input !== 'string') return input;
 const trimmed = input.trim();
 if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
 try {
 const parsed = JSON.parse(trimmed);
 return parseIfJson(parsed); // Recursively parse if the result is still a JSON string
 } catch (e) {
 return input;
 }
 }
 return input;
 };

 // Admin editorde kaynak once malzemeler JSON olsun.
 // Relation satirlari sadece fallback olarak kullanilsin.
 let rawIngs = (editingDish.malzemeler?.length > 0 ? editingDish.malzemeler : null)
 || (editingDish.malzemelerRaw?.length > 0 ? editingDish.malzemelerRaw : null)
 || (editingDish.ingredients?.length > 0 ? editingDish.ingredients : []);
 rawIngs = parseIfJson(rawIngs);
 const rawMalzemeler = parseIfJson(
 editingDish.malzemeler?.length > 0
 ? editingDish.malzemeler
 : (editingDish.malzemelerRaw || [])
 );

 if (!Array.isArray(rawIngs)) return [];

 const normalizeKey = (v) => String(v || '')
 .toLocaleLowerCase('tr-TR')
 .normalize('NFD')
 .replace(/[\u0300-\u036f]/g, '')
 .replace(/ı/g, 'i')
 .replace(/ç/g, 'c')
 .replace(/ğ/g, 'g')
 .replace(/ö/g, 'o')
 .replace(/ş/g, 's')
 .replace(/ü/g, 'u')
 .replace(/[^a-z0-9]+/g, ' ')
 .trim();
 const slimKey = (v) => normalizeKey(v)
 .replace(/\b(islatilmis|haslanmis|hazir|dogranmis|rendelenmis|kurutulmus)\b/g, ' ')
 .replace(/\s+/g, ' ')
 .trim();

 const malzemeByRowKey = new Map();
 if (Array.isArray(rawMalzemeler)) {
 rawMalzemeler.forEach((m) => {
 if (!m || typeof m !== 'object') return;
 const rk = String(m.rowKey || '').trim();
 if (rk) malzemeByRowKey.set(rk, m);
 });
 }

 return rawIngs.flatMap((m, idx) => {
 const parsedItem = parseIfJson(m);

 const extractData = (it) => {
 // V22 / Prisma Relation structure
 const name = it.dictionary?.canonicalName || it.malzeme || it.name || it.ingredient?.name || 'Bilinmiyor';
 const qtyRaw = it.quantity || it.miktar || it.amountNormalized || it.amount || it.amountRaw || '';
 const qty = (() => {
 const val = String(qtyRaw ?? '').trim();
 if (!val) return '';
 const parsed = parseFloat(val.replace(',', '.').replace(/[^0-9./]/g, ''));
 if (Number.isFinite(parsed) && parsed > 0) return String(parsed);
 return val;
 })();
 const unit = it.unitRaw || it.birim || it.unit || '';
 const dictId = it.dictionaryId || it.dictionary?.id || '';
      const matchedMalzeme = malzemeByRowKey.get(String(it.rowKey || '').trim()) || null;

 const trAmount = String(
 it.turkmutfagiMiktar ??
 it.turkmutfagi_miktar ??
 matchedMalzeme?.turkmutfagiMiktar ??
 matchedMalzeme?.turkmutfagi_miktar ??
 ''
 ).trim();
 const trUnit = String(
 it.turkmutfagiBirim ??
 it.turkmutfagi_birim ??
 matchedMalzeme?.turkmutfagiBirim ??
 matchedMalzeme?.turkmutfagi_birim ??
 ''
 ).trim();
 const trText = String(
 it.turkmutfagi ??
 it.originalText ??
 matchedMalzeme?.turkmutfagi ??
 matchedMalzeme?.originalText ??
 (trAmount && trUnit ? `${trAmount} ${trUnit}` : '')
 ).trim();

 return {
 name: String(name),
 quantity: String(qty),
 unit: String(unit),
 turkmutfagi: trText,
 turkmutfagiMiktar: trAmount,
 turkmutfagiBirim: trUnit,
 rawInput: it.rawInput || trText || it.originalText || (typeof it === 'string' ? it : name),
 alternativeIds: uniqTextList([
 ...(Array.isArray(it.alternativeIds) ? it.alternativeIds : []),
 ...(Array.isArray(matchedMalzeme?.alternativeIds) ? matchedMalzeme.alternativeIds : [])
 ]),
 alternativeNames: uniqTextList([
 ...(Array.isArray(it.alternativeNames) ? it.alternativeNames : []),
 ...(Array.isArray(matchedMalzeme?.alternativeNames) ? matchedMalzeme.alternativeNames : [])
 ])
 };
 };

 if (Array.isArray(parsedItem)) {
 return parsedItem.map(subItem => ({
 ...subItem,
 ...extractData(subItem)
 }));
 }

 if (typeof parsedItem === 'string') {
 return [{ name: parsedItem, rawInput: parsedItem }];
 }

 return [{
 ...parsedItem,
 ...extractData(parsedItem)
 }];
 });
 })()}
 resolutions={ingredientResolutions}
 onResolve={(idx, resolvedData) => {
 applyResolvedIngredient(idx, resolvedData);
}}
/>
 </div>
 ) : (
 <div className="animate-in fade-in duration-300">
 <textarea
 className="w-full h-[450px] px-4 py-4 bg-slate-900 text-rose-400 border border-slate-800 rounded-2xl text-xs font-mono focus:ring-2 focus:ring-rose-500/30 outline-none leading-relaxed shadow-inner"
 value={JSON.stringify((editingDish.malzemeler?.length > 0 ? editingDish.malzemeler : (editingDish.malzemelerRaw?.length > 0 ? editingDish.malzemelerRaw : null)) || (editingDish.ingredients?.length > 0 ? editingDish.ingredients.map(ing => {
 const name = ing.dictionary?.canonicalName || ing.malzeme || ing.name || ing.ingredient?.name || '';
 const qty = ing.amountRaw || ing.miktar || ing.quantity || '';
 const unit = ing.unitRaw || ing.birim || ing.unit || '';
 return `${qty} ${unit} ${name}`.trim();
 }) : []), null, 2)}
 onChange={(e) => {
 try {
 const parsed = JSON.parse(e.target.value);
 if (Array.isArray(parsed)) {
 const normalizedRows = ensureIngredientRowKeys(parsed);
 const nextDish = { ...editingDish, malzemeler: normalizedRows, ingredients: [] };
 setEditingDish(nextDish);
 setIngredientResolutions(
 normalizedRows.map((row) => {
 const rowKey = String(row?.rowKey || '').trim();
 return {
 rowKey: rowKey || undefined,
 rawInput: buildIngredientResolverLine(row),
 resolvedName: row?.malzeme || row?.name || '',
 found: Boolean(row?.dictionaryId),
 status: row?.dictionaryId ? 'RESOLVED' : 'UNKNOWN',
 dictionaryId: row?.dictionaryId || null,
 canonicalName: row?.canonicalName || row?.dictionaryName || row?.malzeme || row?.name || null,
 familyName: row?.familyName || null,
 alternativeIds: Array.isArray(row?.alternativeIds) ? row.alternativeIds : [],
 alternativeNames: Array.isArray(row?.alternativeNames) ? row.alternativeNames : []
 };
 })
 );
 // JSON yapistirma sonrasi otomatik batch resolve: kullaniciyi tek tek secimden kurtarir.
 setTimeout(() => {
 handleResolveIngredients(nextDish);
 }, 0);
 }
 } catch (err) { }
 }}
 />
 <p className="text-[10px] text-slate-500 mt-2 italic">* Sadece malzeme listesini (String Dizisi) JSON olarak düzenliyorsunuz. Örnek: ["2 su bardağı nohut", "1 adet soğan"]</p>
 <p className="text-[10px] text-amber-500 mt-1 font-bold">Kaydetmeden önce LISTE moduna dönüp tüm malzeme eşleşmelerini doğrulayın.</p>
 </div>
 )}

 {/* Case 3: Empty */}
 {(!editingDish.ingredients || editingDish.ingredients.length === 0) && (!editingDish.malzemeler || editingDish.malzemeler.length === 0) && (
 <div className="py-12 flex flex-col items-center justify-center text-slate-400 italic bg-slate-50 dark:bg-slate-900/20 rounded-3xl border-2 border-dashed border-slate-100 dark:border-slate-800">
 <span className="material-symbols-outlined text-4xl mb-2 opacity-20">egg_alt</span>
 Henüz malzeme eklenmemiş.
 </div>
 )}

 {/* --- DOMINANT INGREDIENTS AREA --- */}
 <div className="pt-8 border-t border-slate-100 dark:border-slate-800/50 mt-8">
 <div className="flex items-center justify-between mb-4">
 <div>
 <label className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
 <span className="material-symbols-outlined text-sm text-primary">analytics</span>
 Dominant (Ana) Malzemeler
 </label>
 <p className="text-[10px] text-slate-500 font-medium mt-1">Stok kontrolü yapılırken ağırlığı olan kalemler (örn: Pirinç, Barbunya)</p>
 </div>
 </div>

 <div className="space-y-4">
 {/* JSON Textarea Area */}
 <div className="relative">
 <textarea
 value={typeof editingDish.dominants === 'string' ? editingDish.dominants : JSON.stringify(editingDish.dominants || [], null, 2)}
 onChange={(e) => {
 const val = e.target.value;
 try {
 const parsed = JSON.parse(val);
 if (Array.isArray(parsed)) {
 setEditingDish({ ...editingDish, dominants: parsed });
 } else {
 // Eğer JSON ama array değilse sadece string olarak tut (editörde kalsın)
 setEditingDish({ ...editingDish, dominants: val });
 }
 } catch (err) {
 // Geçersiz JSON iken de kullanıcı yazabilsin
 setEditingDish({ ...editingDish, dominants: val });
 }
 }}
 placeholder='["Kıyma", "Soğan", "Salça"]'
 rows={6}
 className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-3xl text-sm font-mono focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
 />
 <div className="absolute right-4 bottom-4 flex items-center gap-2">
 {(typeof editingDish.dominants === 'string' && editingDish.dominants.trim()) ? (
 <span className="flex items-center gap-1.5 px-3 py-1 bg-red-50 dark:bg-red-900/30 text-red-500 text-[10px] font-black uppercase tracking-tighter rounded-xl border border-red-100 dark:border-red-800">
 <span className="material-symbols-outlined text-xs">error</span>
 GEÇERSİZ JSON
 </span>
 ) : (
 <span className="flex items-center gap-1.5 px-3 py-1 bg-green-50 dark:bg-green-900/30 text-green-600 text-[10px] font-black uppercase tracking-tighter rounded-xl border border-green-100 dark:border-green-800">
 <span className="material-symbols-outlined text-xs">check_circle</span>
 JSON OK
 </span>
 )}
 </div>
 </div>
 <p className="text-[10px] text-slate-400 italic px-2">
 * Malzemeleri tırnak içinde ve virgülle ayırarak (Array formatında) yapıştırın.
 </p>
 </div>
 </div>
 </div>
 )}

 {modalTab === 'STEPS' && (
 <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-3 ml-1 tracking-wider">Tarif Adımları (JSON Array)</label>
 <p className="text-[10px] text-slate-500 mb-3 -mt-1 ml-1">Her adım; adim, islem, detay ve asistan_mesaji içerebilir.</p>
 <textarea
 value={typeof editingDish.instructions === 'string' ? editingDish.instructions : JSON.stringify(editingDish.instructions, null, 2)}
 onChange={e => {
 try {
 const val = e.target.value;
 // We don't parse it immediately to allow typing, but we should handle it on save.
 setEditingDish({ ...editingDish, instructions: val });
 } catch (err) { }
 }}
 className="w-full h-[400px] px-4 py-4 bg-slate-900 text-emerald-400 border border-slate-800 rounded-2xl text-xs font-mono focus:ring-2 focus:ring-primary/30 outline-none leading-relaxed"
 placeholder='[
 {
 "adim": 1, 
 "islem": "Yıkama", 
 "type": "preparation",
 "detay_acemi": "Tavukları akan soğuk suyun altında en az 2 dakika boyunca ovalayarak yıkayın; üzerindeki kalıntıları temizlemiş olursunuz.",
 "detay_usta": "Tavukları soğuk suyla yıkayın ve süzün.",
 "detay_sef": "Tavuk karkaslarını basınçlı suyla arındırın.",
 "durationMinutes": 5,
 "mode": "common",
 "asistan_mesaji": "Kapağını açalım şefim!"
 }
]'
 />
 </div>
 </div>
 )}

 {modalTab === 'COOKING' && (
 <div className="space-y-8 animate-in slide-in-from-right-4 duration-300">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
 <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-[24px] border border-slate-100 dark:border-slate-800">
 <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
 <span className="material-symbols-outlined text-[16px]">schedule</span> Hazırlık Parametreleri
 </h4>
 <div className="space-y-6">
 <label className="flex items-center gap-4 cursor-pointer">
 <input
 type="checkbox"
 checked={editingDish.requiresPrep}
 onChange={e => setEditingDish({ ...editingDish, requiresPrep: e.target.checked })}
 className="w-5 h-5 accent-primary"
 />
 <span className="text-sm font-bold text-slate-800 dark:text-white">Ön Hazırlık Gerekir (Lokal)</span>
 </label>
 <div>
 <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 ml-1">Hazırlık Süresi (Saat)</label>
 <input
 type="number"
 step="0.5"
 value={editingDish.prepLeadTimeHours}
 onChange={e => setEditingDish({ ...editingDish, prepLeadTimeHours: parseFloat(e.target.value) })}
 className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold"
 />
 </div>
 </div>
 </div>

 <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-[24px] border border-slate-100 dark:border-slate-800">
 <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
 <span className="material-symbols-outlined text-[16px]">child_care</span> Bebek Notu (Ahmet)
 </h4>
 <textarea
 value={editingDish.babyAdjustment || ''}
 onChange={e => setEditingDish({ ...editingDish, babyAdjustment: e.target.value })}
 className="w-full h-32 px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 outline-none"
 placeholder="Alerjen veya baharat ayırma notu..."
 />
 </div>

 <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-[24px] border border-slate-100 dark:border-slate-800">
 <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                        <span className="material-symbols-outlined text-[16px]">restaurant</span> Şefin Notu
 </h4>
 <textarea
 value={editingDish.chef_note || ''}
 onChange={e => setEditingDish({ ...editingDish, chef_note: e.target.value })}
 className="w-full h-32 px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 outline-none"
 placeholder="Özel ipucu veya kişisel not..."
 />
 </div>
 </div>

 <div>
 <label className="block text-xs font-bold text-slate-400 uppercase mb-3 ml-1 tracking-wider">Pişirme Modları (JSON Mapping)</label>
 <textarea
 value={typeof editingDish.cookingModes === 'string' ? editingDish.cookingModes : JSON.stringify(editingDish.cookingModes, null, 2)}
 onChange={e => setEditingDish({ ...editingDish, cookingModes: e.target.value })}
 className="w-full h-40 px-4 py-4 bg-slate-900 text-blue-400 border border-slate-800 rounded-2xl text-xs font-mono focus:ring-2 focus:ring-primary/30 outline-none"
 placeholder='{
 "oven": {
 "mode": "oven",
 "temp": "180C",
 "time": "30",
 "heat": "turbo"
 },
 "pot": {
 "mode": "pot",
 "time": "20",
 "heat": "medium"
 }
}'
 />
 </div>
 </div>
 )}

 {modalTab === 'ALLERGENS' && (
 <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
 <div className="p-8 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-[32px] border-2 border-emerald-100 dark:border-emerald-800/50 shadow-sm">
 <div className="flex items-start gap-4 mb-8">
 <div className="size-12 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
 <span className="material-symbols-outlined text-emerald-600">shield</span>
 </div>
 <div>
 <h3 className="text-lg font-black text-slate-900 dark:text-white leading-tight">Alerjen Mutfağı</h3>
 <p className="text-xs font-bold text-emerald-600/80 uppercase tracking-wider">Premium Özellik - Alerji güvenli tarifler için doğrulama</p>
 </div>
 </div>

 <label className="block text-xs font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest mb-4 ml-1">
 HANGİ ALERJİLER İÇİN GÜVENLİ?
 </label>

 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 {ALLERGEN_OPTIONS.map(allergen => (
 <label
 key={allergen}
 className={`flex items-center justify-center p-4 rounded-2xl border-2 transition-all cursor-pointer font-bold text-xs ${(editingDish.safeForAllergies || []).includes(allergen)
 ? 'bg-emerald-500 border-emerald-600 text-white shadow-lg shadow-emerald-500/20'
 : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-400 hover:border-emerald-200 opacity-60'
 }`}
 >
 <input
 type="checkbox"
 className="hidden"
 checked={(editingDish.safeForAllergies || []).includes(allergen)}
 onChange={e => {
 const current = editingDish.safeForAllergies || [];
 const updated = e.target.checked
 ? [...current, allergen]
 : current.filter(a => a !== allergen);
 setEditingDish({ ...editingDish, safeForAllergies: updated });
 }}
 />
 {allergen.replace(' Alerjisi', '')}
 </label>
 ))}
 </div>
 </div>

 <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-100 dark:border-slate-800 italic text-[11px] text-slate-500 leading-relaxed">
 ℹ️ <strong>Not:</strong> Burada işaretlediğiniz alerjenler, kullanıcının profilindeki alerji kısıtlamalarıyla eşleştiğinde bu tarif "Güvenli" olarak listelenecektir. Çapraz bulaşma riski varsa ilgili alerjeni <strong>işaretlemeyiniz</strong>.
 </div>
 </div>
 )}

 {modalTab === 'JSON' && (
 <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
 <div className="flex items-center justify-between px-2">
 <h4 className="text-xs font-black text-rose-500 uppercase tracking-widest">Master Raw Edit</h4>
 <span className="text-[10px] text-slate-500 italic">Yalnızca şefler için ileri seviye müdahale alanı.</span>
 </div>
 {!jsonAdvancedUnlocked ? (
 <div className="p-5 rounded-2xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
 <p className="text-sm font-bold text-amber-700 dark:text-amber-300">Bu alan kilitli. Ham JSON düzenlemek için üst sekmeden bilinçli onay vermen gerekir.</p>
 <p className="text-xs text-amber-700/90 dark:text-amber-200 mt-1">Öneri: mümkünse LISTE modunda çalış, dictionary eşleşmesini oradan yönet.</p>
 </div>
 ) : (
 <textarea
 value={JSON.stringify(editingDish, (key, val) => key === 'ingredients' ? undefined : val, 2)}
 onChange={e => {
 try {
 const parsed = JSON.parse(e.target.value);
 setEditingDish({ ...editingDish, ...parsed });
 } catch (err) { }
 }}
 className="w-full h-[500px] px-4 py-4 bg-slate-950 text-slate-300 border border-slate-800 rounded-[24px] text-xs font-mono focus:ring-2 focus:ring-rose-500/30 outline-none"
 />
 )}
 </div>
 )}

 </form>
 </div>

 {/* Modal Footer */}
 <div className="p-8 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/30 dark:bg-slate-900/10 shrink-0">
 <button
 type="button"
 onClick={() => { setEditingDish(null); setModalTab('BASIC'); }}
 className="px-8 py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-2xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-800"
 >
 Vazgeç
 </button>
 <div className="flex items-center gap-4">
 <div className="flex items-center gap-2 mr-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
 <button
 type="button"
 onClick={() => setSaveTarget('DB')}
 className={`px-4 py-2 rounded-lg text-xs font-black transition-all flex items-center gap-2 ${saveTarget === 'DB'
 ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
 : 'text-slate-400 hover:text-slate-600'}`}
 >
 <span className="material-symbols-outlined text-[16px]">database</span>
 DB
 </button>
 <button
 type="button"
 onClick={() => setSaveTarget('JSON')}
 className={`px-4 py-2 rounded-lg text-xs font-black transition-all flex items-center gap-2 ${saveTarget === 'JSON'
 ? 'bg-white dark:bg-slate-700 text-rose-500 shadow-sm'
 : 'text-slate-400 hover:text-slate-600'}`}
 >
 <span className="material-symbols-outlined text-[16px]">data_object</span>
 JSON
 </button>
 </div>

 <div className="flex flex-col items-end gap-2">
 {ingredientResolveNotice && (
 <div className="px-3 py-1.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-bold dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
 Satır {ingredientResolveNotice.index + 1}: {ingredientResolveNotice.message}
 </div>
 )}
 <div className="flex flex-col items-end gap-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-800">
 <div className="flex items-center gap-2">
 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Çözümleme</span>
 <span className={`size-2 rounded-full ${hasBlockingResolution || isJsonIngredientMode ? 'bg-rose-500 animate-pulse' : 'bg-emerald-500'}`}></span>
 </div>
 <p className="text-[9px] text-slate-500 font-bold uppercase">
 {ingredientResolutionSummary.matched} Eşleşti • {ingredientResolutionSummary.needsVerification} Doğrulama • {ingredientResolutionSummary.failed} Hata
 </p>
 {saveBlockReason ? (
 <p className="text-[10px] text-rose-500 font-bold max-w-[280px] text-right">{saveBlockReason}</p>
 ) : (
 <p className="text-[10px] text-emerald-500 font-bold max-w-[280px] text-right">Kaydetme için doğrulama tamamlandı.</p>
 )}
 {lastProjectionVerification && (
 <p className={`text-[10px] font-bold max-w-[280px] text-right ${lastProjectionVerification?.ok ? 'text-emerald-500' : 'text-rose-500'}`}>
 Projection: {lastProjectionVerification?.ok ? 'Senkron doğrulandı' : 'Senkron hatası'}
 </p>
 )}
 </div>
 </div>

 {(ingredientResolutionSummary.failedRows.length > 0 || ingredientResolutionSummary.verificationRows.length > 0) && (
 <div className="hidden xl:flex flex-col gap-1 px-3 py-2 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 max-w-[340px]">
 <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300">Doğrulama Özeti</span>
 {ingredientResolutionSummary.failedRows.slice(0, 2).map((row) => (
 <span key={`failed-${row.index}`} className="text-[10px] font-semibold text-rose-600 dark:text-rose-300">Eşleşme başarısız: {row.label}</span>
 ))}
 {ingredientResolutionSummary.verificationRows.slice(0, 2).map((row) => (
 <span key={`verify-${row.index}`} className="text-[10px] font-semibold text-amber-700 dark:text-amber-200">Doğrulama gerekli: {row.label}</span>
 ))}
 {(ingredientResolutionSummary.failedRows.length + ingredientResolutionSummary.verificationRows.length) > 4 && (
 <span className="text-[10px] font-semibold text-slate-500">+{(ingredientResolutionSummary.failedRows.length + ingredientResolutionSummary.verificationRows.length) - 4} satır daha</span>
 )}
 </div>
 )}

 <button
 type="submit"
 form="editDishForm"
 disabled={isSaveBlocked}
 className={`px-8 py-3.5 rounded-2xl font-black shadow-xl transition-all flex items-center gap-2 ${(isSaveBlocked)
 ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed grayscale'
 : 'bg-gradient-to-r from-slate-900 to-slate-800 dark:from-white dark:to-slate-200 text-white dark:text-slate-900 hover:scale-[1.02] active:scale-[0.98]'
 }`}
 >
 <span className="material-symbols-outlined">{isSaveBlocked ? 'lock' : 'save'}</span>
 KAYDET
 </button>
 </div>
 </div>
 </div>
 </div >
 )
 }
 {isDishLoading && (
 <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm">
 <div className="px-5 py-4 rounded-2xl bg-white dark:bg-[#0B1219] border border-slate-200 dark:border-slate-800 shadow-2xl flex items-center gap-3">
 <span className="material-symbols-outlined animate-spin text-primary">progress_activity</span>
 <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Tarif detayı yükleniyor...</span>
 </div>
 </div>
 )}
 </div >
 );
}
